import { useEffect, useState, Fragment } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft, Play, Pause, Save, Upload, Database, UserPlus, Trash2, RefreshCcw, RotateCcw, Mic, X, CalendarClock, Eraser,
  ChevronDown, ChevronRight, MessageSquareText, ArrowUpDown, ThumbsUp, ThumbsDown, GraduationCap,
  BarChart3, Download, PhoneCall, Phone, MessageCircle, TrendingUp, Settings, Users, Star,
  Maximize2, Minimize2, Search, PhoneOutgoing
} from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import ErrorBanner from '../components/ErrorBanner.jsx';
import VoicePicker from '../components/VoicePicker.jsx';
import KnowledgeEditor from '../components/KnowledgeEditor.jsx';
import PitchPicker from '../components/PitchPicker.jsx';
import ConfirmDialog, { useConfirm } from '../components/ConfirmDialog.jsx';
import UpcomingCalls from '../components/UpcomingCalls.jsx';
import ScheduledCalls from '../components/ScheduledCalls.jsx';
import FaqEditor from '../components/FaqEditor.jsx';
import { campaignsAPI, phoneNumbersAPI, listsAPI, knowledgeAPI, faqsAPI, callsAPI } from '../services/api.js';
import { formatDateTime, formatDateTimeWithZone, formatPhone, toE164 } from '../lib/format.js';
// ONE funnel, shared with the Leads page — see lib/salesStages.js for why.
import { SALES_STAGE_OPTIONS, HOT_LEAD_STAGES, STAGES_PENDING_IN_CORE } from '../lib/salesStages.js';
import { isoToZonedInput, zonedInputToIso, zoneAbbreviation } from '../lib/tz.js';
import { COMMON_TIMEZONES } from '../lib/tz.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import CampaignBehaviorEditor from '../components/CampaignBehaviorEditor.jsx';
import AutoPauseBanner from '../components/AutoPauseBanner.jsx';
import ContactDossierEditor from '../components/ContactDossierEditor.jsx';
import ContactCallbacks from '../components/ContactCallbacks.jsx';
import ContactNotes from '../components/ContactNotes.jsx';
import useRevalidate from '../hooks/useRevalidate.js';

const OUTCOME_BADGE = {
  // answered = the owner had an actual CONVERSATION — highest recall priority.
  answered: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30',
  interested: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30',
  not_interested: 'bg-stone-700 text-stone-300 border border-stone-600',
  callback_requested: 'bg-sky-500/15 text-sky-300 border border-sky-500/30',
  // referral_captured — no decision-maker reached, but the call OBTAINED CONTACT
  // INFORMATION (an email, a named person, a direct number). Deliberately gold
  // rather than a neutral grey: on the 07-29 carrier run this was ONE call in ~50,
  // and it previously fell into 'other' where nobody looked.
  referral_captured: 'bg-alfred-gold/20 text-alfred-gold border border-alfred-gold/40',
  voicemail: 'bg-sky-500/15 text-sky-300 border border-sky-500/30',
  no_answer: 'bg-amber-500/10 text-amber-300/80 border border-amber-500/25',
  ivr_unreachable: 'bg-amber-500/10 text-amber-300/80 border border-amber-500/25',
  pitched_into_silence: 'bg-amber-500/10 text-amber-300/80 border border-amber-500/25',
  gatekeeper_no_contact: 'bg-stone-700 text-stone-300 border border-stone-600',
  wrong_number: 'bg-red-500/15 text-red-300 border border-red-500/30',
  do_not_call: 'bg-red-600/20 text-red-200 border border-red-600/40',
  // null/undefined outcome = the contact hasn't been reached/dialed yet.
  not_called: 'bg-stone-800 text-stone-400 border border-stone-700',
  other: 'bg-stone-800 text-stone-300 border border-stone-700'
};

// The RESPONSE column always shows a badge — even for contacts with a null
// outcome (treated as "Not called"). resolveOutcome maps the raw stored value
// (which may be null/undefined for never-dialed contacts) to a stable key.
function resolveOutcome(outcome) {
  return outcome || 'not_called';
}

// Human-readable label for an outcome key (raw or resolved). Falls back to a
// de-underscored version of any value we don't have an explicit label for.
const OUTCOME_LABEL = {
  answered: 'Answered',
  interested: 'Interested',
  not_interested: 'Not interested',
  callback_requested: 'Callback requested',
  referral_captured: 'Contact captured',
  gatekeeper_no_contact: 'Gatekeeper — nothing obtained',
  ivr_unreachable: 'Menu — never got through',
  // The line answered but the far end never spoke — our opener went into ringback
  // (a PBX picks up the SIP leg while the handset is still ringing). ~12.9% of
  // outbound campaign calls. Core does not WRITE this yet; registered so the
  // dashboard is never the blocker when it starts.
  pitched_into_silence: 'Nobody ever spoke',
  voicemail: 'Voicemail',
  no_answer: 'No answer',
  wrong_number: 'Wrong number',
  do_not_call: 'Do not call',
  not_called: 'Not called'
};
function outcomeLabel(outcome) {
  const key = resolveOutcome(outcome);
  return OUTCOME_LABEL[key] || key.replace(/_/g, ' ');
}

// Emoji + display order for the Call-response group section-headers. Groups are
// rendered in this order; any outcome value not listed falls into "other".
const OUTCOME_GROUP_ORDER = [
  { key: 'answered', label: 'Answered' },
  { key: 'interested', label: 'Interested' },
  { key: 'callback_requested', label: 'Callback requested' },
  { key: 'referral_captured', label: 'Contact captured' },
  { key: 'voicemail', label: 'Voicemail' },
  { key: 'no_answer', label: 'No answer' },
  // Couldn't-reach band: the number is live and worth re-calling, which is why
  // neither of these belongs under no_answer.
  { key: 'ivr_unreachable', emoji: '🔀', label: 'Menu — never got through' },
  { key: 'pitched_into_silence', emoji: '🔇', label: 'Nobody ever spoke' },
  { key: 'gatekeeper_no_contact', emoji: '🚪', label: 'Gatekeeper — nothing obtained' },
  { key: 'wrong_number', label: 'Wrong number' },
  { key: 'do_not_call', label: 'Do not call' },
  { key: 'not_interested', label: 'Not interested' },
  { key: 'other', label: 'Other' },
  { key: 'not_called', label: 'Not called' }
];

// ── Answered-call SUB-CATEGORY (best-effort, client-side) ────────────────────
// An "answered" call can mean very different things: the AI actually reached the
// business's own AI receptionist, hit voicemail, got stuck in an IVR menu, only
// talked to a gatekeeper, was hung up on, or actually reached the owner. Sorting
// answered rows by that sub-type is what the owner needs to triage follow-ups.
//
// The CLEAN sub-classification isn't stored yet — a backend follow-up (#57) will
// populate it via the QA monitor on each Call. Until then we derive a best-effort
// label CLIENT-SIDE from the call's transcript/turns + outcome. When #57 lands,
// prefer the stored field: read `call.qaSubclassification` (or whatever #57 names
// it) FIRST in callSubcategory() and only fall back to this heuristic.
const ANSWERED_SUBCATS = [
  { key: 'owner_reached',   label: 'Owner reached',    emoji: '🎯' },
  { key: 'reached_ai',      label: 'Reached AI',       emoji: '🤖' },
  { key: 'gatekeeper_only', label: 'Gatekeeper only',  emoji: '🚪' },
  { key: 'ivr',             label: 'IVR / menu',       emoji: '🔢' },
  { key: 'voicemail',       label: 'Voicemail',        emoji: '📨' },
  { key: 'hung_up',         label: 'Hung up',          emoji: '📴' },
  { key: 'unclassified',    label: 'Unclassified',     emoji: '•' }
];
const ANSWERED_SUBCAT_LABEL = Object.fromEntries(ANSWERED_SUBCATS.map((s) => [s.key, s.label]));
const ANSWERED_SUBCAT_EMOJI = Object.fromEntries(ANSWERED_SUBCATS.map((s) => [s.key, s.emoji]));

// Follow-up PRIORITY within the "answered" group — warmest human contact first:
// the owner (decision-maker) → a gatekeeper who can relay a message → their AI →
// menus / voicemail / quick hang-ups → unknown. Used to sub-order answered rows in
// the "Call response" sort so you triage the ones worth a callback first.
const ANSWERED_SUBCAT_PRIORITY = ['owner_reached', 'gatekeeper_only', 'reached_ai', 'ivr', 'voicemail', 'hung_up', 'unclassified'];
function answeredSubcatRank(key) {
  const i = ANSWERED_SUBCAT_PRIORITY.indexOf(key);
  return i === -1 ? ANSWERED_SUBCAT_PRIORITY.length : i;
}

// Flatten a call's transcript into one lowercased string for keyword matching.
function callText(call) {
  if (!call) return '';
  if (Array.isArray(call.turns) && call.turns.length > 0) {
    return call.turns.map((t) => t?.content || '').join('\n').toLowerCase();
  }
  return (call.transcript || '').toLowerCase();
}

// Best-effort sub-classification of a single ANSWERED call from its transcript.
// Returns one of ANSWERED_SUBCATS keys. This is a HEURISTIC placeholder — replace
// with the stored QA sub-classification (#57) once it's populated (see note above).
// Order matters: the most specific / highest-signal patterns win first.
function heuristicSubcategory(call) {
  const text = callText(call);
  if (!text) return 'unclassified';
  const has = (re) => re.test(text);
  // Voicemail: recorded greeting / "leave a message" / beep.
  if (has(/leave (a|your) (message|name)|after the (beep|tone)|reached the voicemail|not available to take your call|please record|at the (tone|beep)/)) {
    return 'voicemail';
  }
  // IVR / phone menu: "press N", "for X press", "main menu", dial-by-name.
  if (has(/press (one|two|three|four|[1-9])|for .{0,30} press|main menu|dial (the )?extension|if you know your party'?s extension|para español/)) {
    return 'ivr';
  }
  // Reached the business's OWN AI receptionist / virtual assistant.
  if (has(/virtual (assistant|receptionist)|automated assistant|i'?m an ai|a\.?i\.? (assistant|receptionist)|this call is being handled by an assistant/)) {
    return 'reached_ai';
  }
  // Gatekeeper: front-desk/receptionist screening ("who's calling", "he's in a
  // meeting", "can I take a message") without handing off to the decision-maker.
  if (has(/who'?s calling|who may i say is calling|he'?s in a meeting|she'?s in a meeting|not available right now|can i take a message|screening (your )?call|front desk|i'?ll let (him|her|them) know/)) {
    return 'gatekeeper_only';
  }
  // Hung up: very short + no substantive back-and-forth.
  const turnCount = Array.isArray(call.turns) ? call.turns.length : 0;
  if ((call.durationSeconds != null && call.durationSeconds > 0 && call.durationSeconds < 12) ||
      (turnCount > 0 && turnCount <= 2)) {
    return 'hung_up';
  }
  // Otherwise, if there was a real conversation, treat it as the owner reached.
  if (turnCount >= 4 || text.length > 200) return 'owner_reached';
  return 'unclassified';
}

// Public entry point: the sub-category for a call. Prefers the stored QA field
// (#57) when present, else the client-side heuristic. Keep the field-name check
// here so #57 is a one-line swap with no call-site changes.
function callSubcategory(call) {
  if (!call) return 'unclassified';
  // TODO(#57): once the QA monitor populates a clean sub-classification, read it
  // here first (e.g. call.qaSubclassification / call.answeredSubtype). Until then
  // fall through to the transcript heuristic below.
  const stored = call.qaSubclassification || call.answeredSubtype;
  if (stored && ANSWERED_SUBCAT_LABEL[stored]) return stored;
  return heuristicSubcategory(call);
}

// Sales-pipeline stages, in funnel order. The value is what's stored on the
// contact (and validated server-side); the label is what the dropdown shows.
// Keep this in sync with SALES_STAGES in core/src/services/campaignService.js.
// Calling-hours days, in week order. Mirrors the routing business-hours editor.
const CALLING_DAYS = [
  ['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'],
  ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun']
];

// Coerce a campaign's stored callingHours (which may be {} or partial) into a full
// { timezone, days:{mon..sun:{enabled,start,end}} } the editor can bind to. Weekday
// windows default to enabled 9–5; weekends off. tz defaults to the tenant's zone —
// not a hardcoded Chicago — so calling hours line up with how the dashboard reads
// every other time.
function normalizeCallingHours(ch, fallbackTz) {
  const src = ch && typeof ch === 'object' ? ch : {};
  const days = src.days && typeof src.days === 'object' ? src.days : {};
  const dayOf = (key, weekend) => {
    const d = days[key] || {};
    return {
      enabled: typeof d.enabled === 'boolean' ? d.enabled : !weekend,
      start: /^([01]\d|2[0-3]):[0-5]\d$/.test(d.start) ? d.start : (weekend ? '10:00' : '09:00'),
      end: /^([01]\d|2[0-3]):[0-5]\d$/.test(d.end) ? d.end : (weekend ? '14:00' : '17:00')
    };
  };
  return {
    timezone: src.timezone || fallbackTz || 'UTC',
    days: {
      mon: dayOf('mon', false), tue: dayOf('tue', false), wed: dayOf('wed', false),
      thu: dayOf('thu', false), fri: dayOf('fri', false),
      sat: dayOf('sat', true), sun: dayOf('sun', true)
    }
  };
}

// Pitch/script cap — MUST match the backend systemPromptOverride cap (createSchema
// in core/src/routes/campaigns.js, currently 32000). The old 8000 here silently
// truncated real pitches (~17K for dental).
const PITCH_MAX = 32000;

// Hard-coded guardrails the AI ALWAYS enforces, shown read-only under the pitch so
// the operator knows what wins over the script. These live in the backend/panel —
// this is a display mirror, not the source of truth.
const GUARDRAILS = [
  'Never asks for the owner by name or proactively asks to be put through to them — it engages whoever answers.',
  'Honors DNC / opt-out immediately — a "stop"/"remove me" ends the call and suppresses the number.',
  'Discloses it\'s an AI when asked (and on research/inquiry calls).',
  'Never texts — follow-up is by email or a scheduling link, never SMS.'
];


// HOT LEADS — owner-identified or cooperative-gatekeeper contacts. These sales
// stages mean a real person is leaning in (a decision-maker who's interested, or
// a gatekeeper who's willing to help), so they're the top follow-up priority and
// get pinned above every other row regardless of the active sort.
const isHotLead = (contact) => HOT_LEAD_STAGES.has(contact?.salesStage || '');

// VOICEMAIL / FOLLOW-UP — "call back" contacts. A left-voicemail or an explicit
// callback request is a distinct category from hot leads: someone to dial again,
// not someone already leaning in. Rendered with the sky/blue "call back" tier.
const CALLBACK_OUTCOMES = new Set(['voicemail', 'callback_requested']);
const isCallback = (contact) => CALLBACK_OUTCOMES.has(resolveOutcome(contact?.outcome));

// Build a flat render-list of { type:'group', ... } and { type:'contact', contact }
// items from the already-sorted contacts array. HOT LEADS are always pinned to the
// very top under a "🔥 Hot leads" header (regardless of sort), and are excluded
// from the normal grouping below so they never render twice. For the remaining
// contacts: when sort is 'priority' we insert a section-header before each outcome
// group; when 'salesStage' we insert one per sales stage (with an "Unset" group).
// For every other sort no headers are added. The in-group order is preserved
// exactly as the server returned it (call-time), so this is purely a visual
// grouping over the existing sort — it never reorders within a group.
function buildContactRows(contacts, sort, subcatOf = null) {
  // Pin hot leads to the top under their own header, in the sorted order the
  // server returned them. The rest of the list is built from the non-hot rows.
  const hotLeads = contacts.filter(isHotLead);
  const rest = contacts.filter((c) => !isHotLead(c));
  const hotItems = [];
  if (hotLeads.length > 0) {
    hotItems.push({ type: 'group', id: 'grp-hot-leads', emoji: '🔥', label: 'Hot leads', count: hotLeads.length });
    for (const c of hotLeads) hotItems.push({ type: 'contact', contact: c });
  }

  if (sort === 'priority') {
    const items = [...hotItems];
    // ⛔ NOTHING MAY FALL OUT OF THIS LIST.
    //
    // `items` was built ONLY from OUTCOME_GROUP_ORDER, so a contact whose outcome
    // matched no entry was never pushed — it VANISHED from the default view. The
    // comment above claimed unlisted values "fall into other"; they did not. Live
    // today for the 4 contacts stored as `ivr_unreachable`, and it would have hit
    // every new value the moment core wrote one — including anything picked from
    // the new outcome dropdown.
    //
    // Registering the missing keys is not the fix; a whitelist that loses rows will
    // lose them again. This sweeps whatever the loop did not claim into a real
    // group, so an unknown outcome is VISIBLE and obviously unrecognised instead of
    // silently absent.
    const claimed = new Set(OUTCOME_GROUP_ORDER.map((g) => g.key));
    const orphans = rest.filter((c) => !claimed.has(resolveOutcome(c.outcome)));

    for (const grp of OUTCOME_GROUP_ORDER) {
      let inGroup = rest.filter((c) => resolveOutcome(c.outcome) === grp.key);
      if (inGroup.length === 0) continue;
      // Within ANSWERED, sub-order by follow-up priority (owner → gatekeeper → AI →
      // menu/voicemail/hang-up → unknown) so the warmest human contacts sit at the
      // top of the group. Needs a subcat resolver (component-scoped); no-op without.
      if (grp.key === 'answered' && subcatOf) {
        inGroup = [...inGroup].sort(
          (a, b) => answeredSubcatRank(subcatOf(a) || 'unclassified') - answeredSubcatRank(subcatOf(b) || 'unclassified')
        );
      }
      items.push({ type: 'group', id: `grp-outcome-${grp.key}`, emoji: grp.emoji, label: grp.label, count: inGroup.length });
      for (const c of inGroup) items.push({ type: 'contact', contact: c });
    }
    if (orphans.length) {
      // Named for what it is. A row here means core wrote an outcome the dashboard
      // has never been told about — worth noticing, not worth hiding.
      items.push({ type: 'group', id: 'grp-outcome-unrecognised', emoji: '❔',
        label: 'Unrecognised outcome', count: orphans.length });
      for (const c of orphans) items.push({ type: 'contact', contact: c });
    }
    return items;
  }
  if (sort === 'salesStage') {
    const stageLabel = (v) => {
      const opt = SALES_STAGE_OPTIONS.find((o) => o.value === (v || ''));
      return opt && opt.value ? opt.label : 'Unset';
    };
    // Funnel order = SALES_STAGE_OPTIONS order, with '' (Unset) last. Hot-lead
    // stages are already pinned to the top, so skip them in the funnel grouping.
    const order = SALES_STAGE_OPTIONS
      .map((o) => o.value)
      .filter((v) => v && !HOT_LEAD_STAGES.has(v))
      .concat(['']);
    const items = [...hotItems];
    for (const stageVal of order) {
      const inGroup = rest.filter((c) => (c.salesStage || '') === stageVal);
      if (inGroup.length === 0) continue;
      items.push({ type: 'group', id: `grp-stage-${stageVal || 'unset'}`, emoji: stageVal ? '📊' : '⚪', label: stageLabel(stageVal), count: inGroup.length });
      for (const c of inGroup) items.push({ type: 'contact', contact: c });
    }
    return items;
  }
  if (sort === 'name') {
    // BUGFIX: 'name' previously fell through to the default branch below, which
    // preserves whatever order the rows arrived in — so "Sort by → Name" never
    // actually alphabetized. Sort the non-hot rows here, client-side: by name,
    // case-insensitive, with nameless contacts pushed to the end (nulls last).
    const byName = [...rest].sort((a, b) => {
      const an = (a.name || '').trim();
      const bn = (b.name || '').trim();
      if (!an && !bn) return 0;
      if (!an) return 1;  // nulls/blank last
      if (!bn) return -1;
      return an.localeCompare(bn, undefined, { sensitivity: 'base' });
    });
    return [...hotItems, ...byName.map((c) => ({ type: 'contact', contact: c }))];
  }
  if (sort === 'drivers') {
    // Flat list, largest driver fleet first. No grouping: the whole point is a single
    // ranked run of who to call, and a group header between 206 and 191 would break
    // exactly the reading the sort exists for. Rows with NO driver data go last,
    // after every known count including 0 — absent data is not "a small carrier".
    const known = rest.filter((c) => driversOf(c) !== null)
      .sort((a, b) => driversOf(b) - driversOf(a));
    const unknown = rest.filter((c) => driversOf(c) === null);
    const items = [...hotItems, ...known.map((c) => ({ type: 'contact', contact: c }))];
    if (unknown.length) {
      items.push({ type: 'group', id: 'grp-drivers-unknown', emoji: '❔', label: 'No driver count on file', count: unknown.length });
      for (const c of unknown) items.push({ type: 'contact', contact: c });
    }
    return items;
  }
  if (sort === 'endorsements') {
    // Two groups: hazmat-authorized carriers, then the rest. Within each, larger CDL
    // fleets first (total_cdl desc, then trucks desc) so the biggest hazmat haulers
    // sit at the very top. Hot leads stay pinned above both groups.
    const cdlWeight = (c) => {
      const p = carrierProfileOf(c);
      return (Number(p.totalCdl) || 0) * 1000 + (Number(p.trucks) || 0);
    };
    const byWeight = (a, b) => cdlWeight(b) - cdlWeight(a);
    const yes = rest.filter(hazmatOf).sort(byWeight);
    const no = rest.filter((c) => !hazmatOf(c)).sort(byWeight);
    const items = [...hotItems];
    if (yes.length) {
      items.push({ type: 'group', id: 'grp-hazmat-yes', emoji: '☣️', label: 'Hazmat authorized', count: yes.length });
      for (const c of yes) items.push({ type: 'contact', contact: c });
    }
    if (no.length) {
      items.push({ type: 'group', id: 'grp-hazmat-no', emoji: '🚚', label: 'Not hazmat-authorized', count: no.length });
      for (const c of no) items.push({ type: 'contact', contact: c });
    }
    return items;
  }
  return [...hotItems, ...rest.map((c) => ({ type: 'contact', contact: c }))];
}

// The contacts-table sort options. PRIMARY = 'priority' (Call response): answered
// conversations float to the top so the owner can bulk-stamp them the earliest
// call-times. The label + which is active is shown in the "Sort by:" control.
const SORT_OPTIONS = [
  { value: 'priority', label: 'Call response', hint: 'Answered conversations first, then voicemail, then couldn’t-reach — so you can call the warm ones first.' },
  { value: 'salesStage', label: 'Sales stage', hint: 'Order by pipeline stage (answered → interested → email → demo → trial).' },
  { value: 'callTime', label: 'Call time', hint: 'Default — by each contact’s 1st-call time, the order the dialer runs.' },
  { value: 'name', label: 'Name', hint: 'Alphabetical by contact name.' },
  // Carrier-list only (gated on hasHazmatData at render). Client-side — never sent
  // to the API sort param. Groups hazmat-authorized carriers first, then the rest.
  { value: 'endorsements', label: 'Endorsements', hint: 'Hazmat-authorized carriers first (FMCSA), then the rest — within each, larger CDL fleets first.' },
  // Carrier-list only (gated on hasDriverData at render). Client-side — never sent to
  // the API sort param. Driver count is the qualifier for this vertical: the biggest
  // driver employers are the call worth making, and they are NOT identifiable from the
  // company name (see driversOf).
  { value: 'drivers', label: 'Drivers', hint: 'Largest driver fleets first — the qualifier for a carrier list. Rows with no driver data sort last.' }
];

// Feature-detect whether THIS browser can decode a given audio MIME type. We
// create a throwaway <audio> and ask canPlayType() — Safari returns '' (no) for
// audio/ogg (Opus), which is exactly the format our egress writes, so this is
// what lets us fall back to a download link on Safari instead of a dead player.
function canBrowserPlay(mimeType) {
  if (typeof document === 'undefined' || !mimeType) return true; // assume yes if we can't test
  const el = document.createElement('audio');
  if (typeof el.canPlayType !== 'function') return true;
  // canPlayType returns 'probably' | 'maybe' | '' — '' means "no".
  return el.canPlayType(mimeType) !== '';
}

// Authenticated audio player for a call recording. Call.recordingUrl points at
// an owner-auth core route that streams the recording from the VPS store — a
// bare <audio src> can't send the bearer token, so we lazily fetch the file as
// a blob URL (on first "Load recording" click) and hand it to <audio>. The blob
// URL is revoked on unmount so we don't leak object URLs across re-renders.
//
// Recordings are .ogg/opus, which Safari CANNOT decode. Rather than showing a
// silent/dead player there, we feature-detect the fetched MIME type: if the
// browser can't play it we surface a clear message + a download link so the
// owner can open the file in Chrome (or download it) instead of hitting a wall.
function RecordingPlayer({ recordingUrl }) {
  const [rec, setRec] = useState(null); // { url, mimeType, ext } once loaded
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [playbackError, setPlaybackError] = useState(false);

  useEffect(() => {
    // Revoke any previously-created object URL when this player unmounts or the
    // recording changes, so the browser frees the buffered audio.
    return () => { if (rec?.url) URL.revokeObjectURL(rec.url); };
  }, [rec]);

  const load = async () => {
    setLoading(true);
    setError('');
    setPlaybackError(false);
    try {
      const result = await callsAPI.recordingBlobUrl(recordingUrl);
      setRec(result);
    } catch (err) {
      setError(err.message || 'Could not load the recording.');
    } finally {
      setLoading(false);
    }
  };

  // A stable download filename from the recording stem (last path segment).
  const downloadName = (() => {
    const stem = (recordingUrl.split('/').pop() || 'recording').split(/[?#]/)[0];
    return stem || 'recording';
  })();

  if (rec) {
    // Unsupported format (Safari + .ogg/opus) OR the <audio> element itself
    // errored on decode → drop to a clear message + download link.
    const unsupported = playbackError || !canBrowserPlay(rec.mimeType);
    if (unsupported) {
      return (
        <div className="flex flex-col gap-1.5">
          <p className="text-xs text-amber-300/90">
            Recording is {rec.ext ? `.${rec.ext}` : rec.mimeType} — your browser can’t play it inline
            (Safari can’t play .ogg/Opus). Open in Chrome, or download it below.
          </p>
          <a
            href={rec.url}
            download={downloadName}
            className="inline-flex items-center gap-1.5 self-start rounded-md border border-stone-700 bg-stone-800/60 px-2.5 py-1.5 text-xs text-stone-200 hover:bg-stone-800"
          >
            <Download size={13} /> Download recording
          </a>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-1.5">
        <audio
          controls
          className="w-full max-w-md"
          onError={() => setPlaybackError(true)}
        >
          <source src={rec.url} type={rec.mimeType} />
        </audio>
        <a
          href={rec.url}
          download={downloadName}
          className="inline-flex items-center gap-1.5 self-start text-[11px] text-stone-500 hover:text-stone-300"
        >
          <Download size={12} /> Download
        </a>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={load}
        disabled={loading}
        className="inline-flex items-center gap-1.5 rounded-md border border-stone-700 bg-stone-800/60 px-2.5 py-1.5 text-xs text-stone-200 hover:bg-stone-800 disabled:opacity-50"
      >
        <Play size={13} /> {loading ? 'Loading…' : 'Load recording'}
      </button>
      {error && <span className="text-xs text-red-400">{error}</span>}
    </div>
  );
}

// Curated tag chips the owner can toggle when coaching a call. Free-form on the
// wire (the core service accepts any tag string) — this list just seeds the UI.
const FEEDBACK_TAGS = [
  'opener', 'objection_handling', 'silence', 'value_pitch',
  'pacing', 'tone', 'closing', 'discovery', 'compliance'
];

// Per-call FEEDBACK / LEARNING-LOOP panel — the owner's "machine learning"
// annotation. 👍/👎/needs-improvement + a coaching textarea ("how the AI SHOULD
// have handled it") + tag chips → POST /calls/:id/feedback. The coaching is
// compiled into the tenant's COACHING PLAYBOOK and injected into the OUTBOUND
// agent's prompt on the NEXT calls. Lazily loads existing feedback on first open.
function CallFeedbackPanel({ callId }) {
  const [open, setOpen] = useState(false);
  const [existing, setExisting] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [rating, setRating] = useState('good');
  const [coaching, setCoaching] = useState('');
  const [tags, setTags] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded) {
      try {
        const res = await callsAPI.listFeedback(callId);
        setExisting(res.data || []);
      } catch (err) {
        setError(err.message || 'Could not load feedback.');
      } finally {
        setLoaded(true);
      }
    }
  };

  const toggleTag = (t) => {
    setTags((prev) => {
      const n = new Set(prev);
      n.has(t) ? n.delete(t) : n.add(t);
      return n;
    });
  };

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await callsAPI.addFeedback(callId, {
        rating,
        coaching: coaching.trim() || undefined,
        tags: [...tags]
      });
      setExisting((prev) => [res.data, ...prev]);
      setCoaching('');
      setTags(new Set());
      setRating('good');
      setSavedAt(Date.now());
    } catch (err) {
      setError(err.message || 'Could not save feedback.');
    } finally {
      setSaving(false);
    }
  };

  const ratingBtn = (value, label, Icon) => (
    <button
      type="button"
      onClick={() => setRating(value)}
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs ${
        rating === value
          ? 'border-alfred-gold/50 bg-alfred-gold/15 text-alfred-gold'
          : 'border-stone-700 bg-stone-800/60 text-stone-300 hover:bg-stone-800'
      }`}
    >
      {Icon && <Icon size={13} />} {label}
    </button>
  );

  return (
    <div className="mt-3 border-t border-stone-800 pt-3">
      <button
        type="button"
        onClick={toggleOpen}
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-stone-400 hover:text-stone-200"
      >
        <GraduationCap size={14} /> Coach this call {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        {existing.length > 0 && (
          <span className="ml-1 rounded-full bg-stone-800 px-1.5 py-0.5 text-[10px] text-stone-300">{existing.length}</span>
        )}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {existing.length > 0 && (
            <div className="space-y-2">
              {existing.map((f) => (
                <div key={f.id} className="rounded-md border border-stone-800 bg-stone-900/40 px-3 py-2 text-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`badge ${
                      f.rating === 'good'
                        ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                        : f.rating === 'bad'
                          ? 'bg-red-500/15 text-red-300 border border-red-500/30'
                          : 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                    }`}>
                      {f.rating === 'needs_improvement' ? 'needs work' : f.rating}
                    </span>
                    {(f.tags || []).map((t) => (
                      <span key={t} className="text-[10px] rounded bg-stone-800 px-1.5 py-0.5 text-stone-400">{t}</span>
                    ))}
                  </div>
                  {f.coaching && <p className="text-stone-300 whitespace-pre-wrap">{f.coaching}</p>}
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2">
            {ratingBtn('good', 'Good', ThumbsUp)}
            {ratingBtn('bad', 'Bad', ThumbsDown)}
            {ratingBtn('needs_improvement', 'Needs work')}
          </div>

          <textarea
            value={coaching}
            onChange={(e) => setCoaching(e.target.value)}
            rows={3}
            placeholder="How SHOULD the AI have handled it? (this lesson is fed back into the AI on the next calls)"
            className="w-full rounded-md border border-stone-700 bg-stone-900/60 px-3 py-2 text-sm text-stone-100 placeholder:text-stone-500"
          />

          <div className="flex flex-wrap gap-1.5">
            {FEEDBACK_TAGS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => toggleTag(t)}
                className={`rounded-full border px-2 py-0.5 text-[11px] ${
                  tags.has(t)
                    ? 'border-alfred-gold/50 bg-alfred-gold/15 text-alfred-gold'
                    : 'border-stone-700 bg-stone-800/40 text-stone-400 hover:bg-stone-800'
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md border border-alfred-gold/40 bg-alfred-gold/10 px-3 py-1.5 text-xs text-alfred-gold hover:bg-alfred-gold/20 disabled:opacity-50"
            >
              <Save size={13} /> {saving ? 'Saving…' : 'Save coaching'}
            </button>
            {savedAt && <span className="text-xs text-emerald-400">Saved — applied to future calls.</span>}
            {error && <span className="text-xs text-red-400">{error}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// Emoji for an outcome key in the report breakdown (reuses the group-order map).
const OUTCOME_EMOJI = Object.fromEntries(OUTCOME_GROUP_ORDER.map((g) => [g.key, g.emoji]));

// CSV-escape a cell: wrap in quotes and double any embedded quotes. Numbers and
// nullish values are coerced to a safe string first.
function csvCell(v) {
  const s = v == null ? '' : String(v);
  return `"${s.replace(/"/g, '""')}"`;
}

// Build the per-contact export rows and trigger a browser download. One row per
// contact: name, company, phone, outcome, sales stage, notes, call duration, and
// whether a transcript / recording is present. callsForContact maps a contact to
// its Call row(s) (already loaded by the page); we use the most recent call for
// the duration / transcript / recording columns.
function exportContactsCsv(campaignName, contacts, callsForContact) {
  const headers = [
    'name', 'company', 'phoneE164', 'outcome', 'salesStage', 'outcomeNotes',
    'callDurationSeconds', 'hasTranscript', 'hasRecording'
  ];
  const lines = [headers.map(csvCell).join(',')];
  for (const c of contacts) {
    const calls = callsForContact(c) || [];
    // Newest call first (the calls list is startedAt desc), pick the first with data.
    const call = calls[0] || null;
    const duration = call?.durationSeconds ?? '';
    const hasTranscript = call && (call.transcript || (call.turns && call.turns.length > 0)) ? 'yes' : 'no';
    const hasRecording = call && call.recordingUrl ? 'yes' : 'no';
    lines.push([
      c.name || '',
      c.company || '',
      c.phoneE164 || '',
      resolveOutcome(c.outcome),
      c.salesStage || '',
      c.outcomeNotes || '',
      duration,
      hasTranscript,
      hasRecording
    ].map(csvCell).join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const safe = (campaignName || 'campaign').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase();
  a.href = url;
  a.download = `${safe || 'campaign'}-report.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// End-of-campaign RESULTS REPORT card. A summary the owner reads when a campaign
// finishes: headline metrics, a per-outcome breakdown (count + %), the sales-stage
// funnel, and a CSV export. Backed by GET /campaigns/:id/report; the CSV is built
// client-side from the contacts the page already loaded. Renders nothing until the
// report has loaded (it's fetched alongside the rest of the page).
function CampaignReportCard({ report, onExport, exporting }) {
  if (!report) return null;
  const m = report.metrics;
  const fmtPct = (n) => `${n}%`;
  const metricTiles = [
    { label: 'Contacts', value: m.totalContacts, Icon: MessageSquareText },
    { label: 'Dialed', value: m.dialed, Icon: PhoneCall },
    { label: '2nd calls', value: m.secondCalls, Icon: Phone },
    { label: 'Conversations', value: m.conversations, Icon: MessageCircle },
    { label: 'Connect rate', value: fmtPct(m.connectRate), Icon: TrendingUp, hint: 'answered ÷ dialed' },
    { label: 'Interest rate', value: fmtPct(m.interestRate), Icon: TrendingUp, hint: '(interested + callback) ÷ answered' },
    { label: 'Talk time', value: formatDuration(m.totalDurationSeconds), Icon: Phone, hint: 'total connected call duration' }
  ];
  // Only show outcome rows that have a count, to keep the card tight — but always
  // keep the canonical order. A 0-count "answered" still hides; the metrics tiles
  // carry the headline figures regardless.
  const outcomeRows = report.outcomes.filter((o) => o.count > 0);
  const funnelRows = report.funnel; // canonical funnel order; show all stages

  return (
    <section className="card mb-6 border-alfred-gold/30">
      <header className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <h2 className="text-lg text-stone-100 inline-flex items-center gap-2">
          <BarChart3 size={18} className="text-alfred-gold" /> Results report
        </h2>
        <button
          type="button"
          className="btn-ghost"
          onClick={onExport}
          disabled={exporting}
          title="Download one row per contact (name, company, phone, outcome, stage, notes, duration, transcript/recording present)"
        >
          <Download size={14} /> {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </header>

      {/* Headline metric tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        {metricTiles.map((t) => (
          <div key={t.label} className="rounded-lg border border-stone-800 bg-stone-900/40 px-3 py-2.5" title={t.hint || ''}>
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-stone-500">
              {t.Icon && <t.Icon size={12} />} {t.label}
            </div>
            <div className="mt-1 text-xl text-stone-100 tabular-nums">{t.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Outcome breakdown */}
        <div>
          <h3 className="text-sm uppercase tracking-wider text-stone-500 mb-3">Outcome breakdown</h3>
          {outcomeRows.length === 0 ? (
            <p className="text-sm text-stone-500">No outcomes recorded yet.</p>
          ) : (
            <ul className="space-y-2">
              {outcomeRows.map((o) => (
                <li key={o.key} className="flex items-center gap-3">
                  <span className="w-44 shrink-0 text-sm text-stone-300">
                    {OUTCOME_LABEL[o.key] || o.key.replace(/_/g, ' ')}
                  </span>
                  {/* Bar scaled to its share of total contacts. */}
                  <span className="flex-1 h-2 rounded-full bg-stone-800 overflow-hidden">
                    <span className="block h-full rounded-full bg-alfred-gold/60" style={{ width: `${Math.min(100, o.pct)}%` }} />
                  </span>
                  <span className="w-24 shrink-0 text-right text-sm text-stone-400 tabular-nums">
                    {o.count} · {o.pct}%
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Sales-stage funnel */}
        <div>
          <h3 className="text-sm uppercase tracking-wider text-stone-500 mb-3">Sales-stage funnel</h3>
          <ul className="space-y-2">
            {funnelRows.map((f) => {
              const opt = SALES_STAGE_OPTIONS.find((o) => o.value === f.stage);
              return (
                <li key={f.stage} className="flex items-center justify-between gap-3 rounded-md border border-stone-800 bg-stone-900/40 px-3 py-1.5">
                  <span className="text-sm text-stone-300">{opt ? opt.label : f.stage}</span>
                  <span className="text-sm text-stone-100 tabular-nums">{f.count}</span>
                </li>
              );
            })}
          </ul>
          <p className="mt-2 text-xs text-stone-500">
            Stages are set manually per contact in the table below.
          </p>
        </div>
      </div>
    </section>
  );
}

/**
 * Why did a fill/clear touch ZERO rows?
 *
 * ⛔ EVERY scheduling path in core requires `status: 'queued'` — dueContacts (the
 * dialer), the unfiltered fill, the clear, AND the scoped per-selection fill. Bulk
 * `restore` only un-suppresses; it does not un-complete. So a contact that has been
 * called once can NEVER be re-queued from this dashboard, and Add/Clear silently
 * report 0 as if the buttons were dead. Brian hit exactly this on a 1-contact test
 * campaign whose only row was `completed`.
 *
 * Until core relaxes the scoped guard to "anything except suppressed/do_not_call",
 * the honest thing is to name the blocker instead of printing a bare 0.
 */
function explainZeroScheduled(contacts) {
  const n = (st) => contacts.filter((c) => c.status === st).length;
  const completed = n('completed');
  const suppressed = n('suppressed') + n('do_not_call');
  const queued = n('queued');
  if (queued > 0) return '';
  const parts = [];
  if (completed) parts.push(`${completed} already called`);
  if (suppressed) parts.push(`${suppressed} removed from the list or on do-not-call`);
  if (!parts.length) return ' No contacts are queued.';
  return ` Nothing is queued — ${parts.join(' and ')}. Scheduling only touches queued contacts, `
    + 'so a contact that has already been called cannot be re-scheduled from here yet.';
}

/**
 * Turn core's 400 into the sentence naming WHAT it refused.
 *
 * ⛔ THE BARE CODE IS USELESS AND IT COST A DIAGNOSIS. Brian changed the AI voice,
 * the save 400'd, and the banner said exactly `bad_request` — no field, no value, no
 * reason. Core sends `issues` (a zod `.flatten()`), and `api.js` builds err.message
 * from `data.message || data.error`, so when a 400 has no `message` the whole body
 * collapses to the error CODE.
 *
 * What it was actually refusing: `roster[].voiceProvider` is
 * z.enum(['elevenlabs','deepgram','kokoro']) — and this tenant's only configured
 * voice is CARTESIA, so the picker can only ever send a value the schema rejects.
 * That is a real mismatch and it is Charlie's to widen; this makes it legible either
 * way, for every 400 on this page.
 */
function describeApiError(err, fallback) {
  const issues = err?.body?.issues;
  if (issues && typeof issues === 'object') {
    const flat = [
      ...(issues.formErrors || []),
      ...Object.entries(issues.fieldErrors || {})
        .map(([field, msgs]) => `${field}: ${[].concat(msgs).filter(Boolean).join(', ')}`)
    ].filter(Boolean);
    if (flat.length) return `${fallback} — ${flat.join(' · ')}`;
  }
  if (Array.isArray(issues) && issues.length) return `${fallback} — ${issues.join(' · ')}`;
  return err?.body?.message || err?.message || fallback;
}

// ⛔ MODULE SCOPE, NOT COMPONENT SCOPE — and that is the whole point.
// Shipped in v1.75.0 defined INSIDE CampaignDetail and called from BOTH there and
// CampaignHistory, a sibling component. The second call site was a ReferenceError
// that threw whenever the History tab met a call with no transcript. Live since
// 2026-08-11; my commit claimed "both empty-state sites updated" on the strength of
// a string match, without checking the two sites shared a scope. Third time for this
// exact shape, and the first time a linter caught it instead of Brian.
//
// It reads nothing but its argument, so it never needed component scope at all.
// ⛔ "No transcript was saved" reads like a filing error. Sometimes it IS one.
//
// 2026-08-05, Sugar Land Modern Dentistry: the follow-up connected, was answered,
// ran 41 seconds — and produced no transcript, no turns and no recording. The
// telephony side logged everything (answer, bridge, AMD, hangup cause); the two
// artifacts that come from the box, the egress recording and the agent-written
// transcript, were both absent. The leg was up and billed with nothing on it.
// Five calls did that on Aug 5. Brian read the empty panel as a dashboard bug,
// which is fair — the screen gave him no way to tell the two apart.
//
// So: a call that never connected gets a calm explanation, and a call that
// connected and still produced nothing is named as the fault it is.
function missingTranscriptReason(call) {
  const secs = Number(call?.durationSeconds) || 0;
  const answered = !!call?.answeredAt;
  if (!answered && secs === 0) {
    return { tone: 'quiet', text: 'This call was never answered, so there was nothing to transcribe.' };
  }
  if (secs > 0 && !call?.recordingUrl) {
    return {
      tone: 'alarm',
      text: `This call connected and ran ${secs}s, but nothing was captured — no transcript and no recording. `
          + 'That means the AI side produced nothing while the line was open, so the business most likely heard silence. '
          + 'This is a fault worth reporting, not an empty call.'
    };
  }
  return { tone: 'quiet', text: 'No transcript was saved for this call.' };
}

export default function CampaignDetail() {
  // In-dashboard replacement for window.confirm — see ConfirmDialog.jsx.
  const { confirm, dialogProps } = useConfirm();
  const { id } = useParams();
  // No zone chosen → no scheduling. A call time is a wall-clock promise and there
  // The calendar is ALWAYS available — never hard-locked on the client. The server
  // stays the real guard (409 timezone_required, which only fires for a tenant that
  // has genuinely never set a zone). Hard-disabling here also locked out perfectly-
  // configured tenants whose SESSION predated the timezone deploy (cached blob had
  // no zone → false lock). Times are interpreted in the tenant zone via the tz
  // singleton, which AuthContext primes on login + refresh.
  const { tenantTimeZone, user } = useAuth();
  const canSchedule = true;
  // Whether we actually know the tenant's zone yet — drives the zone LABEL only
  // (show "(CDT)" when known), never whether the controls are usable.
  const zoneKnown = !!tenantTimeZone;
  const [campaign, setCampaign] = useState(null);
  // Last SAVED behavior block, kept separately from `campaign` (which doubles as
  // the edit buffer). Without it there is no way to tell an unsaved pitch change
  // from a saved one — and "am I about to change what the next call says?" is the
  // whole question this screen answers.
  const [behaviorSaved, setBehaviorSaved] = useState(null);
  // Same reason as behaviorSaved: `campaign` is the edit buffer, so without a
  // snapshot of what the server last sent there is no way to tell an edit from a
  // value this tab simply loaded a while ago.
  const [descriptionSaved, setDescriptionSaved] = useState(null);
  // Which tab an EXPANDED contact row is showing. Defaults to 'calls' so the
  // history — the thing most people open a row to read — is still one click, while
  // the things you ACT on stop being three stacked panels above it.
  const [contactTab, setContactTab] = useState({}); // contactId -> 'calls'|'followups'|'notes'|'briefing'
  const [validatingBehavior, setValidatingBehavior] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactTotal, setContactTotal] = useState(0);
  // Contacts-table sort (one of SORT_OPTIONS' values). 'callTime' = default
  // call-order (by 1st-call time, the order the dialer runs); 'priority' (Call
  // response) floats answered conversations to the top; 'salesStage' orders by
  // the funnel; 'name' is alphabetical. Re-fetched server-side so the rows come
  // back in the chosen order — none of this changes how the dialer runs.
  const [contactSort, setContactSort] = useState('callTime');
  // Stacking contacts filter chips (multiple can be active at once). Each narrows
  // the visible list; they combine additively (a contact matching ANY active chip
  // shows). See filteredContacts below for how they're applied. The answered-by
  // chips (owner/gatekeeper/AI/voicemail/IVR/hung-up) reuse the client-side
  // sub-classification. The former standalone "Answered sub-type" <select> has
  // been folded into these chips (answeredIvr + hungUp added), so the filter now
  // drives filteredContacts directly — no separate answeredSubFilter state.
  const [filters, setFilters] = useState({
    contacted: false,
    notContacted: false,
    answered: false,       // reached a human (outcome 'answered') — the review bucket
    answeredOwner: false,
    answeredAI: false,
    answeredGatekeeper: false,
    answeredVoicemail: false,
    noAnswer: false,        // nobody picked up (outcome 'no_answer') — pairs w/ Voicemail
    answeredIvr: false,
    hungUp: false,
    interestedNoClose: false // reached + interested but no close — the recover-later bucket
  });
  // Metro filter ('' = all) — buckets contact.metadata city into a TX metro
  // (Houston/Dallas/Austin/San Antonio, suburbs folded in) and narrows to one.
  const [metroFilter, setMetroFilter] = useState('');
  // Filters collapse behind a button. Eleven chips, three dropdowns, a sort row and
  // the drill-down box were all permanently on screen above the table — the page
  // read as controls with a table attached. They stay one click away, and the
  // button carries the ACTIVE COUNT so a collapsed filter can never quietly narrow
  // the list while the screen looks unfiltered.
  const [showFilters, setShowFilters] = useState(false);
  // Hazmat-authorized-only filter (a separate AND axis, like metroFilter). Reads the
  // FMCSA-enriched contact.metadata.hazmat — see hasHazmatData / hazmatOf.
  const [hazmatOnly, setHazmatOnly] = useState(false);
  // Cargo-class filter ('' = all) — narrows to carriers whose cargo_types includes the
  // selected FMCSA class (the carrier-level "endorsement" dimension). AND axis.
  // Cargo-class filter — a SET of selected FMCSA classes (the carrier-level
  // "endorsement" dimension). Multi-select with OR semantics: a carrier matches if it
  // hauls ANY selected class, so several classes combine into one campaign segment.
  const [cargoFilters, setCargoFilters] = useState([]);
  // FIND A BUSINESS. Client-side because the whole contact list is already loaded
  // (limit 5000) — an 881-row campaign filters as you type with no round trip.
  // Matches company, contact name and phone, and ignores phone punctuation so
  // "2814920600" finds "+1 (281) 492-0600".
  const [contactQuery, setContactQuery] = useState('');
  // Deep link from the campaigns page's business search (`?q=Fairview Dental`) —
  // land on the row instead of on 881 rows. Read once; typing afterwards is free.
  const [searchParams] = useSearchParams();
  // ⛔ TWO BUGS LIVED HERE.
  //
  // 1. `?q=` is a TEXT SEARCH, so landing on a specific business depended on its name
  //    matching itself. Any punctuation difference, a renamed company, a trailing
  //    "at 281" — and you land on the campaign with 877 rows and no idea why. Leads
  //    now sends `?contact=<id>` as well, which is exact and cannot mis-match. `q` is
  //    kept as the fallback for older links and for the demo-name path, where all we
  //    have IS a name.
  // 2. The deps were `[]`, so this applied ONLY on first mount. React Router reuses
  //    this component when just the param changes, so a second deep-link into the same
  //    route silently kept the previous search.
  useEffect(() => {
    const cid = searchParams.get('contact');
    const q = searchParams.get('q');
    if (cid) setExpandedContactId(cid);
    if (q) setContactQuery(q);
  }, [searchParams]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [allLists, setAllLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const [selectedListId, setSelectedListId] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addDraft, setAddDraft] = useState({ phone: '', name: '', company: '', alsoSaveToList: '' });
  const [addStatus, setAddStatus] = useState('');
  const [activePane, setActivePane] = useState('results');
  // Within the Contacts pane: 'contacts' = schedule/manage the list; 'history' =
  // read-only log of calls actually placed (scheduling never shows here).
  const [contactsView, setContactsView] = useState('contacts');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDraft, setScheduleDraft] = useState({ start: '', interval: '5', second: '' });
  const [scheduleStatus, setScheduleStatus] = useState('');
  // "Call entries" numbered-selection control (e.g. "1, 40, 33-200").
  // selectionDraft = what's typed in the box; appliedSelection = what's actually
  // in effect (narrows the view + is the target of the selection actions). Typing
  // no longer narrows live — you press "Enter" to APPLY. This keeps the box from
  // scheduling anything (it used to lay times down) and from jumping around as you
  // type; scheduling a selection is done with "Add" in the scheduling area above.
  const [selectionDraft, setSelectionDraft] = useState('');
  // ── REMOVED-FROM-THIS-LIST ───────────────────────────────────────────────────
  // Brian, 2026-07-31: "there's still some calls we don't wanna call for whatever
  // reason, so we're gonna remove those individually from that filtered list."
  //
  // The row checkbox USED to only mark a row so the bulk actions could be scoped to
  // it, which meant ticking a box appeared to do nothing at all. It now REMOVES the
  // row from the working list: it disappears from the table, it stops being counted
  // in "N shown", and — because every scheduling action targets the visible rows —
  // it will not be called.
  //
  // ⛔ It is NOT a delete and must never become one. The contact, its transcripts
  // and its outcome stay in the campaign and in the report. The same endpoint has a
  // `delete` action; this screen deliberately never calls it.
  //
  // ⚠️ A CLIENT-SIDE HIDE ALONE WOULD BE A LIE. A contact that already carries a
  // scheduled time is dialed by the worker regardless of what this table shows, so
  // hiding the row would tell Brian it won't be called while it still would be. The
  // hide is therefore optimistic UI over a REAL server write —
  // POST /contacts/bulk {action:'suppress'}, which moves the row out of the status
  // the dialer claims ('queued') — and when that write can't happen, the chip says
  // plainly that the row is only hidden.
  const [removedIds, setRemovedIds] = useState(() => new Set());
  // Set when the endpoint 404s — it ships behind Alpha's next core deploy. Until
  // then removal is view-only, and the UI must not imply otherwise.
  const [suppressUnavailable, setSuppressUnavailable] = useState(false);
  const [removeStatus, setRemoveStatus] = useState('');

  // A row is off the list if this session removed it OR the server already has it
  // suppressed. The second half is what makes a reload agree with the screen: the
  // in-memory set is empty after F5, and without the status check every row removed
  // yesterday would come back looking callable while the dialer still skipped it.
  const isRemoved = (c) => removedIds.has(c.id) || c.status === 'suppressed';

  const removeFromList = async (contact) => removeManyFromList([contact]);

  // One request for the whole set — Charlie's endpoint is bulk precisely so a
  // partial failure can't leave half a list changed.
  // Returns { ok, changed, hiddenOnly, message } so a CALLER can report the truth.
  // It used to swallow every outcome, and the drill-down button then announced
  // "Removed N — still in the campaign, not called" even on a 404 where nothing was
  // written and an already-scheduled contact would still be dialed.
  const removeManyFromList = async (targets) => {
    // ⛔ NEVER suppress a call that is IN FLIGHT. Core's SUPPRESSABLE list includes
    // 'attempting', and outcomeClassifier early-returns on a suppressed contact —
    // upstream of the only place a spoken opt-out is written to the DNC table. So
    // removing a row mid-call can silently discard "take me off your list". Until
    // core fixes that ordering, this screen refuses.
    const live = targets.filter((c) => c.status === 'attempting');
    const safe = targets.filter((c) => c.status !== 'attempting');
    const ids = safe.map((c) => c.id).filter(Boolean);
    if (live.length && !ids.length) {
      const msg = `Not removed — ${live.length === 1 ? 'that call is' : `${live.length} calls are`} in progress. Try again once it ends.`;
      setRemoveStatus(msg);
      return { ok: false, changed: 0, hiddenOnly: false, message: msg };
    }
    if (!ids.length) return { ok: false, changed: 0, hiddenOnly: false, message: 'Nothing to remove.' };
    setRemovedIds((prev) => { const n = new Set(prev); ids.forEach((x) => n.add(x)); return n; });
    setRemoveStatus('');
    try {
      const r = await campaignsAPI.bulkContacts(id, { ids, action: 'suppress' });
      const out = r?.data || r;
      // Core protects `completed` and `do_not_call` and reports them back rather
      // than silently changing nothing. Say which happened — "removed" on a row the
      // server refused to change would be the same silent lie in a smaller place.
      const s = out?.skipped || {};
      const notes = [];
      if (s.completed) notes.push(`${s.completed} already called (kept in the report)`);
      if (s.doNotCall) notes.push(`${s.doNotCall} on Do-Not-Call (status protected)`);
      if (s.notFound) notes.push(`${s.notFound} no longer in the campaign`);
      // `other` = a status core's SUPPRESSABLE list doesn't cover. Rare, but without
      // this the row is hidden, nothing was written, and the chip says "not called".
      if (s.other) notes.push(`${s.other} the server wouldn’t change`);
      if (live.length) notes.push(`${live.length} left in — call in progress`);
      // Say how many were ACTUALLY changed vs how many were ticked. Reporting only
      // the ticked count would read as success on rows the server refused to touch.
      const message = notes.length
        ? `${out?.changed ?? 0} removed · ${notes.join(' · ')} — hidden here either way.`
        : '';
      setRemoveStatus(message);
      const changedIds = new Set(ids);
      if ((out?.changed ?? 0) > 0) {
        // Reflect the new status locally so a render without a reload agrees with the
        // server, and so rows stay out once the session overlay is cleared.
        setContacts((prev) => prev.map((c) => (changedIds.has(c.id) ? { ...c, status: 'suppressed' } : c)));
      }
      setSuppressUnavailable(false);
      return { ok: true, changed: out?.changed ?? 0, hiddenOnly: false, message };
    } catch (err) {
      if (err.status === 404) {
        // Endpoint not deployed yet. Keep the rows hidden (still useful — every
        // scheduling action targets the visible rows) but do NOT claim they won't
        // be called; the chip switches to the view-only wording.
        setSuppressUnavailable(true);
        return { ok: false, changed: 0, hiddenOnly: true, message: '' };
      }
      setRemovedIds((prev) => { const n = new Set(prev); ids.forEach((x) => n.delete(x)); return n; });
      const message = err.body?.message || err.message || 'Could not remove those contacts.';
      setRemoveStatus(message);
      return { ok: false, changed: 0, hiddenOnly: false, message };
    }
  };

  // ⛔ A ROW SUPPRESSED BY THE DIALER'S DNC SCRUB IS NOT RESTORABLE FROM HERE.
  // `dialerWorker` marks an opt-out with status='suppressed' AND outcome='do_not_call'.
  // The bulk route's protection tests `status === 'do_not_call'`, which that row is
  // NOT — so it would sail into `eligible`, come back as 'queued', and report
  // skipped.doNotCall = 0. "Restore all" would then re-arm an opted-out number while
  // telling the operator nothing happened. Filter them out HERE and say so; the
  // server-side guard is being fixed separately (routed to Charlie).
  const restorable = contacts.filter((c) => isRemoved(c) && resolveOutcome(c.outcome) !== 'do_not_call');
  const restoreAllRemoved = async () => {
    const ids = restorable.map((c) => c.id);
    const heldBack = removedCount - ids.length;
    setRemoveStatus('');
    if (suppressUnavailable) {
      // Nothing was ever written, so there is nothing to restore server-side —
      // just drop the local hide.
      setRemovedIds(new Set());
      return;
    }
    if (!ids.length) {
      setRemoveStatus(heldBack
        ? `Nothing restored — ${heldBack} opted out and can’t be put back in the queue.`
        : '');
      return;
    }
    try {
      const r = await campaignsAPI.bulkContacts(id, { ids, action: 'restore' });
      const out = r?.data || r;
      const idSet = new Set(ids);
      setContacts((prev) => prev.map((c) => (idSet.has(c.id) ? { ...c, status: 'queued' } : c)));
      // Only clear the local hide for what the server actually restored — clearing
      // it up front would show a row as callable on a request that failed.
      setRemovedIds((prev) => { const n = new Set(prev); ids.forEach((x) => n.delete(x)); return n; });
      const skippedDnc = (out?.skipped?.doNotCall || 0) + heldBack;
      if (skippedDnc) setRemoveStatus(`${out?.changed ?? 0} restored · ${skippedDnc} stayed out — on Do-Not-Call and never re-queued.`);
    } catch (err) {
      setRemoveStatus(err.status === 404
        ? 'Restore needs the newer server — nothing was changed.'
        : (err.body?.message || err.message || 'Could not restore.'));
    }
  };
  const [appliedSelection, setAppliedSelection] = useState('');
  const applySelection = () => setAppliedSelection(selectionDraft.trim());
  const [selectionStatus, setSelectionStatus] = useState('');
  // Tenant-wide (Settings) library + the subset opted into THIS campaign. The
  // included* sets are kept in component state for snappy checkbox toggling and
  // persisted to the campaign's includedKnowledgeIds / includedFaqIds on toggle.
  const [libKnowledge, setLibKnowledge] = useState([]);
  const [libFaqs, setLibFaqs] = useState([]);
  const [includedKnowledge, setIncludedKnowledge] = useState(() => new Set());
  const [includedFaqs, setIncludedFaqs] = useState(() => new Set());
  const [libError, setLibError] = useState('');
  // Call review — this campaign's calls + their full transcripts. Now folded
  // into the Contacts table: each contact row reveals its call(s) inline.
  const [calls, setCalls] = useState([]);
  const [callsError, setCallsError] = useState('');
  // End-of-campaign results report (per-outcome breakdown + metrics + funnel),
  // fetched from GET /campaigns/:id/report alongside the rest of the page.
  const [report, setReport] = useState(null);
  const [exporting, setExporting] = useState(false);
  // Knowledge & FAQ is long; collapse it by default so the owner doesn't scroll
  // past it every visit. Local/per-session is fine — no need to persist.
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  // Scheduling section — relocated ABOVE the contact list, collapsed by default
  // (mirrors the Knowledge collapse pattern). Inside, the two modes render as
  // TWO TABS ('auto' = Auto-schedule, 'autonomous' = rate-based) instead of two
  // stacked panels; only the active tab's controls render.
  const [schedulingOpen, setSchedulingOpen] = useState(false);
  const [scheduleTab, setScheduleTab] = useState('auto');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [c, contactsRes, phonesRes, listsRes, reportRes] = await Promise.all([
        campaignsAPI.get(id),
        // Pass the active table sort so the server returns rows in the chosen
        // order. 'callTime' is the server default, so we omit it; the rest
        // ('priority' / 'salesStage' / 'name') are passed through explicitly.
        // Load the WHOLE contact list, not a 200-row page: filter chips + their
        // counts run client-side over what's loaded, so a page cap silently
        // truncated every campaign over 200 contacts (a 337-list showed only
        // entries 1–200, and "Not contacted" counted just the loaded slice).
        // The server caps its own take (500 today), so this is an upper bound,
        // not an unbounded fetch. NOTE: campaigns over 500 contacts still
        // truncate at 500 until the backend take-cap (Math.min(500, limit) in
        // campaignService.listContacts) is lifted separately.
        // 'endorsements' is a client-side-only sort (buildContactRows groups by hazmat);
        // never send it to the API, which doesn't know that key.
        campaignsAPI.contacts(id, { limit: 5000, sort: (contactSort === 'callTime' || contactSort === 'endorsements' || contactSort === 'drivers') ? undefined : contactSort }),
        phoneNumbersAPI.list().catch(() => ({ data: [] })),
        listsAPI.list().catch(() => ({ data: [] })),
        // The results report is non-blocking — if it fails the rest of the page
        // still renders (the card simply doesn't show).
        campaignsAPI.report(id).catch(() => null)
      ]);
      const camp = c.data || c;
      setCampaign(camp);
      setBehaviorSaved(camp.behaviorBlock ?? null);
      setDescriptionSaved(camp.description ?? null);
      setIncludedKnowledge(new Set(camp.includedKnowledgeIds || []));
      setIncludedFaqs(new Set(camp.includedFaqIds || []));
      setContacts(contactsRes?.data || []);
      setContactTotal(contactsRes?.total || 0);
      setPhoneNumbers(phonesRes?.data || phonesRes || []);
      setAllLists(listsRes?.data || []);
      setReport(reportRes?.data || null);
      // Refresh the merged-in transcripts too, so the top "Refresh" button (and
      // the post-mutation reloads) keep each contact's inline call list current.
      loadCalls();
    } catch (err) {
      setError(err.message || 'Failed to load campaign.');
    } finally {
      setLoading(false);
    }
  };

  const importFromList = async () => {
    if (!selectedListId) { setImportStatus('Pick a list first.'); return; }
    setImportStatus('Importing…');
    try {
      const r = await listsAPI.importToCampaign(selectedListId, id);
      setImportStatus(`Added ${r.data.added}, skipped ${r.data.skipped} (already in campaign), total in list ${r.data.total}.`);
      load();
    } catch (err) {
      setImportStatus(err.message || 'Import failed.');
    }
  };

  const addOneContact = async () => {
    const e164 = normalizePhoneInput(addDraft.phone);
    if (!e164) {
      setAddStatus(`Couldn't parse "${addDraft.phone}". Need 10-digit US (e.g. 7135551234) or full E.164 (+13462580006).`);
      return;
    }
    setAddStatus('Adding…');
    try {
      const row = {
        phoneE164: e164,
        name: addDraft.name.trim() || undefined,
        company: addDraft.company.trim() || undefined
      };
      const r = await campaignsAPI.addContacts(id, [row]);
      // Optionally save to a list at the same time.
      if (addDraft.alsoSaveToList) {
        await listsAPI.addMembers(addDraft.alsoSaveToList, [row]).catch(() => {});
      }
      setAddStatus(`Added ${r.data.added}${addDraft.alsoSaveToList ? ' (also saved to list)' : ''}.`);
      setAddDraft({ phone: '', name: '', company: '', alsoSaveToList: '' });
      load();
    } catch (err) {
      setAddStatus(err.message || 'Add failed.');
    }
  };
  // Reload on campaign change AND when the contacts sort toggles, so flipping
  // between call-order and recall-priority re-fetches the rows in the new order.
  useEffect(() => { load(); }, [id, contactSort]);

  // SERVER-OWNED STATE ONLY. `campaign` doubles as the edit buffer for this page,
  // so a background poll that called load() would silently discard whatever the
  // user is typing — a worse bug than the stale status it set out to fix.
  //
  // This merges just the fields the SERVER owns: the health monitor's auto-pause
  // (status / pausedAt / the reason marker on description) and the dialer's
  // progress counters. Every editable field is left exactly as the user left it.
  const revalidateStatus = async () => {
    const r = await campaignsAPI.get(id);
    const fresh = r?.data || r;
    if (!fresh || typeof fresh !== 'object') return;
    // Computed BEFORE setCampaign: a state updater must be pure, and React may
    // invoke it more than once (StrictMode, concurrent rendering). Setting other
    // state from inside one is a render-phase update.
    const userEditedDescription =
      (campaign?.description ?? null) !== (descriptionSaved ?? null);
    if (!userEditedDescription) setDescriptionSaved(fresh.description ?? null);

    setCampaign((c) => {
      if (!c) return c;
      return {
        ...c,
        status: fresh.status,
        pausedAt: fresh.pausedAt,
        startedAt: fresh.startedAt,
        completedAt: fresh.completedAt,
        totalContacts: fresh.totalContacts,
        contactsAttempted: fresh.contactsAttempted,
        contactsCompleted: fresh.contactsCompleted,
        ...(userEditedDescription ? {} : { description: fresh.description })
      };
    });
  };

  // The health monitor can pause a campaign at any moment with no user action, so
  // this page cannot rely on a reload to learn about it.
  // Disabled while a save is in flight. Otherwise a tick that started BEFORE the
  // user hit Start can resolve after it and merge the stale `status: 'paused'`
  // back — bouncing the row and re-showing the auto-pause banner on a campaign
  // they just resumed, on the very screen this change exists to make trustworthy.
  useRevalidate(revalidateStatus, { intervalMs: 30_000, enabled: !saving });

  // ── Tenant-wide (Settings) library ──────────────────────────────────────────
  // Fetch the shared, tenant-wide knowledge + FAQs (campaignId IS NULL) so the
  // owner can opt individual rows into THIS campaign via checkboxes. Independent
  // of `load` (the campaign payload) — only needs to run once per tenant context.
  const loadLibrary = async () => {
    setLibError('');
    try {
      const [k, f] = await Promise.all([
        knowledgeAPI.list().catch(() => ({ data: [] })),
        faqsAPI.list().catch(() => ({ data: [] }))
      ]);
      setLibKnowledge(k?.data || []);
      setLibFaqs(f?.data || []);
    } catch (err) {
      setLibError(err.message || 'Failed to load the tenant-wide library.');
    }
  };
  useEffect(() => { loadLibrary(); }, []);

  // ── Call review ─────────────────────────────────────────────────────────────
  // Fetch THIS campaign's calls (with full transcript + follow-up outcome). The
  // backend filters Call rows on conversationContext.campaignId == this id. Read
  // is independent of `load` so a reviewer can refresh just the calls.
  const loadCalls = async () => {
    setCallsError('');
    try {
      const r = await campaignsAPI.calls(id, { limit: 2000 });
      setCalls(r?.data || []);
    } catch (err) {
      setCallsError(err.message || 'Failed to load calls.');
    }
  };
  useEffect(() => { loadCalls(); }, [id]);

  // Toggle a tenant-wide row in/out of this campaign and persist the new id set.
  // Optimistic: flip the Set locally, PUT the campaign, and on failure roll back.
  // `kind` is 'knowledge' | 'faq'.
  const toggleIncluded = async (kind, rowId) => {
    const isKnowledge = kind === 'knowledge';
    const cur = isKnowledge ? includedKnowledge : includedFaqs;
    const setCur = isKnowledge ? setIncludedKnowledge : setIncludedFaqs;
    const next = new Set(cur);
    if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
    setCur(next);
    const payload = isKnowledge
      ? { includedKnowledgeIds: [...next] }
      : { includedFaqIds: [...next] };
    try {
      const r = await campaignsAPI.update(id, payload);
      const camp = r.data || r;
      // Trust the server's sanitized arrays (it drops foreign / non-library ids).
      setIncludedKnowledge(new Set(camp.includedKnowledgeIds || []));
      setIncludedFaqs(new Set(camp.includedFaqIds || []));
    } catch (err) {
      setCur(cur); // roll back
      setLibError(err.message || 'Failed to update included library.');
    }
  };

  // Select-all / deselect-all for a whole library section in one shot, so the
  // owner doesn't click ~30 boxes individually. `selectAll` true => include every
  // library row's id; false => clear the set. Optimistic with rollback like above.
  const toggleAllIncluded = async (kind, selectAll) => {
    const isKnowledge = kind === 'knowledge';
    const cur = isKnowledge ? includedKnowledge : includedFaqs;
    const setCur = isKnowledge ? setIncludedKnowledge : setIncludedFaqs;
    const lib = isKnowledge ? libKnowledge : libFaqs;
    const next = selectAll ? new Set(lib.map((r) => r.id)) : new Set();
    setCur(next);
    const payload = isKnowledge
      ? { includedKnowledgeIds: [...next] }
      : { includedFaqIds: [...next] };
    try {
      const r = await campaignsAPI.update(id, payload);
      const camp = r.data || r;
      setIncludedKnowledge(new Set(camp.includedKnowledgeIds || []));
      setIncludedFaqs(new Set(camp.includedFaqIds || []));
    } catch (err) {
      setCur(cur); // roll back
      setLibError(err.message || 'Failed to update included library.');
    }
  };

  // ── Call-schedule editing ───────────────────────────────────────────────────
  // Inline per-row edit of either timeslot. The <input type="datetime-local">
  // gives a local wall-clock string ("2026-06-24T14:30"); we convert to an ISO
  // string (with offset) for the API and back for display. Empty clears the slot.
  //
  // We keep an in-flight DRAFT of each box (keyed `${contactId}:${slot}`) so the
  // cell reflects exactly what's typed — INCLUDING an empty string. The previous
  // version drove the box straight off `contact.scheduledAt` and only saved in
  // onChange; when a box was emptied, the cleared state could be lost (a transient
  // invalid datetime-local value aborted the save, or a browser that doesn't fire
  // onChange on clear left the old time in place) and the next `load()` "refreshed"
  // the old time back in. Persisting the draft on BLUR — empty → explicit null —
  // makes a cleared box stay cleared.
  const [slotDrafts, setSlotDrafts] = useState({});
  const slotKey = (contactId, slot) => `${contactId}:${slot}`;

  // Update only the local draft as the user types/clears — no network yet.
  const onSlotInput = (contact, slot, localValue) => {
    setSlotDrafts((prev) => ({ ...prev, [slotKey(contact.id, slot)]: localValue }));
  };

  // Persist the draft on blur. Empty string → null (clears + re-arms the slot).
  const commitSlot = async (contact, slot) => {
    const k = slotKey(contact.id, slot);
    const localValue = slotDrafts[k];
    // No draft for this box → user never touched it; nothing to persist.
    if (localValue === undefined) return;
    const key = slot === 2 ? 'scheduledAt2' : 'scheduledAt';
    // Read the typed wall clock in the TENANT's zone, not the browser's — this is
    // the inverse of isoToTenantInput above.
    // Guard the parse: a partially-typed datetime-local is truthy but invalid.
    // zonedInputToIso returns null on anything unparseable, so distinguish "box
    // cleared" (save null) from "still being typed" (keep the draft, save nothing).
    let iso;
    if (!localValue) {
      iso = null;
    } else {
      iso = zonedInputToIso(localValue);
      if (!iso) return; // keep the draft, don't save junk
    }
    // No-op if it already matches what's stored (avoids a pointless PUT + refetch).
    const current = slot === 2 ? contact.scheduledAt2 : contact.scheduledAt;
    if ((current || null) === iso) {
      setSlotDrafts((prev) => { const n = { ...prev }; delete n[k]; return n; });
      return;
    }
    // Optimistic local update so the cell doesn't flicker on slow networks.
    // Also clear this slot's fired flag locally — the backend re-arms it on any
    // scheduledAt write, and slotValue() blanks a fired slot, so without this the
    // freshly-entered time would vanish to blank when rescheduling a contact that
    // already placed its call.
    const firedKey = slot === 2 ? 'slot2Fired' : 'slot1Fired';
    setContacts((prev) => prev.map((c) => (c.id === contact.id ? { ...c, [key]: iso, [firedKey]: false } : c)));
    // Drop the draft so the box now renders from the (updated) contact value.
    setSlotDrafts((prev) => { const n = { ...prev }; delete n[k]; return n; });
    try {
      await campaignsAPI.updateContact(id, contact.id, { [key]: iso });
    } catch (err) {
      setError(err.message || 'Failed to set schedule.');
      load();
    }
  };

  // ── Sales-stage editing ─────────────────────────────────────────────────────
  // Inline per-contact stage dropdown. Optimistic: set the value locally, PATCH
  // the contact, and roll back to the previous value on failure. Empty string in
  // the <select> means "no stage" → send null to clear the column.
  // The classifier labels every call from the transcript and gets some wrong; before
  // this there was no way to correct one. Probe-gated: `outcome` is not yet in
  // contactPatchSchema, so the control renders disabled and says why rather than
  // saving into nothing (routed to Charlie).
  const outcomeEditable = false;
  const setContactOutcome = async (contact, value) => {
    const prev = contact.outcome ?? null;
    const next = value || null;
    setContacts((cs) => cs.map((c) => (c.id === contact.id ? { ...c, outcome: next } : c)));
    try {
      await campaignsAPI.updateContact(id, contact.id, { outcome: next });
    } catch (err) {
      setContacts((cs) => cs.map((c) => (c.id === contact.id ? { ...c, outcome: prev } : c)));
      setError(err.body?.message || err.message || 'Could not change the response.');
    }
  };

  // ⛔ SAY THAT IT SAVED. Brian, 2026-08-20: "I have to make the change and then refresh
  // in order for me to see the change take effect... otherwise it comes across like it's
  // a bug." The dropdown was already optimistic, so the VALUE changed instantly — what
  // was missing was any evidence it reached the server, so the only way to trust it was
  // to reload and look again. An optimistic update with no confirmation is
  // indistinguishable from one that silently failed.
  const [stageSavedId, setStageSavedId] = useState(null);

  const setSalesStage = async (contact, value) => {
    const next = value || null;
    const prev = contact.salesStage ?? null;
    if (next === prev) return;
    setContacts((cs) => cs.map((c) => (c.id === contact.id ? { ...c, salesStage: next } : c)));
    try {
      await campaignsAPI.updateContact(id, contact.id, { salesStage: next });
      setStageSavedId(contact.id);
      // Clear on a timer, and only if this contact is still the one showing "Saved" —
      // otherwise a slower earlier save wipes a newer one's confirmation.
      setTimeout(() => setStageSavedId((cur) => (cur === contact.id ? null : cur)), 2500);
    } catch (err) {
      setContacts((cs) => cs.map((c) => (c.id === contact.id ? { ...c, salesStage: prev } : c)));
      setError(err.message || 'Failed to set sales stage.');
    }
  };

  // ── Schedule a callback ──────────────────────────────────────────────────────
  // Re-dial a reached-but-not-closed contact TOMORROW, at a different time of day
  // than the missed call (a morning miss → an afternoon try, and vice-versa —
  // catch them when they might actually pick up). Setting status→queued plus a
  // future scheduledAt re-arms slot1Fired server-side, so the dialer picks it up
  // at the callback time. The exact time stays adjustable in the Call cell after.
  const scheduleCallback = async (contact) => {
    // Never resurrect an opted-out contact (the server 409s this too — this is the
    // friendly front of the same DNC backstop).
    if (['do_not_call', 'suppressed'].includes(contact.status)) {
      setScheduleStatus(`${contact.name || contact.phoneE164} is on the do-not-call list — not scheduling a callback.`);
      return;
    }
    if (!canSchedule) {
      setScheduleStatus('Set your timezone in Settings first — a callback time needs a zone to mean anything.');
      return;
    }
    const tomorrow = isoToZonedInput(new Date(Date.now() + 86_400_000).toISOString()).slice(0, 10);
    let hour = 10; // default mid-morning when we don't know when the miss was
    if (contact.lastCalledAt) {
      const lastHour = Number(isoToZonedInput(contact.lastCalledAt).slice(11, 13));
      if (Number.isFinite(lastHour)) hour = lastHour < 12 ? 14 : 10;
    }
    const iso = zonedInputToIso(`${tomorrow}T${String(hour).padStart(2, '0')}:00`);
    if (!iso) { setScheduleStatus('Could not compute a callback time.'); return; }
    const prev = { status: contact.status, scheduledAt: contact.scheduledAt, slot1Fired: contact.slot1Fired };
    setContacts((cs) =>
      cs.map((c) => (c.id === contact.id ? { ...c, status: 'queued', scheduledAt: iso, slot1Fired: false } : c))
    );
    try {
      await campaignsAPI.updateContact(id, contact.id, { status: 'queued', scheduledAt: iso });
      setScheduleStatus(`Callback queued for ${contact.name || contact.phoneE164} — ${formatDateTimeWithZone(iso)}.`);
    } catch (err) {
      setContacts((cs) => cs.map((c) => (c.id === contact.id ? { ...c, ...prev } : c)));
      setError(err.message || 'Failed to schedule the callback.');
    }
  };

  // ── Hot-lead star toggle ─────────────────────────────────────────────────────
  // The ⭐ star on each row toggles a contact's HOT status (which pins it to the
  // top via buildContactRows). "Hot" is currently a salesStage value — see
  // HOT_LEAD_STAGES / isHotLead — so toggling ON sets salesStage to
  // 'decision_maker_interested' and toggling OFF clears it. Optimistic through the
  // same per-contact PATCH path as the sales-stage/schedule edits: flip local
  // `contacts` first (so buildContactRows re-sorts and re-pins the row instantly,
  // even in preview where the write is a no-op), then persist; roll back on error.
  const toggleHotLead = async (contact) => {
    const prev = contact.salesStage ?? null;
    const next = isHotLead(contact) ? null : 'decision_maker_interested';
    setContacts((cs) => cs.map((c) => (c.id === contact.id ? { ...c, salesStage: next } : c)));
    try {
      await campaignsAPI.updateContact(id, contact.id, { salesStage: next });
    } catch (err) {
      setContacts((cs) => cs.map((c) => (c.id === contact.id ? { ...c, salesStage: prev } : c)));
      setError(err.message || 'Failed to update hot-lead status.');
    }
  };

  // ── Expand a contact row to reveal its call transcript(s) ───────────────────
  // The merged Contacts section shows each contact's calls inline. Toggle which
  // contact is expanded (only one at a time keeps the table compact).
  const [expandedContactId, setExpandedContactId] = useState(null);

  // ── HEAR THIS CAMPAIGN BEFORE IT DIALS ANYONE ───────────────────────────────
  // Brian: "the feature that allows me to test a call before I send it out, or before
  // it's scheduled to go out."
  //
  // POST /campaigns/:id/test-call has existed and worked the whole time — it dials a
  // number you name through the EXACT campaign config (roster, voice, TTS override, the
  // opening line, systemPromptOverride and a real contact's dossier) using
  // `buildCampaignPlaceArgs`, the same builder the dialer uses. There was simply no
  // button. Nothing in core has to change for this one.
  //
  // ⚠️ Distinct from the tenant "Test call" on Settings, which uses the DEFAULT panel and
  // is therefore NOT this campaign — that difference is the reason a rehearsal can sound
  // right and the real thing not.
  const [testTo, setTestTo] = useState(() => {
    try { return localStorage.getItem('alfred.testCallTo') || ''; } catch { return ''; }
  });
  const [testOpen, setTestOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState(null); // { ok, text }

  const runCampaignTest = async () => {
    // Same normalisation as the callback rehearsal — see lib/format.js toE164().
    const to = toE164(testTo);
    if (!to) {
      setTestMsg({ ok: false, text: `“${testTo.trim()}” isn’t a number I can dial. A US number needs 10 digits, or start with + and the country code.` });
      return;
    }
    try { localStorage.setItem('alfred.testCallTo', to); } catch { /* private mode */ }
    setTestTo(to);
    setTesting(true); setTestMsg(null);
    try {
      await campaignsAPI.testCall(id, { toE164: to });
      setTestMsg({ ok: true, text: `Calling ${to} now. This is this campaign's real opener, voice and briefing — what a prospect would get.` });
    } catch (err) {
      setTestMsg({ ok: false, text: err?.body?.message || err.message || 'Could not place the test call.' });
    } finally {
      setTesting(false);
    }
  };
  // A `?contact=<id>` deep link is only half-usable until the row is actually on
  // screen: the list may be filtered, sorted or paginated past it. Once contacts load,
  // narrow to that contact by its OWN company/phone so the row is guaranteed visible,
  // whatever the incoming text said.
  const deepLinkContactId = searchParams.get('contact');
  useEffect(() => {
    if (!deepLinkContactId || contacts.length === 0) return;
    const c = contacts.find((x) => x.id === deepLinkContactId);
    if (!c) return;
    setExpandedContactId(c.id);
    // The phone still goes in the search box so the state is visible and editable —
    // but the narrowing no longer DEPENDS on it (see focusContact).
    // ⛔ NARROW BY PHONE, NOT BY NAME. Brian, 2026-08-14: "the best way to match the
    // business is not from the name but use the business phone number."
    //
    // A name is a description and it drifts — punctuation, a rename, "(formerly …)",
    // a trailing "at 281", a chain that shares a word with fifty other rows. The number
    // is the identifier: stable, effectively unique, and normalized to E.164 on the way
    // in. The search already matches on digits, so this needs nothing new.
    //
    // Useful side effect: a DUPLICATE contact row carrying the same number now shows up
    // beside the real one instead of hiding behind it — which is how the duplicates got
    // missed the first time.
    setContactQuery(c.phoneE164 || c.company || c.name || '');
  }, [deepLinkContactId, contacts]);
  // Client-side pagination of the contact list (long campaigns are 300+ rows).
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);
  // Jump back to page 1 whenever the view changes (filter/sort/selection).
  // ⚠️ contactQuery belongs in here. Without it, searching while on page 3 left you on
  // page 3 — so a single match that lands on page 1 rendered as an empty list, which
  // reads exactly like "the business isn't here".
  useEffect(() => { setPage(0); }, [contactSort, filters, appliedSelection, metroFilter, hazmatOnly, cargoFilters, contactQuery]);

  // ── Bulk "set call time" for the currently-visible rows ─────────────────────
  // After sorting by Call response (answered first), the owner can stamp the
  // top group's 1st-call slot to one time in a single move. We prompt for a
  // wall-clock time, then PATCH scheduledAt on every queued contact shown.
  const [bulkTimeStatus, setBulkTimeStatus] = useState('');
  const bulkSetCallTime = async () => {
    if (!canSchedule) {
      setBulkTimeStatus('Set your timezone in Settings first — a call time needs a zone to mean anything.');
      return;
    }
    // Sensible default: today at 9am on the TENANT's clock. Deriving it from the
    // browser's calendar day would pick the wrong date near midnight for a tenant
    // in another zone, so take today's date as the tenant reads it.
    const today = isoToZonedInput(new Date().toISOString()).slice(0, 10);
    const suggested = `${today}T09:00`;
    const zone = zoneAbbreviation();
    const input = window.prompt(
      `Set the 1st-call time for all ${contacts.length} visible contact(s) to (${zone}, YYYY-MM-DDTHH:mm):`,
      suggested
    );
    if (input == null) return; // cancelled
    const trimmed = input.trim();
    if (!trimmed) { setBulkTimeStatus('No time entered.'); return; }
    const iso = zonedInputToIso(trimmed);
    if (!iso) { setBulkTimeStatus(`Couldn’t parse "${trimmed}" — use YYYY-MM-DDTHH:mm.`); return; }
    const targets = contacts.filter((c) => c.status === 'queued');
    if (targets.length === 0) { setBulkTimeStatus('No queued contacts in view to schedule.'); return; }
    setBulkTimeStatus(`Setting 1st-call time on ${targets.length} contact(s)…`);
    // Optimistic local update so the column fills immediately.
    setContacts((cs) => cs.map((c) => (c.status === 'queued' ? { ...c, scheduledAt: iso } : c)));
    let ok = 0;
    for (const c of targets) {
      try { await campaignsAPI.updateContact(id, c.id, { scheduledAt: iso }); ok++; }
      catch { /* keep going; the final reload reconciles */ }
    }
    setBulkTimeStatus(`Set 1st-call time on ${ok}/${targets.length} contact(s).`);
    load();
  };

  // The value a slot box should render: the live draft if one exists, else the
  // stored (UTC) ISO on the tenant's wall clock.
  const slotValue = (contact, slot) => {
    const k = slotKey(contact.id, slot);
    if (k in slotDrafts) return slotDrafts[k];
    // The Call column is scheduling-only: once a slot has PLACED its call it goes
    // blank (the call itself lives in the History view, not this column).
    // Re-scheduling a new time re-arms the fired flag (backend on any scheduledAt
    // write; commitSlot mirrors it optimistically), so a fresh pending time shows.
    const fired = slot === 2 ? contact.slot2Fired : contact.slot1Fired;
    if (fired) return '';
    return isoToTenantInput(slot === 2 ? contact.scheduledAt2 : contact.scheduledAt);
  };

  // ── PER-CONTACT FALLBACK — what makes Add and Clear actually work ───────────
  //
  // Every BULK scheduling path in core requires `status: 'queued'` (dueContacts,
  // the unfiltered fill, the clear, and — since the un-suppress hardening — the
  // scoped fill too). Bulk `restore` only un-suppresses, never un-completes. So on
  // a campaign whose contacts have all been called, both buttons legitimately
  // matched 0 rows and did nothing, which reads as a dead control.
  //
  // The PER-CONTACT route is not bound by that: `contactPatchSchema` accepts
  // `status: 'queued'`, and the DNC backstop (campaigns.js:506) refuses only when
  // the row is ALREADY `do_not_call` or `suppressed`. So re-queueing a *completed*
  // contact is permitted, and clearing a time is permitted unconditionally.
  //
  // ⛔ THE SAFETY PROPERTY IS PRESERVED, TWICE. We filter do_not_call/suppressed
  // out here, and the server refuses them anyway. Nothing this does can resurrect
  // an opted-out contact — that was the actual hazard behind the guard, and it is
  // untouched.
  // ⛔ "NOT QUEUED" IS NOT "ALREADY CALLED". The confirm used to derive the second
  // from the first — `eligible.length - (status === 'queued').length` — and told
  // Brian that 93 carriers "have already been called" when every one of them had
  // `attempts = 0`, no outcome and no lastCalledAt. They were simply in a status the
  // bulk path won't schedule. He cancelled a 104-contact run on the strength of it,
  // which is exactly right: if the dialog says a business was already called and it
  // wasn't, the only safe move is to stop.
  //
  // Count a call by EVIDENCE OF A CALL, never by status.
  const hasBeenCalled = (c) =>
    (Number(c.attempts) || 0) > 0
    || !!c.lastCalledAt
    // A call row for this contact IS evidence of a call, even when the contact row
    // never recorded it — which is exactly what the callback path leaves behind.
    || callsForContact(c).length > 0
    || (!!c.outcome && resolveOutcome(c.outcome) !== 'not_called');

  const schedulableFallback = (rows) => rows.filter((c) => {
    if (c.status === 'suppressed' || c.status === 'do_not_call') return false;
    return resolveOutcome(c.outcome) !== 'do_not_call';
  });

  /** Re-queue + time each row individually, spaced like the bulk fill would. */
  const fillIndividually = async (rows, startIso, intervalMin, column) => {
    const key = column === 2 ? 'scheduledAt2' : 'scheduledAt';
    const startMs = new Date(startIso).getTime();
    let ok = 0;
    let failed = 0;
    for (let i = 0; i < rows.length; i++) {
      const when = new Date(startMs + i * intervalMin * 60_000).toISOString();
      try {
        // status:'queued' is the half the bulk path can no longer do — without it
        // the time is written and the dialer still skips the row.
        await campaignsAPI.updateContact(id, rows[i].id, { status: 'queued', [key]: when });
        ok += 1;
      } catch { failed += 1; }
    }
    return { ok, failed };
  };

  /** Clear a column on rows the bulk clear won't touch. */
  const clearIndividually = async (rows, column) => {
    const key = column === 2 ? 'scheduledAt2' : 'scheduledAt';
    let ok = 0;
    for (const c of rows) {
      try { await campaignsAPI.updateContact(id, c.id, { [key]: null }); ok += 1; }
      catch { /* server refuses DNC rows; the reload reconciles */ }
    }
    return ok;
  };

  // Null out an entire call-time column across every queued contact in one click,
  // so the owner can wipe a column and re-fill it cleanly with the scheduler.
  const clearColumn = async (column) => {
    const label = column === 2 ? '2nd' : '1st';
    if (!(await confirm({
      title: `Clear the ${label}-call time on every queued contact?`,
      body: 'This can’t be undone, but you can re-fill the column with Add.',
      confirmLabel: 'Clear column'
    }))) return;
    setScheduleStatus(`Clearing the ${label}-call column…`);
    try {
      const r = await campaignsAPI.autoSchedule(id, { column, clear: true });
      let cleared = r.data.cleared;
      if (cleared === 0) {
        // Nothing was queued — clear the times that are actually on screen instead.
        const stuck = schedulableFallback(orderedVisibleContacts)
          .filter((c) => (column === 2 ? c.scheduledAt2 : c.scheduledAt));
        if (stuck.length) {
          setScheduleStatus(`Clearing ${stuck.length} already-called contact${stuck.length === 1 ? '' : 's'}…`);
          cleared = await clearIndividually(stuck, column);
        }
      }
      setScheduleStatus(`Cleared the ${label}-call column for ${cleared} contact${cleared === 1 ? '' : 's'}.`
        + (cleared === 0 ? explainZeroScheduled(contacts) : ''));
      // Wipe any in-flight drafts for that column so they don't re-show old times.
      setSlotDrafts((prev) => {
        const n = {};
        for (const key of Object.keys(prev)) if (!key.endsWith(`:${column}`)) n[key] = prev[key];
        return n;
      });
      load();
    } catch (err) {
      setScheduleStatus(err.message || 'Clear failed.');
    }
  };

  // Fill ONE column independently (column 1 = 1st call, 2 = 2nd call) so the
  // 1st and 2nd calls can be laid out on different days, not minutes apart.
  //
  // SCOPE: "Add" fills only the rows currently IN VIEW — it honors the Call-entries
  // selection AND the active filter chips, so a narrowed list ("1-100", or a filter)
  // schedules exactly those rows, never the whole pool. The whole-campaign fill runs
  // ONLY when nothing narrows the view, and then we confirm the count first (this was
  // the reported bug: a "1-100" selection still stamped all queued contacts).
  const runAutoSchedule = async (column) => {
    if (!scheduleDraft.start) { setScheduleStatus('Pick a start time first.'); return; }
    const intervalMinutes = Number(scheduleDraft.interval);
    if (!Number.isFinite(intervalMinutes) || intervalMinutes < 0) {
      setScheduleStatus('Minutes-between-calls must be a number ≥ 0.'); return;
    }
    const label = column === 2 ? '2nd' : '1st';
    // The picker's wall clock is the tenant's, so read it back in the tenant's zone.
    const startIso = zonedInputToIso(scheduleDraft.start);
    if (!startIso) { setScheduleStatus('That start time isn’t valid.'); return; }

    // Scoped fill — a Call-entries selection or an active filter narrows WHICH rows
    // get times. Route through the per-selection endpoint so nothing else is touched.
    // ⛔ REMOVALS MUST NOT APPEAR IN THIS CONDITION. Removed rows are already out of
    // `orderedVisibleContacts`, so they need no special case — but adding
    // `removedCount > 0` here was actively dangerous: `removedCount` also counts rows
    // the DIALER suppressed (its DNC scrub sets status='suppressed'), so any campaign
    // that had ever scrubbed one would silently route the UNFILTERED "Fill" through
    // /schedule-selection instead of the whole-campaign path. That endpoint writes
    // `status:'queued'` unconditionally, so it re-queues completed and do-not-call
    // rows that the whole-campaign path (which scopes to status='queued') correctly
    // skips. One stale suppressed row would have re-armed a finished campaign.
    const scoped = selectionViewActive || anyFilterActive;
    if (scoped) {
      // Precedence: typed entry numbers > everything passing the filter.
      const scopedContacts = selectionViewActive
        ? resolveSelection().map((p) => p.contact)
        : orderedVisibleContacts;
      // ⚠️ THIS COMMENT USED TO SAY the per-selection endpoint re-queues a completed
      // row. It no longer does. `scheduleSelection` now guards `status: 'queued'`
      // (campaignService.js:709) — a hardening I asked for after it was silently
      // un-suppressing DNC-scrubbed rows, and which landed broader than the danger:
      // it also blocks the legitimate re-call of a `completed` contact. So a filter
      // or selection over already-called rows fills ZERO, and the only honest thing
      // this screen can do is say so (see explainZeroScheduled).
      // Routed to Charlie: the guard wants to be `notIn ['suppressed','do_not_call']`,
      // which keeps the safety property and restores re-calls.
      //
      // The client-side do-not-call filter below stays as the outer belt.
      const fillable = scopedContacts.filter((c) => resolveOutcome(c.outcome) !== 'do_not_call');
      const dncSkipped = scopedContacts.length - fillable.length;

      // ⛔ THE TIME-DRIFT BUG. `scheduleSelection` advances its slot counter for every
      // target it CONSIDERS but only writes the ones passing `status:'queued'`
      // (campaignService.js:696-711). So sending a filtered view that also contains
      // completed rows spreads the survivors across the skipped rows' slots.
      //
      // Measured on Carriers — Houston, 2026-08-04: start 1:30pm, interval 1 min,
      // 21 queued rows inside a ~113-row filtered view. They landed at +40, +42, +44,
      // +47, +49 … +112 minutes — first call 40 minutes late and the run stretched
      // over 72 minutes instead of 21. Each survivor got the slot of its position
      // among the 113, not among the 21.
      //
      // So: only send rows the server will actually write. Anything else in view that
      // is legitimately re-callable goes through the per-contact route, which re-queues
      // AND schedules — and we keep ONE contiguous time sequence across both, in the
      // order the rows appear on screen.
      const nonDialable = (c) => c.status === 'suppressed' || c.status === 'do_not_call';
      const eligible = fillable.filter((c) => !nonDialable(c));
      const allQueued = eligible.every((c) => c.status === 'queued');
      const baseNums = allQueued
        ? eligible.map((c) => baseEntryNumById.get(c.id)).filter(Boolean)
        : [];
      if (baseNums.length === 0) {
        if (eligible.length === 0) {
          setScheduleStatus(dncSkipped > 0
            ? `Nothing scheduled — all ${dncSkipped} row(s) in view are do-not-call.`
            : 'No matching rows in view to schedule.');
          return;
        }
        // Mixed queued + already-called rows. The bulk endpoint would silently skip
        // the already-called ones AND drift the times of the rest, so schedule the
        // whole visible set per contact: contiguous times, visible order, and the
        // already-called rows get re-queued on the way.
        const already = eligible.filter((c) => resolveOutcome(c.outcome) === 'answered').length;
        if (!(await confirm({
          title: `Schedule ${eligible.length} contact${eligible.length === 1 ? '' : 's'}?`,
          body: (() => {
            const called = eligible.filter(hasBeenCalled).length;
            const requeue = eligible.filter((c) => c.status !== 'queued' && !hasBeenCalled(c)).length;
            const parts = [];
            if (called) parts.push(`${called} ${called === 1 ? 'has' : 'have'} already been called and will be put back in the queue`
              + `${already ? ` (${already} reached a person)` : ''}`);
            if (requeue) parts.push(`${requeue} ${requeue === 1 ? 'is' : 'are'} not currently queued — never called, just not dialable — and will be queued`);
            return `From ${scheduleDraft.start}, ${intervalMinutes} minute${intervalMinutes === 1 ? '' : 's'} apart.`
              + (parts.length ? `\n\n${parts.join('.\n')}.` : '');
          })(),
          confirmLabel: 'Schedule'
        }))) { setScheduleStatus('Cancelled — nothing scheduled.'); return; }
        setScheduleStatus(`Scheduling ${eligible.length} contact${eligible.length === 1 ? '' : 's'}…`);
        const out = await fillIndividually(eligible, startIso, intervalMinutes, column);
        setScheduleStatus(`Filled the ${label}-call column for ${out.ok} contact${out.ok === 1 ? '' : 's'}`
          + `, ${intervalMinutes} minute${intervalMinutes === 1 ? '' : 's'} apart from ${scheduleDraft.start}.`
          + (out.failed ? ` ${out.failed} refused by the server (do-not-call).` : ''));
        load();
        return;
      }
      // Only guard against ACCIDENTALLY re-dialing contacts you actually reached — a
      // filter that includes answered rows gets one confirm; re-calling no-answer /
      // voicemail (nobody reached) just goes through.
      const answered = fillable.filter((c) => resolveOutcome(c.outcome) === 'answered').length;
      if (answered > 0 && !(await confirm({
        title: 'Call contacts you already reached?',
        body: `${fillable.length} contact(s) will be scheduled — ${answered} of them already reached a person.`,
        confirmLabel: 'Schedule anyway'
      }))) { setScheduleStatus('Cancelled — nothing scheduled.'); return; }
      const scopeWord = selectionViewActive ? 'selected' : 'filtered';
      setScheduleStatus(`Scheduling ${label} calls for ${baseNums.length} ${scopeWord} contact(s)…`);
      try {
        const r = await campaignsAPI.scheduleSelection(id, baseNums.join(','), { start: startIso, intervalMinutes, column });
        const out = r?.data || r;
        const n = out.scheduled ?? baseNums.length;
        setScheduleStatus(
          `Filled the ${label}-call column for ${n} ${scopeWord} contact${n === 1 ? '' : 's'}` +
          (n === 0 ? explainZeroScheduled(fillable) : '') +
          (dncSkipped > 0 ? ` — skipped ${dncSkipped} do-not-call.` : '.')
        );
        load();
      } catch (err) {
        setScheduleStatus(err.message || 'Auto-schedule failed.');
      }
      return;
    }

    // ⛔ NO CONFIRM ON THE DEFAULT CASE. This used to stop and ask whenever no filter
    // or selection was active, on the theory that a whole-pool stamp should be
    // deliberate. That reads the model backwards (Brian, 2026-08-03): the campaign's
    // full list IS the target — scheduling it as loaded is the ordinary thing to do,
    // and filtering is the OPTIONAL narrowing on top. Gating the common action behind
    // a dialog made the normal path feel like an error, and on a one-contact campaign
    // it was pure ceremony.
    //
    // It is also cheap to undo and easy to see: Clear wipes the column, and every time
    // written shows in the Call column immediately. The guard that actually matters —
    // never re-arming a suppressed or do-not-call contact — lives in core and in
    // schedulableFallback, not in a dialog.
    setScheduleStatus(`Scheduling ${label} calls…`);
    try {
      const r = await campaignsAPI.autoSchedule(id, { start: startIso, intervalMinutes, column });
      let filled = r.data.scheduled;
      if (filled === 0) {
        const eligible = schedulableFallback(orderedVisibleContacts);
        // Re-calling someone who was already reached is a deliberate act — confirm it,
        // and say plainly that these rows are being put back in the queue.
        if (eligible.length && await confirm({
          title: 'Put these contacts back in the queue?',
          body: `Nothing is queued on this campaign, so nothing was scheduled.\n\n`
            + `This puts ${eligible.length} contact${eligible.length === 1 ? '' : 's'} back in the queue and schedules `
            + `${eligible.length === 1 ? 'it' : 'them'} from ${scheduleDraft.start}. `
            + (() => {
              const reached = eligible.filter((c) => resolveOutcome(c.outcome) === 'answered').length;
              return reached ? `${reached} of them have already been reached.` : 'None of them have been called yet.';
            })(),
          confirmLabel: 'Re-queue and schedule'
        })) {
          setScheduleStatus(`Re-queueing ${eligible.length} contact${eligible.length === 1 ? '' : 's'}…`);
          const out = await fillIndividually(eligible, startIso, intervalMinutes, column);
          filled = out.ok;
          if (out.failed) setScheduleStatus(`${out.failed} refused by the server (do-not-call).`);
        }
      }
      setScheduleStatus(`Filled the ${label}-call column for ${filled} contact${filled === 1 ? '' : 's'}.`
        + (filled === 0 ? explainZeroScheduled(contacts) : ''));
      load();
    } catch (err) {
      setScheduleStatus(err.message || 'Auto-schedule failed.');
    }
  };

  const setField = (k, v) => setCampaign((c) => ({ ...c, [k]: v }));

  // CAPABILITY PROBE, not a feature flag. Prisma returns every scalar column —
  // including nulls — so the key being present means core has the column and the
  // route accepts it. Absent means Charlie's migration hasn't shipped, and the
  // editor stays hidden rather than offering a box whose saves go nowhere.
  // (This is the Cal.com lesson: a dashboard control shipped ahead of its core
  // route called 404s in production for days.)
  const behaviorSupported = !!campaign && campaign.behaviorBlock !== undefined;
  const behaviorValidationSupported =
    !!campaign && campaign.behaviorBlockValidatedAt !== undefined;
  const behaviorDirty =
    behaviorSupported && (campaign.behaviorBlock || '') !== (behaviorSaved || '');

  // Calling hours (autonomous-mode dial window). Always edit against a fully
  // normalized object so partial/empty stored values don't crash the grid.
  const callingHours = normalizeCallingHours(campaign?.callingHours, tenantTimeZone);
  const setCallingTz = (tz) => setField('callingHours', { ...callingHours, timezone: tz });
  const setCallingDay = (key, patch) =>
    setField('callingHours', {
      ...callingHours,
      days: { ...callingHours.days, [key]: { ...callingHours.days[key], ...patch } }
    });

  // Serialize a rate input for the API: empty string / null / NaN → null
  // (persists as NULL); a real number → that number.
  const rateOrNull = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  // Clear the three rate fields in the form (they persist as null on Save).
  const useSchedule = campaign?.useSchedule !== false; // default true
  const clearRateFields = () =>
    setCampaign((c) => ({ ...c, callsPerMinute: '', maxAttempts: '', retryAfterMin: '' }));

  // ── Campaign AIs (roster) editors ───────────────────────────────────────────
  // roster is an ordered list of { name, voiceId, voiceProvider }. The order-0
  // entry is the host; 2+ entries run as a panel. Mutations are local until Save.
  const roster = Array.isArray(campaign?.roster) ? campaign.roster : [];
  const setRoster = (next) => setCampaign((c) => ({ ...c, roster: next }));
  const addAi = () =>
    setRoster([...roster, { name: '', voiceId: '', voiceProvider: 'elevenlabs' }]);
  const removeAi = (idx) => setRoster(roster.filter((_, i) => i !== idx));
  const updateAi = (idx, patch) =>
    setRoster(roster.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const saveCampaign = async () => {
    if (!campaign) return;
    // Calling hours: a reversed window (start >= end) is accepted by the backend
    // but silently makes that day NEVER dial. Catch it here rather than let a
    // campaign quietly go dark on that day.
    const ch = normalizeCallingHours(campaign.callingHours, tenantTimeZone);
    const badDay = CALLING_DAYS.find(([k]) => {
      const d = ch.days[k];
      return d.enabled && d.start >= d.end;
    });
    if (badDay) {
      setError(`Calling hours for ${badDay[1]} end at or before they start — fix the window (or disable that day).`);
      return;
    }
    // Drop incomplete roster rows (no name or no voice) before saving so the
    // server doesn't reject the whole payload over a half-filled row.
    const cleanRoster = roster
      .map((r) => ({
        name: (r.name || '').trim(),
        voiceId: (r.voiceId || '').trim(),
        voiceProvider: r.voiceProvider || 'elevenlabs'
      }))
      .filter((r) => r.name && r.voiceId);
    setSaving(true);
    try {
      const payload = {
        name: campaign.name,
        fromNumberId: campaign.fromNumberId,
        // Rate fields persist as null when emptied (the Clear button). An empty
        // string / null / non-number sends null so the server stores NULL and
        // the dialer falls back to safe defaults.
        callsPerMinute: rateOrNull(campaign.callsPerMinute),
        maxAttempts: rateOrNull(campaign.maxAttempts),
        retryAfterMin: rateOrNull(campaign.retryAfterMin),
        useSchedule: campaign.useSchedule !== false,
        // Autonomous-mode dial window. Stored regardless of mode (harmless in
        // manual mode; lets the owner pre-configure). Always a full, valid object.
        callingHours: normalizeCallingHours(campaign.callingHours, tenantTimeZone),
        systemPromptOverride: campaign.systemPromptOverride,
        openingLine: campaign.openingLine,
        voiceId: campaign.voiceId || null,
        ttsVendorOverride: campaign.ttsVendorOverride || null,
        roster: cleanRoster
      };
      // Only when the user actually edited it. The health monitor records WHY it
      // auto-paused a campaign by prefixing a marker onto `description`; a tab
      // that loaded before the pause holds a description without that marker, and
      // echoing it back would ERASE the only record of why the platform stopped
      // the campaign. Same class as behaviorBlock below.
      if ((campaign.description ?? null) !== (descriptionSaved ?? null)) {
        payload.description = campaign.description;
      }

      // Sent ONLY when it actually changed. This page does a full-object save, so
      // echoing the field unconditionally means any unrelated edit (pacing, calling
      // hours, archive) writes back whatever this tab loaded — and a tab opened
      // before the block was seeded would send null and silently revert the
      // campaign to the code default on the next live call. Exactly the invisible
      // pitch change this whole migration exists to end.
      const sendBehavior = behaviorSupported && behaviorDirty;
      if (sendBehavior) payload.behaviorBlock = campaign.behaviorBlock || null;

      const r = await campaignsAPI.update(id, payload);
      const saved = r.data || r;
      setCampaign(saved);
      setBehaviorSaved(saved.behaviorBlock ?? null);
      setDescriptionSaved(saved.description ?? null);
      setSavedAt(new Date());

      // VERIFY THE ROUND TRIP. The column can land in prod before the write path
      // does (`railway up` ships the tree and auto-applies migrations, and neither
      // the zod schema nor updateCampaign's key allowlist mentions behaviorBlock
      // yet). In that window a save returns 200 and discards the field. A "Saved"
      // stamp over a discarded pitch change is worse than an error.
      if (sendBehavior && (saved.behaviorBlock ?? null) !== (payload.behaviorBlock ?? null)) {
        setError(
          'The rest of the campaign saved, but the server did not accept the behavior block — it is NOT saved. Core needs the write path for this field.'
        );
      }
    } catch (err) {
      setError(describeApiError(err, 'Save failed.'));
    } finally {
      setSaving(false);
    }
  };

  // Marking a version validated is DELIBERATE and separate from saving: it
  // asserts a human listened to a real call on this exact text. Core copies the
  // saved block into the validated slot and stamps it, so rollback is one click
  // instead of the timestamp archaeology it took last time.
  const validateBehavior = async () => {
    setValidatingBehavior(true);
    setError('');
    try {
      const r = await campaignsAPI.validateBehavior(id);
      const saved = r?.data || r;
      // Merge ONLY the behavior keys. `campaign` is the edit buffer for the whole
      // page, so spreading a full server row (or calling load()) would throw away
      // an in-progress 18,000-character pitch edit with no warning.
      if (saved && typeof saved === 'object') {
        setCampaign((c) => ({
          ...c,
          behaviorBlock: saved.behaviorBlock ?? c.behaviorBlock,
          behaviorBlockValidated: saved.behaviorBlockValidated ?? c.behaviorBlockValidated,
          behaviorBlockValidatedAt: saved.behaviorBlockValidatedAt ?? c.behaviorBlockValidatedAt
        }));
        setBehaviorSaved(saved.behaviorBlock ?? behaviorSaved);
      }
    } catch (err) {
      setError(err.message || 'Could not record this version as validated.');
    } finally {
      setValidatingBehavior(false);
    }
  };

  const setStatus = async (target) => {
    // `saving` also parks the background revalidation for the duration — a tick
    // that started before this PUT would otherwise resolve after it and merge the
    // OLD status back.
    setSaving(true);
    try {
      const r = await campaignsAPI.setStatus(id, target);
      const fresh = r?.data || r;
      // Merge server-owned fields only. This used to be `setCampaign(fresh)`,
      // which replaced the whole object — so clicking Pause silently discarded an
      // unsaved pitch, roster or behavior-block edit. `campaign` is this page's
      // edit buffer, not just a server snapshot.
      if (fresh && typeof fresh === 'object') {
        setDescriptionSaved(fresh.description ?? null);
        setCampaign((c) => (c ? {
          ...c,
          status: fresh.status,
          pausedAt: fresh.pausedAt,
          startedAt: fresh.startedAt,
          completedAt: fresh.completedAt,
          description: fresh.description
        } : fresh));
      }
    } catch (err) {
      setError(err.message || 'Status change failed.');
    } finally {
      setSaving(false);
    }
  };

  const resetCampaign = async () => {
    if (!(await confirm({
      title: 'Reset this campaign?',
      body: 'Every completed and failed contact goes back to queued so they can be dialed again. '
        + 'Contacts on the do-not-call list stay locked.',
      confirmLabel: 'Reset campaign',
      danger: true
    }))) return;
    try {
      const r = await campaignsAPI.reset(id);
      const requeued = r.data?.contactsRequeued ?? 0;
      setError('');
      load();
      alert(`Reset complete — ${requeued} contact${requeued === 1 ? '' : 's'} requeued. Press Start when ready.`);
    } catch (err) {
      setError(err.message || 'Reset failed.');
    }
  };

  const onUpload = async (file) => {
    setUploadStatus('Parsing…');
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (rows.length === 0) {
        setUploadStatus('No rows found in file.');
        return;
      }
      setUploadStatus(`Uploading ${rows.length} rows…`);
      const r = await campaignsAPI.addContacts(id, rows);
      setUploadStatus(`Added ${r.data.added}, skipped ${r.data.skipped}, total in file ${r.data.total}.`);
      load();
    } catch (err) {
      setUploadStatus(err.message || 'Upload failed.');
    }
  };

  // Group this campaign's calls by the contact's phone so each contact row can
  // reveal ALL of its transcripts inline (a contact may have been dialed more
  // than once — e.g. 1st + 2nd slot). Calls carry contactPhone (joined from the
  // CampaignContact by callId) or fall back to toNumber. Newest first.
  const callsByPhone = (() => {
    const map = new Map();
    for (const call of calls) {
      const key = call.contactPhone || call.toNumber || call.fromNumber;
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(call);
    }
    return map;
  })();
  const callsForContact = (contact) =>
    callsByPhone.get(contact.phoneE164) || [];


  // ⛔ THE CONTACT ROW'S OWN COUNTERS LIE AFTER A CALLBACK.
  // 2026-08-11: Brian scheduled callbacks for three interested dental prospects. All
  // three fired and placed real calls on 08-05 — and every one of those contacts still
  // read `attempts = 1, lastCalledAt = 07-22`, because the callback path places the
  // call without writing back to the contact row. He concluded the callbacks had never
  // run. 3 of 3 — a 100% failure rate on that path, hidden by low volume.
  //
  // The calls themselves were never missing: core resolves a follow-up to its contact
  // by phone, so `callsForContact` already returns all of them. What was wrong is that
  // the SUMMARY the eye lands on came from a stale scalar instead of from the calls
  // sitting right there. Same lesson as `hasBeenCalled` — read the evidence, not the proxy.
  //
  // Fixing the write-back is core's (see the ask to Charlie); until then this stops the
  // dashboard from repeating the claim.
  const callHistory = (contact) => {
    const cs = callsForContact(contact);
    const rowCount = Number(contact.attempts) || 0;
    const rowLast = contact.lastCalledAt ? new Date(contact.lastCalledAt).getTime() : 0;
    const realLast = cs.reduce((max, c) => {
      const t = new Date(c.startedAt || c.answeredAt || 0).getTime();
      return Number.isNaN(t) ? max : Math.max(max, t);
    }, 0);
    return {
      count: Math.max(rowCount, cs.length),
      lastAt: realLast > rowLast ? new Date(realLast).toISOString() : (contact.lastCalledAt || null),
      // True when a real call is newer than what the row claims — i.e. a call the row
      // never recorded. Surfaced rather than silently corrected, because the row being
      // wrong is a live bug someone still has to fix.
      rowIsStale: realLast > rowLast + 5 * 60_000
    };
  };

  // The answered SUB-CATEGORY for a contact = the classification of its most
  // recent call (calls are newest-first). Only meaningful for answered contacts;
  // returns null when there's no call to classify. Uses callSubcategory() which
  // prefers the stored QA field (#57) and falls back to the transcript heuristic.
  const contactSubcategory = (contact) => {
    const cs = callsForContact(contact);
    if (cs.length === 0) return null;
    return callSubcategory(cs[0]);
  };

  // ── Stacking filter chips ────────────────────────────────────────────────────
  // The chips drive the visible list directly (the old standalone "Answered
  // sub-type" <select> was folded in here). Chips STACK additively: with none
  // active, everything shows; with some active, a contact shows if it matches ANY
  // active chip (union). The contacted / not-contacted chips read fields the data
  // already has; the answered-by chips (Owner / AI / Gatekeeper / Voicemail / IVR
  // menu / Hung up) reuse the client-side sub-classification (callSubcategory,
  // heuristic until #57 lands).
  const anyChipActive = Object.values(filters).some(Boolean);
  // Metro filter is a SEPARATE axis: it ANDs with the chips (narrows further),
  // whereas the chips OR among themselves. Folded into anyFilterActive so the
  // "shown" count + scoped scheduling ("Add" targets the shown rows) both honor it.
  // Search is an AND axis like metro: it narrows whatever the chips already chose.
  const searchTerm = contactQuery.trim().toLowerCase();
  const digitsOnly = searchTerm.replace(/\D/g, '');
  const matchesSearch = (c) => {
    if (!searchTerm) return true;
    const hay = `${c.company || ''} ${c.name || ''}`.toLowerCase();
    if (hay.includes(searchTerm)) return true;
    // Phone match only when the query actually looks like digits, so a 1-character
    // letter search doesn't accidentally match every number.
    return digitsOnly.length >= 3 && String(c.phoneE164 || '').replace(/\D/g, '').includes(digitsOnly);
  };
  const anyFilterActive = anyChipActive || !!metroFilter || hazmatOnly || cargoFilters.length > 0 || !!searchTerm;
  // What the Filters button reports. Counts each ACTIVE axis, so the number matches
  // what a person would say is "on" rather than counting internal flags.
  const activeFilterCount =
    Object.values(filters).filter(Boolean).length
    + (metroFilter ? 1 : 0) + (hazmatOnly ? 1 : 0) + cargoFilters.length + (searchTerm ? 1 : 0);
  const clearAllFilters = () => {
    setFilters({});
    setMetroFilter('');
    setHazmatOnly(false);
    setCargoFilters([]);
    setContactQuery('');
  };
  const contactMatchesFilters = (c) => {
    // "Contacted" must key off DURABLE call evidence — chiefly a stored `outcome`
    // (the same field that drives the Answered/Gatekeeper badge). status/slot1Fired
    // are NOT reliable: a scheduling re-queue (Queue-selected / Auto-schedule)
    // flips an already-answered contact back to status='queued' + slot1Fired=false
    // while KEEPING outcome='answered'. Keying off status/slot alone leaked those
    // answered contacts into "Not contacted". Contacted / Not-contacted are exact
    // inverses of this one predicate so the two chips can never disagree.
    const wasContacted = Boolean(c.outcome)
      || c.status === 'completed'
      || Boolean(c.lastCalledAt)
      || Boolean(c.slot1Fired)
      || Boolean(c.slot2Fired);
    if (filters.contacted && wasContacted) return true;
    if (filters.notContacted && !wasContacted) return true;
    // Outcome-based buckets (map straight to the stored outcome): "Answered" =
    // reached a human (the review bucket); "No answer" = nobody picked up (pairs
    // with Voicemail to give the whole "nobody answered" re-call set).
    if (filters.answered && resolveOutcome(c.outcome) === 'answered') return true;
    if (filters.noAnswer && resolveOutcome(c.outcome) === 'no_answer') return true;
    // Answered-by chips: only meaningful for answered contacts. Reuse the same
    // best-effort sub-classification the (now-removed) Answered-sub-type control
    // used — derived from the transcript heuristic in callSubcategory() until the
    // stored QA sub-classification (#57) is populated.
    const answered = resolveOutcome(c.outcome) === 'answered';
    const sub = answered ? contactSubcategory(c) : null;
    if (filters.answeredOwner && sub === 'owner_reached') return true;
    if (filters.answeredAI && sub === 'reached_ai') return true;
    if (filters.answeredGatekeeper && sub === 'gatekeeper_only') return true;
    if (filters.answeredVoicemail && (sub === 'voicemail' || resolveOutcome(c.outcome) === 'voicemail')) return true;
    if (filters.answeredIvr && sub === 'ivr') return true;
    if (filters.hungUp && sub === 'hung_up') return true;
    // Recover-later bucket: manually-tagged reached-but-no-close leads. Keyed off
    // the salesStage tag (not an outcome), so it works whether tagged by hand or
    // (later) auto-tagged by the AI.
    if (filters.interestedNoClose && c.salesStage === 'interested_no_close') return true;
    return false;
  };
  // ── ENDORSEMENTS ARE ONE OR-GROUP ────────────────────────────────────────────
  // Hazmat authority and the cargo classes are the SAME dimension to the person
  // reading the screen — "what is this carrier endorsed to haul" — and they render
  // as one row of identical chips. Hazmat used to be a separate AND axis, so
  // hazmat + oilfield + liquids/gas evaluated as `hazmat AND (oilfield OR
  // liquids/gas)`: 53 rows, against hazmat's own 77. Adding an endorsement made the
  // list SHRINK, which is the opposite of what a row of additive chips promises.
  //
  // Now: no endorsement chosen → no narrowing; otherwise a carrier shows if it
  // matches ANY chosen endorsement, so adding a chip can only ever grow the list.
  // (Brian, 2026-07-31: "I selected multiple endorsements, and they should show all
  // the selections … it's showing me a number much smaller than just one
  // endorsement.") Metro and the outcome chips stay separate AND axes — those are
  // genuinely different questions about a carrier, not more endorsements.
  const endorsementCount = (hazmatOnly ? 1 : 0) + cargoFilters.length;
  const endorsementActive = endorsementCount > 0;
  // Everything currently off the list, session overlay and server-side suppression
  // together — this is what the "N removed" chip and Restore all both operate on.
  const removedCount = contacts.filter(isRemoved).length;
  const matchesEndorsements = (c) => {
    if (!endorsementActive) return true;
    if (hazmatOnly && hazmatOf(c)) return true;
    return cargoClassesOf(c).some((code) => cargoFilters.includes(code));
  };
  // ⛔ A `?contact=<id>` DEEP LINK WINS OVER EVERY OTHER AXIS.
  //
  // Three attempts at this went out relying on the search box, and Brian still landed
  // on the unfiltered campaign each time: "it takes me to the campaign but not the
  // business. I would still need to search for the business." The link, the effects
  // and the phone matching were all shipped and all correct in isolation — which is
  // the point. Arriving at ONE row was being negotiated with a search predicate, an
  // active-filter flag, a removed-ids set and a pagination slice, and any one of them
  // could veto it silently.
  //
  // So it no longer negotiates. When the URL names a contact, the list IS that
  // contact — no filters, no search, no removal, no paging. Deterministic, and the
  // banner above the table says it is focused and offers the way out.
  const focusContact = deepLinkContactId
    ? contacts.find((c) => c.id === deepLinkContactId) || null
    : null;

  const filteredContacts = focusContact ? [focusContact] : (anyFilterActive
    ? contacts.filter((c) =>
        (!anyChipActive || contactMatchesFilters(c))
        && (!metroFilter || metroOf(c) === metroFilter)
        && matchesEndorsements(c)
        && matchesSearch(c))
    : contacts
  // Rows ticked OFF this working list. Applied LAST and unconditionally, so a
  // removal holds whether or not any filter is active. Nothing is deleted — see
  // `removedIds` / `isRemoved`.
  ).filter((c) => !isRemoved(c));
  // Metros present across ALL of this campaign's contacts (not just the filtered
  // view), in a fixed geographic order — the options for the metro dropdown. Hide
  // the dropdown entirely when the only bucket is 'Other' (a non-TX campaign has
  // nothing meaningful to narrow by; same as a campaign with no city data at all).
  const presentMetros = new Set(contacts.map(metroOf).filter(Boolean));
  const availableMetros = METRO_ORDER.filter((m) => presentMetros.has(m));
  const showMetroFilter = availableMetros.some((m) => m !== 'Other');
  // "Endorsements" for a carrier list = FMCSA hazmat authority (CDL H/N/T endorsements
  // are a per-DRIVER attribute FMCSA doesn't publish per company). Only show the
  // hazmat chip + the Endorsements sort once the campaign carries the enriched field
  // (contact.metadata.hazmat present) — i.e. after the FMCSA re-import; harmless on
  // every other campaign. hazmatCount powers the chip's live count.
  // ── KNOWLEDGE CURATION STATE ─────────────────────────────────────────────────
  // `uncurated` = nothing ticked = the panel's include-all flag is on. Gated on
  // `libraryLoaded` so the warning can never render off an empty initial state and
  // tell the operator "all sent" about a library we haven't fetched yet — a false
  // alarm on a cost warning is how the warning stops being read.
  const libraryLoaded = libKnowledge.length > 0 || libFaqs.length > 0;
  const uncurated = includedKnowledge.size === 0 && includedFaqs.size === 0;

  const hasHazmatData = contacts.some((c) => metaHasKey(c, 'hazmat'));
  // Driver count is FMCSA-enriched and absent on non-carrier lists — gate the
  // sort on real data rather than offering an option that reorders nothing.
  const hasDriverData = contacts.some((c) => driversOf(c) !== null);
  const hazmatCount = contacts.filter(hazmatOf).length;
  // Cargo classes present across ALL contacts, with counts — the options for the
  // cargo dropdown, ordered most-common first so the meaningful classes surface.
  const cargoCounts = contacts.reduce((acc, c) => {
    for (const code of cargoClassesOf(c)) acc.set(code, (acc.get(code) || 0) + 1);
    return acc;
  }, new Map());
  const availableCargo = [...cargoCounts.entries()].sort((a, b) => b[1] - a[1]);
  const hasCargoData = availableCargo.length > 0;

  // Toggle one filter chip on/off (chips stack — this only flips the one key).
  const toggleFilter = (key) =>
    setFilters((prev) => ({ ...prev, [key]: !prev[key] }));

  // Filter-chip descriptors: key + label. Owner/AI/Gatekeeper/Voicemail wire to
  // callSubcategory()'s owner_reached/reached_ai/gatekeeper_only/voicemail; the
  // two new chips (IVR menu → 'ivr', Hung up → 'hung_up') wire to the same
  // heuristic's ivr/hung_up verdicts, so all six answered-by chips are wired.
  // The clean per-contact classification arrives with QA #57 — see callSubcategory.
  // Text only. The emoji made eleven chips read as eleven competing signals; the
  // labels already say what each one is.
  // ⛔ THESE ARE TWO DIFFERENT QUESTIONS AND THEY USED TO SIT IN ONE FLAT ROW.
  //
  // Owner / AI / Gatekeeper / Voicemail / IVR menu are NOT alternatives to
  // "Answered" — they are a breakdown OF it. `contactMatchesFilters` only computes
  // the sub-classification when `resolveOutcome(c.outcome) === 'answered'`, so
  // ticking Voicemail asks "answered, and it was a voicemail system", not "did not
  // reach a person".
  //
  // Presented as one undifferentiated row, that reads backwards. Brian ticked
  // Voicemail + No answer + IVR menu expecting "everything we did NOT reach", and
  // Allen Smile Design (outcome `answered`, 70-second call, sub `voicemail`) stayed
  // on screen showing an Answered badge — behaving exactly as designed and looking
  // exactly like a bug.
  //
  // Grouping them is the fix. The matching is unchanged; what changes is that the
  // screen now says which question each chip answers.
  const FILTER_GROUPS = [
    { label: 'Reached', chips: [
      { key: 'contacted', label: 'Contacted' },
      { key: 'notContacted', label: 'Not contacted' }
    ] },
    { label: 'Outcome', chips: [
      { key: 'answered', label: 'Answered' },
      { key: 'noAnswer', label: 'No answer' },
      { key: 'hungUp', label: 'Hung up' },
      { key: 'interestedNoClose', label: 'Interested — no close' }
    ] },
    // Every chip here implies Answered. Saying so on the label is what stops
    // "Voicemail" reading as "nobody picked up".
    { label: 'Answered by', chips: [
      { key: 'answeredOwner', label: 'Owner' },
      { key: 'answeredAI', label: 'AI' },
      { key: 'answeredGatekeeper', label: 'Gatekeeper' },
      { key: 'answeredVoicemail', label: 'Voicemail' },
      { key: 'answeredIvr', label: 'IVR menu' }
    ] }
  ];
  const FILTER_CHIPS = FILTER_GROUPS.flatMap((g) => g.chips);

  // The contacts in the EXACT order the table renders them — filter AND sort
  // applied, group-header rows dropped. Both the # column and the "Call entries"
  // selection resolve against THIS list, so the numbers you see and type always
  // match the current view: they renumber whenever you filter or sort.
  const orderedVisibleContacts = buildContactRows(filteredContacts, contactSort, contactSubcategory)
    .filter((row) => row.type === 'contact')
    .map((row) => row.contact);

  // Display #: 1-based POSITION in the visible list above (not a fixed id).
  const entryIndexById = (() => {
    const map = new Map();
    orderedVisibleContacts.forEach((c, i) => map.set(c.id, i + 1));
    return map;
  })();

  // DRILL-DOWN VIEW: when the "Call entries" box has a valid selection, the table
  // shows ONLY those rows (so "1-70" narrows the view to 70 rows, keeping the same
  // # numbering). The selection still schedules/removes/deletes exactly these.
  const selectionPositions = (() => {
    const set = new Set();
    const sel = appliedSelection.trim();
    if (!sel) return set;
    for (const part of sel.split(',')) {
      const p = part.trim();
      const m = p.match(/^(\d+)\s*-\s*(\d+)$/);
      if (m) { let a = +m[1], b = +m[2]; if (a > b) [a, b] = [b, a]; for (let x = a; x <= b; x++) set.add(x); }
      else if (/^\d+$/.test(p)) set.add(+p);
    }
    return set;
  })();
  const selectedIdSet = new Set(
    orderedVisibleContacts.filter((c, i) => selectionPositions.has(i + 1)).map((c) => c.id)
  );
  const selectionViewActive = selectedIdSet.size > 0;
  // NOTE: the old `checkedIds` scope ("bulk actions apply to the ticked rows") is
  // gone. Ticking now REMOVES a row, so a removed row is simply not in
  // `orderedVisibleContacts` and every scoped action skips it without a special
  // case. One rule instead of two that had to agree.
  // Rows the table renders — filtered to the selection when one is active (drop
  // group headers so nothing is orphaned), otherwise the full grouped list.
  const displayRows = selectionViewActive
    ? buildContactRows(filteredContacts, contactSort, contactSubcategory).filter((r) => r.type === 'contact' && selectedIdSet.has(r.contact.id))
    : buildContactRows(filteredContacts, contactSort, contactSubcategory);

  // PAGINATION — page the CONTACT rows (headers ride along with their group's rows).
  const displayContactRows = displayRows.filter((r) => r.type === 'contact');
  const totalContactsShown = displayContactRows.length;
  const paginated = pageSize > 0 && totalContactsShown > pageSize;
  const totalPages = paginated ? Math.ceil(totalContactsShown / pageSize) : 1;
  const curPage = Math.min(page, totalPages - 1);
  const pageStart = curPage * pageSize;
  const pageContactIds = paginated
    ? new Set(displayContactRows.slice(pageStart, pageStart + pageSize).map((r) => r.contact.id))
    : null;
  // Rows to render this page: contact rows in the page + each group header whose
  // first in-page contact appears (so groups stay labeled without orphaning).
  const pagedRows = !paginated ? displayRows : (() => {
    const out = [];
    let header = null; let headerShown = false;
    for (const r of displayRows) {
      if (r.type === 'group') { header = r; headerShown = false; continue; }
      if (pageContactIds.has(r.contact.id)) {
        if (header && !headerShown) { out.push(header); headerShown = true; }
        out.push(r);
      }
    }
    return out;
  })();

  // Base entry number: 1-based over the FULL set by createdAt ASC (id tiebreaker)
  // — the index the backend's /schedule-selection maps against. We translate the
  // user's VISIBLE-position selection into these before sending, so "1-20" means
  // the first 20 rows of the CURRENT (filtered/sorted) list, not the first 20
  // ever added. The server contract is unchanged; only what we send is remapped.
  const baseEntryNumById = (() => {
    const ordered = [...contacts].sort((a, b) => {
      const ca = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const cb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      if (ca !== cb) return ca - cb;
      return String(a.id).localeCompare(String(b.id));
    });
    const map = new Map();
    ordered.forEach((c, i) => map.set(c.id, i + 1));
    return map;
  })();

  // "Call entries" control — schedule a numbered selection ("1, 40, 33-200").
  // The numbers are POSITIONS in the current (filtered/sorted) list. We map each
  // to its visible row, then to that row's base entry number (what the server
  // schedules against) — so the selection matches exactly what you see, instead
  // of hitting the first-N-ever-added rows regardless of the active filter.
  // Resolve the "Call entries" positions against the CURRENT visible list.
  // Returns the selected contacts in visible order, each tagged with the base
  // entry number the server's /schedule-selection maps against. Positions that
  // fall outside the visible list are dropped.
  //
  // ⛔ FALL BACK TO WHAT IS TYPED. This read `appliedSelection` only — the value
  // that exists after pressing Enter — so typing "6" and clicking an action button
  // resolved to nothing and the box answered "Enter entry numbers that match the
  // visible rows", i.e. it told the operator to do the thing they had just done.
  // (Brian, 2026-07-31: "If I try to put in six and say remove from list, it doesn't
  // work.") A button next to a filled-in field must act on that field; requiring a
  // separate Enter first is a rule only the code knew about.
  const resolveSelection = () => {
    const sel = (appliedSelection.trim() || selectionDraft.trim());
    if (!sel) return [];
    const positions = [];
    const seenPos = new Set();
    for (const part of sel.split(',')) {
      const p = part.trim();
      if (!p) continue;
      const m = p.match(/^(\d+)\s*-\s*(\d+)$/);
      if (m) {
        let a = parseInt(m[1], 10), b = parseInt(m[2], 10);
        if (a > b) [a, b] = [b, a];
        for (let x = a; x <= b; x++) if (!seenPos.has(x)) { seenPos.add(x); positions.push(x); }
      } else if (/^\d+$/.test(p)) {
        const x = parseInt(p, 10);
        if (!seenPos.has(x)) { seenPos.add(x); positions.push(x); }
      }
    }
    positions.sort((x, y) => x - y);
    const picked = [];
    for (const pos of positions) {
      const c = orderedVisibleContacts[pos - 1];
      if (c) picked.push({ contact: c, baseNum: baseEntryNumById.get(c.id) });
    }
    return picked;
  };

  // (The old "Schedule selected" action was removed — the drill-down box only
  // narrows the view now; scheduling a selection is done via "Add" in the
  // scheduling area above, which targets the shown rows.)

  // "Remove selected from schedule" — UNSCHEDULE the selected entries: clear
  // their call time so they won't be called (the contact stays for later). Used
  // to trim, e.g., the last few that bleed into the evening. Per-contact so it
  // hits exactly the visible selection.
  const runRemoveSelection = async () => {
    const picked = resolveSelection();
    if (picked.length === 0) { setSelectionStatus('Enter entry numbers that match the visible rows, e.g. 1, 40, 33-200.'); return; }
    // Guard a large unschedule — each row is its own request, and this is the
    // kind of range that's easy to fat-finger ("1-500" vs "1-5").
    if (picked.length > 25 && !(await confirm({
      title: `Remove ${picked.length} entries from the schedule?`,
      body: 'Their call times are cleared. The contacts stay in the campaign.',
      confirmLabel: 'Remove times'
    }))) return;
    setSelectionStatus('Removing from schedule…');
    let ok = 0;
    for (const { contact } of picked) {
      try { await campaignsAPI.updateContact(id, contact.id, { scheduledAt: null }); ok += 1; }
      catch { /* keep going; report how many cleared */ }
    }
    const unit = ok === 1 ? 'entry' : 'entries';
    setSelectionStatus(`Removed ${ok} ${unit} from the schedule — times cleared, contacts kept.`);
    load();
  };

  // "Remove from list" for a TYPED entry selection — the same action as the row
  // checkbox, reachable the way Brian actually reached for it (type 6, click).
  const runRemoveFromListSelection = async () => {
    const picked = resolveSelection();
    if (picked.length === 0) {
      setSelectionStatus('Enter entry numbers that match the visible rows, e.g. 1, 40, 33-200.');
      return;
    }
    setSelectionStatus(`Removing ${picked.length} from the list…`);
    const res = await removeManyFromList(picked.map((p) => p.contact));
    // Clear the box: the entries it names are gone from the view, so leaving the
    // numbers behind would now point at different rows (# is positional).
    setSelectionDraft('');
    setAppliedSelection('');
    // Report what ACTUALLY happened. Announcing "not called" on a request that never
    // reached the server is the exact lie this feature exists to avoid.
    setSelectionStatus(
      res?.hiddenOnly
        ? `Hidden from this view only — the server didn’t record it, so anything already scheduled will still be called.`
        : res?.ok
          ? `Removed ${res.changed} from this list — still in the campaign, not called.`
          : (res?.message || 'Nothing was removed.')
    );
  };

  // "Delete selected" — permanently remove the selected contacts from the campaign
  // (not just their call time). Used to purge rows you don't want at all, e.g. the
  // ones auto-scheduled into the evening.
  const runDeleteSelection = async () => {
    const picked = resolveSelection();
    if (picked.length === 0) { setSelectionStatus('Enter entry numbers that match the visible rows, e.g. 1, 40, 33-200.'); return; }
    // ⛔ NAME THEM. The entry numbers are POSITIONAL, and the list renumbers whenever
    // a row is removed — so a number typed a moment ago can now point at a different
    // business. This is a permanent delete, so the confirm must show WHO, not a count.
    const names = picked.map(({ contact: c }) => c.company || c.name || formatPhone(c.phoneE164));
    if (!(await confirm({
      title: `Delete ${picked.length} contact${picked.length === 1 ? '' : 's'} from this campaign?`,
      body: `${names.slice(0, 12).join('\n')}${names.length > 12 ? `\n…and ${names.length - 12} more` : ''}\n\n`
        + 'They are removed entirely — not just unscheduled — and this cannot be undone.',
      confirmLabel: 'Delete',
      danger: true
    }))) return;
    setSelectionStatus('Deleting…');
    let ok = 0;
    const removed = new Set();
    for (const { contact } of picked) {
      try { await campaignsAPI.removeContact(id, contact.id); removed.add(contact.id); ok += 1; }
      catch { /* keep going; report how many deleted */ }
    }
    setContacts((cs) => cs.filter((c) => !removed.has(c.id)));
    setSelectionDraft('');
    const unit = ok === 1 ? 'contact' : 'contacts';
    setSelectionStatus(`Deleted ${ok} ${unit} from the campaign.`);
  };

  // Per-row delete — remove ONE contact from the campaign.
  const deleteOneContact = async (contact) => {
    // Spell out the scope: this sits in a row inside a FILTERED table, so "delete"
    // reads ambiguously — people reasonably expect it to drop the row from the view.
    // It does not; it removes the business from the campaign for good.
    if (!(await confirm({
      title: `Remove ${contact.company || contact.name || formatPhone(contact.phoneE164)} from this campaign?`,
      body: 'This deletes them from the CAMPAIGN entirely — not just from the filtered view you are '
        + 'looking at. Their call history goes with them. This cannot be undone.',
      confirmLabel: 'Remove from campaign',
      danger: true
    }))) return;
    try {
      await campaignsAPI.removeContact(id, contact.id);
      setContacts((cs) => cs.filter((c) => c.id !== contact.id));
    } catch (err) {
      setError(err.message || 'Delete failed.');
    }
  };

  // CSV export for the results report. Pulls the FULL contact set (the table only
  // shows the first page) so the export is complete, then builds one row per
  // contact client-side. Calls are matched by phone from the already-loaded set.
  const onExportCsv = async () => {
    setExporting(true);
    try {
      const res = await campaignsAPI.contacts(id, { limit: 500 });
      const all = res?.data || contacts;
      exportContactsCsv(campaign?.name, all, callsForContact);
    } catch (err) {
      // Fall back to the rows already on the page if the full fetch fails.
      exportContactsCsv(campaign?.name, contacts, callsForContact);
      setError(err.message || 'CSV export used the visible rows only.');
    } finally {
      setExporting(false);
    }
  };

  if (loading && !campaign) return <div className="px-6 md:px-10 py-8 text-stone-500">Loading…</div>;
  if (error && !campaign) return <div className="px-6 md:px-10 py-8"><ErrorBanner message={error} /></div>;
  if (!campaign) return <div className="px-6 md:px-10 py-8 text-stone-500">Not found.</div>;

  const pct = campaign.totalContacts > 0
    ? Math.round((campaign.contactsCompleted / campaign.totalContacts) * 100)
    : 0;

  return (
    <div className="px-6 md:px-10 py-8">
      <Link to="/campaigns" className="inline-flex items-center gap-1 text-sm text-stone-400 hover:text-alfred-gold mb-4">
        <ChevronLeft size={16} /> Back to campaigns
      </Link>

      <PageHeader
        title={campaign.name}
        subtitle={`${campaign.status} · ${campaign.contactsCompleted}/${campaign.totalContacts} completed (${pct}%)`}
      />

      {error && <div className="mb-6"><ErrorBanner message={error} /></div>}

      {/* Above the pane nav on purpose: if the platform stopped this campaign, that
          is the first thing to know, whichever pane you were last looking at. */}
      <AutoPauseBanner campaign={campaign} className="mb-6" />

      {/* In-page sub-nav. Was a 224px LEFT RAIL, which cost the contacts table a
          quarter of the screen for three links — on a table with eleven columns and
          a filter bar that is the wrong trade. Now a horizontal tab strip across the
          top, and the body below runs the full width of the page. */}
      <div className="mb-6 flex flex-wrap items-center gap-1 border-b border-stone-800">
        {[
          { key: 'results', label: 'Results Report', Icon: BarChart3 },
          { key: 'config', label: 'Configuration', Icon: Settings },
          { key: 'contacts', label: 'Contacts & Transcripts', Icon: Users },
        ].map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setActivePane(key)}
            aria-pressed={activePane === key}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm -mb-px border-b-2 transition ${
              activePane === key
                ? 'border-alfred-gold text-stone-100'
                : 'border-transparent text-stone-400 hover:text-stone-200'
            }`}
          >
            <Icon size={16} />
            <span>{label}</span>
          </button>
        ))}
      </div>

      <div>
        <div className="min-w-0">

      {/* End-of-campaign RESULTS REPORT — a summary the owner reads when the
          campaign finishes (outcome breakdown, headline metrics, funnel, CSV
          export). Renders nothing until the report payload has loaded. */}
      {activePane === 'results' && (
      <CampaignReportCard report={report} onExport={onExportCsv} exporting={exporting} />
      )}

      {/* Configuration */}
      {activePane === 'config' && (
      <>
      <section className="card mb-6">
        <h2 className="text-lg text-stone-100 mb-4">Configuration</h2>
        <div className="space-y-4">
          <div>
            <label className="field-label">Campaign Pitch / Script</label>
            <p className="mt-1 mb-2 text-sm text-stone-400">
              The single script the AI runs from AND the QA monitor grades against — edit here and
              both change, no code push. Guardrails below are enforced separately and always win.
            </p>
            <textarea
              className="field-input min-h-[360px] font-mono text-sm leading-relaxed"
              placeholder="What Alfred is calling about and how to run the call. Replaces the tenant's default custom-instructions for THIS campaign only. Be specific about the goal, qualification, and how to handle a gatekeeper — but DON'T put guardrails here (they're always enforced)."
              value={campaign.systemPromptOverride || ''}
              onChange={(e) => setField('systemPromptOverride', e.target.value)}
              maxLength={PITCH_MAX}
            />
            <div className="mt-1 text-xs text-stone-500 text-right tabular-nums">
              {(campaign.systemPromptOverride || '').length.toLocaleString()} / {PITCH_MAX.toLocaleString()} characters
            </div>
          </div>

          {/* Layer 2's other half — HOW to sell, which used to be a Python string
              that outranked the pitch above. Hidden entirely until core has the
              column (see behaviorSupported). */}
          {behaviorSupported && (
            <CampaignBehaviorEditor
              value={campaign.behaviorBlock || ''}
              onChange={(v) => setField('behaviorBlock', v)}
              saved={behaviorSaved}
              validated={campaign.behaviorBlockValidated || null}
              validatedAt={campaign.behaviorBlockValidatedAt || null}
              onValidate={validateBehavior}
              onRestore={(text) => setField('behaviorBlock', text)}
              dirty={behaviorDirty}
              busy={saving || validatingBehavior}
              supportsValidation={behaviorValidationSupported}
            />
          )}

          {/* Read-only: the hard-coded guardrails that ALWAYS win over the pitch above.
              Shown for clarity; they are NOT editable here (they live in the backend/panel). */}
          <div className="rounded-lg border border-stone-800 bg-stone-900/40 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Settings size={14} className="text-stone-400" />
              <h3 className="text-sm font-medium text-stone-200">Guardrails (always enforced)</h3>
            </div>
            <p className="text-xs text-stone-500 mb-3">
              These are hard-coded and take precedence over the pitch above — editing the pitch can't
              override them. Listed here so you know what the AI will always do regardless of the script.
            </p>
            <ul className="space-y-1.5 text-sm text-stone-300">
              {GUARDRAILS.map((g) => (
                <li key={g} className="flex items-start gap-2">
                  <span className="mt-0.5 text-alfred-gold">•</span><span>{g}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <label className="field-label">Opening line</label>
            <input
              className="field-input"
              placeholder="Hi, this is Alfred from Relay Work — do you have a quick moment?"
              value={campaign.openingLine || ''}
              onChange={(e) => setField('openingLine', e.target.value)}
              maxLength={500}
            />
          </div>
          <div>
            <label className="field-label inline-flex items-center gap-2">
              <Mic size={14} className="text-alfred-gold" /> Campaign AIs
            </label>
            <p className="mt-1 mb-3 text-sm text-stone-400">
              The AI(s) that run THIS campaign's calls — each with its own voice and
              vendor. The first AI is the host; add a second or third to run a panel.
              Leave empty to use the tenant's shared persona roster instead.
            </p>

            {roster.length === 0 ? (
              <p className="text-sm text-stone-500 mb-3">
                No campaign AIs yet — calls use the tenant's shared roster. Add one to
                give this campaign its own voice.
              </p>
            ) : (
              <ul className="space-y-3 mb-3">
                {roster.map((ai, idx) => (
                  <li key={idx} className="rounded-lg border border-stone-800 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs uppercase tracking-wider text-stone-500">
                        {idx === 0 ? 'Host (AI 1)' : `AI ${idx + 1}`}
                      </span>
                      <button
                        type="button"
                        className="btn-ghost text-red-300 hover:text-red-200"
                        onClick={() => removeAi(idx)}
                        aria-label="Remove AI"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-[180px,1fr] gap-2 items-start">
                      <input
                        className="field-input"
                        placeholder="Name (e.g. Jay)"
                        value={ai.name || ''}
                        onChange={(e) => updateAi(idx, { name: e.target.value })}
                        maxLength={80}
                      />
                      <VoicePicker
                        provider={ai.voiceProvider || ''}
                        voiceId={ai.voiceId || ''}
                        onChange={({ provider, voiceId }) =>
                          updateAi(idx, { voiceProvider: provider || 'elevenlabs', voiceId })
                        }
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <button type="button" className="btn-ghost" onClick={addAi}>
              <UserPlus size={14} /> Add AI
            </button>
          </div>

          <div>
            <label className="field-label">Voice engine (TTS)</label>
            <select
              className="field-input"
              value={campaign.ttsVendorOverride || ''}
              onChange={(e) => setField('ttsVendorOverride', e.target.value || null)}
            >
              <option value="">Default — each AI’s own engine</option>
              <option value="deepgram">Deepgram (Aura)</option>
              <option value="cartesia">Cartesia (Sonic)</option>
              <option value="elevenlabs">ElevenLabs</option>
            </select>
            <p className="mt-1 text-sm text-stone-400">
              Leave this on <strong>Default</strong> and each AI above speaks through the engine
              its own voice belongs to — the voice you picked is the voice you get. Set it only to
              force every AI on this campaign onto one engine, whatever their voices say.
            </p>
            <p className="mt-1 text-sm text-stone-400">
              Worth knowing before you force <strong>ElevenLabs</strong>: that account is
              single-concurrency, so a campaign dialling several calls at once can dead-air. It is
              fine for short lists and risky for long ones.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div>
              <label className="field-label">Call from</label>
              <select
                className="field-input"
                value={campaign.fromNumberId || ''}
                onChange={(e) => setField('fromNumberId', e.target.value || null)}
              >
                <option value="">First active number</option>
                {phoneNumbers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {formatPhone(p.phoneNumber)}{p.configuration?.label ? ` — ${p.configuration.label}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" className="btn-primary" onClick={saveCampaign} disabled={saving}>
              <Save size={14} /> {saving ? 'Saving…' : 'Save'}
            </button>
            {savedAt && <span className="text-xs text-stone-500">Saved {savedAt.toLocaleTimeString()}</span>}
          </div>
        </div>
      </section>

      {/* Follow-ups already promised on THIS campaign. They are callbacks, not
          scheduled-call slots, so they never appeared in the Call column and there
          was nowhere on this page to see them. */}
      {/* Costs nothing — `contacts` is already loaded with scheduledAt on every row. */}
      <ScheduledCalls contacts={contacts} campaign={campaign} limit={20} />
      <UpcomingCalls campaignId={id} title="Scheduled follow-ups" limit={20} />

      {/* WHICH PITCH this campaign runs. Sits ABOVE the script fields on purpose:
          selecting a bundle is the decision, and the individual fields below are the
          fallback for anything the pitch doesn't define. Hides itself until core
          ships /pitches. */}
      <PitchPicker
        campaignId={id}
        campaignPitchId={campaign?.pitchId}
        campaignStatus={campaign?.status}
        onChanged={() => load()}
      />

      {/* Campaign-scoped Knowledge & FAQs — used ONLY on this campaign's calls,
          layered on top of the tenant-wide library the inbound AI sees.
          Collapsed by default (it's long); toggle the header to expand. */}
      <section className="card mb-6">
        <button
          type="button"
          className="flex w-full items-center justify-between text-left"
          onClick={() => setKnowledgeOpen((o) => !o)}
          aria-expanded={knowledgeOpen}
        >
          <span className="inline-flex items-center gap-2">
            {knowledgeOpen ? <ChevronDown size={18} className="text-stone-400" /> : <ChevronRight size={18} className="text-stone-400" />}
            <span className="text-lg text-stone-100">Knowledge &amp; FAQs for this campaign</span>
            {/* ⛔ ZERO SELECTED MEANS *EVERYTHING* IS SENT — the badge must not go
                quiet in the expensive state. The panel treats an empty
                includedKnowledgeIds as an INCLUDE-ALL flag
                (panel_agent_multitenant.py:3557, `(not knowledge_curated)`), so a
                campaign with nothing ticked injects the whole tenant library into the
                system prompt on EVERY TURN of EVERY CALL. The old badge rendered only
                when the count was > 0, so the single most expensive configuration
                displayed nothing at all and read as "nothing included". 11 campaigns
                are in exactly that state in prod. */}
            {libraryLoaded && uncurated ? (
              <span
                className="badge bg-amber-600/20 text-amber-200 border border-amber-600/40 text-[10px]"
                title="Nothing is selected, so the AI receives the entire tenant-wide library on every turn of every call. Tick the ones this campaign needs to cut it down."
              >
                ⚠︎ all {libKnowledge.length + libFaqs.length} sent every turn
              </span>
            ) : (includedKnowledge.size + includedFaqs.size) > 0 ? (
              <span className="badge bg-stone-800 text-stone-300 border border-stone-700 text-[10px]">
                {includedKnowledge.size + includedFaqs.size} from library
              </span>
            ) : null}
          </span>
          <span className="text-xs text-stone-500">{knowledgeOpen ? 'Hide' : 'Show'}</span>
        </button>
        {!knowledgeOpen && (
          <p className="text-sm text-stone-500 mt-2">
            Facts and Q&amp;A the campaign's AIs can use on the call. Click to expand.
            {libraryLoaded && uncurated && (
              <span className="block text-amber-200 mt-1">
                Nothing is selected, so the whole library ({libKnowledge.length} articles,{' '}
                {libFaqs.length} FAQs) goes into every prompt on every call. Expand to pick
                just what this campaign needs.
              </span>
            )}
          </p>
        )}
        {knowledgeOpen && (
        <>
        <p className="text-sm text-stone-400 mt-2 mb-4">
          Facts and Q&amp;A the campaign's AIs can use on the call — on top of the
          tenant-wide library in <span className="text-stone-200">Settings</span>.
        </p>
        <div className="space-y-6">
          <div className="space-y-2">
            <KnowledgeEditor campaignId={id} />
            <p className="text-xs text-stone-500">
              Your shared knowledge library is managed at the tenant level —{' '}
              <Link to="/settings" className="text-alfred-gold hover:underline">Settings → Knowledge</Link>.
              Add files here only for docs specific to THIS campaign.
            </p>
          </div>
          <FaqEditor campaignId={id} />

          {/* Pull in tenant-wide (Settings) library rows — opt-in per row. The
              campaign always uses its own scoped rows above; these checkboxes add
              ONLY the chosen tenant-wide articles/FAQs (not the whole library). */}
          <section className="card">
            <div className="mb-4">
              <h2 className="text-xl text-stone-100">Include FAQs from the tenant-wide library</h2>
              <p className="text-sm text-stone-400 mt-1">
                Check tenant-wide FAQs (managed in{' '}
                <Link to="/settings" className="text-alfred-gold hover:underline">Settings</Link>)
                to also make them available on this campaign's calls. Once at least one
                is ticked, unchecked rows are NOT used by this campaign — but with
                <span className="text-amber-200"> nothing ticked at all, every row is sent</span>,
                which is the expensive default rather than the empty one.
              </p>
            </div>

            {libError && (
              <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{libError}</p>
            )}

            <div className="grid grid-cols-1 gap-6">
              <div>
                <h3 className="text-sm uppercase tracking-wider text-stone-500 mb-2">
                  FAQs ({includedFaqs.size}/{libFaqs.length} included)
                </h3>
                {libFaqs.length === 0 ? (
                  <p className="text-sm text-stone-500">No tenant-wide FAQs yet.</p>
                ) : (
                  <ul className="space-y-2">
                    <li>
                      <label className="flex items-center gap-2 rounded-lg border border-stone-800 bg-stone-900/40 px-3 py-2 cursor-pointer hover:border-stone-700">
                        <input
                          type="checkbox"
                          className="accent-alfred-gold"
                          checked={libFaqs.length > 0 && includedFaqs.size === libFaqs.length}
                          ref={(el) => {
                            if (el) el.indeterminate = includedFaqs.size > 0 && includedFaqs.size < libFaqs.length;
                          }}
                          onChange={(e) => toggleAllIncluded('faq', e.target.checked)}
                        />
                        <span className="text-xs uppercase tracking-wider text-stone-400">
                          {includedFaqs.size === libFaqs.length ? 'Deselect all' : 'Select all'}
                        </span>
                      </label>
                    </li>
                    {libFaqs.map((f) => (
                      <li key={f.id}>
                        <label className="flex items-start gap-2 rounded-lg border border-stone-800 p-3 cursor-pointer hover:border-stone-700">
                          <input
                            type="checkbox"
                            className="mt-1 accent-alfred-gold"
                            checked={includedFaqs.has(f.id)}
                            onChange={() => toggleIncluded('faq', f.id)}
                          />
                          <span className="min-w-0">
                            <span className="flex items-center gap-2 flex-wrap">
                              <span className="text-stone-100 font-medium">{f.question}</span>
                              {f.category && (
                                <span className="badge bg-stone-800 text-stone-300 border border-stone-700 text-[10px]">
                                  {f.category}
                                </span>
                              )}
                            </span>
                            <span className="block mt-1 text-xs text-stone-400 line-clamp-2 whitespace-pre-wrap">
                              {f.answer}
                            </span>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </section>
        </div>
        </>
        )}
      </section>

      </>
      )}

      {/* Contacts — merged with calls & transcripts. Each contact row expands to
          reveal its call transcript(s). */}
      {activePane === 'contacts' && (
      <section className="card">
        <header className="flex items-start justify-between mb-2 gap-4 flex-wrap">
          <h2 className="text-lg text-stone-100 inline-flex items-center gap-2">
            <MessageSquareText size={18} className="text-alfred-gold" />
            Contacts &amp; transcripts ({contactTotal.toLocaleString()})
          </h2>
          {/* Contact-management action row — add / import / upload contacts. The
              auto-schedule controls live in the Scheduling section above the list. */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Contacts | History — Contacts = schedule/manage the list; History
                = read-only log of calls placed. */}
            <div className="inline-flex rounded-lg border border-stone-800 overflow-hidden mr-1">
              {[{ k: 'contacts', label: 'Contacts' }, { k: 'history', label: 'History' }].map((t) => (
                <button
                  key={t.k}
                  type="button"
                  onClick={() => setContactsView(t.k)}
                  aria-pressed={contactsView === t.k}
                  className={`px-3 py-1.5 text-sm ${contactsView === t.k ? 'bg-alfred-gold/15 text-alfred-gold' : 'text-stone-400 hover:bg-stone-800/50'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {contactsView === 'contacts' && (<>
            <button type="button" className="btn-ghost" onClick={() => { setAddOpen(!addOpen); setImportOpen(false); setUploadOpen(false); setScheduleOpen(false); }}>
              <UserPlus size={14} /> Add contact
            </button>
            <button type="button" className="btn-ghost" onClick={() => { setImportOpen(!importOpen); setUploadOpen(false); setAddOpen(false); setScheduleOpen(false); }}>
              <Database size={14} /> Import from list
            </button>
            <button type="button" className="btn-ghost" onClick={() => { setUploadOpen(!uploadOpen); setImportOpen(false); setAddOpen(false); setScheduleOpen(false); }}>
              <Upload size={14} /> Upload CSV
            </button>
            </>)}
          </div>
        </header>

        {contactsView === 'contacts' && focusContact && (
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-alfred-gold/40 bg-alfred-gold/10 px-3 py-2 text-sm">
            <span className="text-alfred-gold">
              Showing one business — {focusContact.company || focusContact.name || formatPhone(focusContact.phoneE164)}
            </span>
            <Link to={`/campaigns/${id}`} className="ml-auto text-xs text-stone-300 underline-offset-2 hover:text-alfred-gold hover:underline">
              Show all contacts
            </Link>
          </div>
        )}
        {contactsView === 'history' && <CampaignHistory calls={calls} contacts={contacts} />}

        {contactsView === 'contacts' && (<>
        <p className="text-sm text-stone-400 mb-4">
          Each contact's call transcript(s) are inline — click a row to read them.
          Set a sales stage per contact and re-order the list below.
        </p>

        {/* One button, then everything. See showFilters above. */}
        <div className="mb-4 flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            aria-expanded={showFilters}
            className={`rounded-full border px-3 py-1.5 text-xs ${
              showFilters || activeFilterCount > 0
                ? 'border-alfred-gold/50 bg-alfred-gold/15 text-alfred-gold'
                : 'border-stone-700 bg-stone-800/40 text-stone-400 hover:bg-stone-800'
            }`}
          >
            Filters{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ''}
          </button>
          {/* Always visible — looking up one business is the most common thing
              anyone does on an 881-row list, and it shouldn't be behind a panel. */}
          <div className="relative">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
            <input
              className="field-input py-1.5 pl-9 pr-8 text-sm w-56"
              placeholder="Find a business…"
              value={contactQuery}
              onFocus={(e) => e.target.select()}
              onChange={(e) => setContactQuery(e.target.value)}
              aria-label="Search contacts by business, name or phone"
            />
            {contactQuery && (
              <button
                type="button"
                onClick={() => setContactQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300"
                aria-label="Clear search"
              >
                <X size={13} />
              </button>
            )}
          </div>
          {/* Active filters stay visible as a sentence even when the panel is shut —
              the count alone doesn't tell you the list is being narrowed. */}
          {removedCount > 0 && (
            <>
              <span className="rounded-full border border-alfred-gold/50 bg-alfred-gold/15 px-3 py-1.5 text-xs text-alfred-gold">
                {removedCount} removed
              </span>
              <button
                type="button"
                onClick={restoreAllRemoved}
                className="text-xs text-stone-500 underline hover:text-stone-300"
              >
                Restore all
              </button>
              {/* Two different truths, and they must read differently. With the
                  endpoint live, removal genuinely stops the call. Without it, the
                  row is only hidden — and an already-scheduled contact WILL still be
                  dialed, so saying "won't be called" here would be false. */}
              <span className="text-xs text-stone-500">
                {suppressUnavailable
                  ? 'Hidden from this view only — not saved, and any call already scheduled still goes out.'
                  : 'Kept in the campaign and in the report, but not called.'}
              </span>
            </>
          )}
          {removeStatus && (
            <span className="text-xs text-amber-300">{removeStatus}</span>
          )}
          {activeFilterCount > 0 && (
            <>
              <span className="text-xs text-stone-400">
                {/* Both sides must exclude removed rows, or a removal shrinks only
                    the numerator and reads as a filter that stopped matching. */}
                {/* Name the query in the count. Brian searched a second number, the
                    new row rendered, and he read it as the PREVIOUS result — because
                    "1 of 880 shown" looks identical whichever search produced it.
                    Echoing the term back is what makes a new result legible as new. */}
                {searchTerm ? (
                  <>
                    {filteredContacts.length} match{filteredContacts.length === 1 ? '' : 'es'} for{' '}
                    <span className="text-stone-200">“{contactQuery.trim()}”</span>
                    <span className="text-stone-600"> · of {contacts.length - removedCount}</span>
                  </>
                ) : (
                  <>{filteredContacts.length} of {contacts.length - removedCount} shown</>
                )}
              </span>
              <button
                type="button"
                onClick={clearAllFilters}
                className="text-xs text-stone-500 underline hover:text-stone-300"
              >
                Clear
              </button>
            </>
          )}
        </div>

        {showFilters && (
        <div className="mb-4 flex items-center gap-2 flex-wrap rounded-lg border border-stone-800 bg-stone-900/40 p-3">
          {/* Metro filter — narrows to one TX metro (suburbs folded in, from contact
              metadata city). Only shown when the campaign has a real metro present.
              ANDs with the chips. */}
          {showMetroFilter && (
            <select
              className="field-input py-1 px-2 text-xs w-auto"
              value={metroFilter}
              onChange={(e) => setMetroFilter(e.target.value)}
              title="Show only contacts in this metro (suburbs within ~50mi included)"
            >
              <option value="">All metros</option>
              {availableMetros.map((metro) => (
                <option key={metro} value={metro}>{metro}</option>
              ))}
            </select>
          )}
          {/* ── ENDORSEMENTS — ONE OR-GROUP ──────────────────────────────────────
              Hazmat authority and the cargo classes are the same question ("what is
              this carrier endorsed to haul") and now combine with OR: a carrier
              matching ANY selected endorsement is shown, so adding a chip can only
              grow the list. The label exists so the group is legible as one control
              — these chips used to sit unlabelled next to the outcome chips, which
              are a separate AND axis, and nothing on screen said so. */}
          {(hasHazmatData || hasCargoData) && (
            <span className="text-xs uppercase tracking-wider text-stone-500">Endorsements:</span>
          )}
          {hasHazmatData && (
            <button
              type="button"
              onClick={() => setHazmatOnly((v) => !v)}
              aria-pressed={hazmatOnly}
              title="Hazmat-authorized carriers (FMCSA). Combines with the cargo classes using OR — a carrier matching ANY selected endorsement is shown."
              className={`rounded-full border px-2.5 py-1 text-xs ${
                hazmatOnly
                  ? 'border-alfred-gold/50 bg-alfred-gold/15 text-alfred-gold'
                  : 'border-stone-700 bg-stone-800/40 text-stone-400 hover:bg-stone-800'
              }`}
            >
              Hazmat authorized{hazmatCount ? ` (${hazmatCount})` : ''}{hazmatOnly ? ' ✕' : ''}
            </button>
          )}
          {/* Cargo classes — the rest of the same OR-group as Hazmat above. Selected
              classes show as removable chips; the dropdown adds another. Each carries
              its own count so the union total below is checkable at a glance. */}
          {hasCargoData && cargoFilters.map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => setCargoFilters((prev) => prev.filter((c) => c !== code))}
              title="Remove this cargo class from the filter"
              className="rounded-full border border-alfred-gold/50 bg-alfred-gold/15 text-alfred-gold px-2.5 py-1 text-xs inline-flex items-center gap-1"
            >
              {cargoLabel(code)}
              {cargoCounts.get(code) ? ` (${cargoCounts.get(code)})` : ''} ✕
            </button>
          ))}
          {hasCargoData && availableCargo.some(([code]) => !cargoFilters.includes(code)) && (
            <select
              className="field-input py-1 px-2 text-xs w-auto"
              value=""
              onChange={(e) => {
                const v = e.target.value;
                if (v) setCargoFilters((prev) => (prev.includes(v) ? prev : [...prev, v]));
              }}
              title="Add a cargo class — carriers hauling ANY selected class are shown (combine several into one segment)"
            >
              <option value="">{cargoFilters.length ? '+ Add cargo…' : 'Cargo / endorsements…'}</option>
              {availableCargo
                .filter(([code]) => !cargoFilters.includes(code))
                .map(([code, n]) => (
                  <option key={code} value={code}>{cargoLabel(code)} ({n})</option>
                ))}
            </select>
          )}
          {FILTER_GROUPS.map((group) => (
            <span key={group.label} className="inline-flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wider text-stone-500">
                {group.label}
                {/* The label alone would still leave "Answered by · Voicemail" ambiguous
                    on a quick read, so spell the implication out on hover. */}
                {group.label === 'Answered by' && (
                  <span title="These narrow the ANSWERED calls — a contact must have been answered to match. They are not ways of missing a call.">
                    {' '}·<span className="cursor-help"> answered only</span>
                  </span>
                )}
              </span>
              {group.chips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => toggleFilter(chip.key)}
                  aria-pressed={filters[chip.key]}
                  title={group.label === 'Answered by'
                    ? `${chip.label}: answered calls where the AI reached ${chip.label.toLowerCase()}. Does NOT include calls nobody answered.`
                    : undefined}
                  className={`rounded-full border px-2.5 py-1 text-xs ${
                    filters[chip.key]
                      ? 'border-alfred-gold/50 bg-alfred-gold/15 text-alfred-gold'
                      : 'border-stone-700 bg-stone-800/40 text-stone-400 hover:bg-stone-800'
                  }`}
                >
                  {chip.label}
                </button>
              ))}
            </span>
          ))}
          {(anyFilterActive || selectionViewActive) && (
            <span className="text-xs text-stone-400">
              {selectionViewActive ? selectedIdSet.size : filteredContacts.length} shown
              {/* Spell the OR out next to the number. The count is the thing that
                  looked wrong (53 with hazmat's own 77 on screen), so the screen
                  should say which question it answered. */}
              {!selectionViewActive && endorsementCount > 1 && (
                <span className="text-stone-500"> · any of {endorsementCount} endorsements</span>
              )}
            </span>
          )}
        </div>
        )}

        {showFilters && (
        <>
        {/* Sort-by control. The criteria are visible and the active one is shown.
            PRIMARY = Call response (answered conversations float to the top). */}
        <div className="mb-4 flex items-center gap-3 flex-wrap">
          <div className="inline-flex items-center gap-2">
            <ArrowUpDown size={14} className="text-stone-400" />
            <span className="text-xs uppercase tracking-wider text-stone-500">Sort by:</span>
          </div>
          <div className="inline-flex items-center gap-1 flex-wrap">
            {SORT_OPTIONS.filter((opt) => (opt.value !== 'endorsements' || hasHazmatData)
            && (opt.value !== 'drivers' || hasDriverData)).map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={contactSort === opt.value ? 'btn-primary' : 'btn-ghost'}
                onClick={() => setContactSort(opt.value)}
                title={opt.hint}
                aria-pressed={contactSort === opt.value}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {contactSort === 'priority' && (
            <button
              type="button"
              className="btn-ghost"
              onClick={bulkSetCallTime}
              title="Stamp the 1st-call time on every queued contact currently shown — handy after sorting answered-first to schedule that group together."
            >
              <CalendarClock size={14} /> Set 1st-call time for visible rows
            </button>
          )}
          {bulkTimeStatus && <span className="text-xs text-stone-400">{bulkTimeStatus}</span>}
          {/* The former "Answered sub-type" <select> was folded into the top
              filter chips (Owner / AI / Gatekeeper / Voicemail / IVR menu /
              Hung up), which now drive filteredContacts directly. */}
        </div>
        </>
        )}

        {/* Call-entries drill-down — a FILTER, so it lives with the filters (moved
            here from inside the scheduling area). Narrows the list to typed entry
            numbers; the scheduling "Add" below then targets whatever's shown. */}
        {showFilters && contacts.length > 0 && (
          <div className="mb-4 rounded-lg border border-stone-800 p-3">
            <div className="flex flex-col gap-2">
              <span className="text-xs uppercase tracking-wider text-stone-500">Call entries — drill down:</span>
              <input
                className="field-input py-1 px-2 text-sm w-full"
                placeholder="e.g. 1, 40, 33-200"
                value={selectionDraft}
                onChange={(e) => setSelectionDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') applySelection(); }}
                title="Positions in the # column of the list AS YOU SEE IT (filtered/sorted) — single numbers and inclusive ranges, e.g. 1, 40, 33-200. Press Enter to show just those."
              />
              <div className="flex flex-wrap items-center gap-2">
                {/* Enter just narrows the view to the typed entries — it does NOT
                    schedule. To schedule these, use "Add" in the scheduling area
                    below (it targets the shown selection). */}
                <button
                  type="button"
                  className="btn-primary py-1"
                  onClick={applySelection}
                >
                  Enter
                </button>
                {/* The action Brian reached for by typing an entry number. Same
                    meaning as the row checkbox: off this list, still in the
                    campaign, not called. */}
                <button type="button" className="btn-ghost py-1" onClick={runRemoveFromListSelection}>
                  Remove from list
                </button>
                <button type="button" className="btn-ghost py-1 text-red-300 hover:text-red-200" onClick={runRemoveSelection}>
                  Remove selected from schedule
                </button>
                <button type="button" className="btn-ghost py-1 text-red-300 hover:text-red-200" onClick={runDeleteSelection}>
                  <Trash2 size={13} /> Delete selected
                </button>
              </div>
              {selectionViewActive && (
                <div className="flex items-center gap-2 text-xs text-alfred-gold">
                  <span>Showing only your selection ({selectedIdSet.size} of {orderedVisibleContacts.length}).</span>
                  <button
                    type="button"
                    className="underline hover:text-alfred-cream"
                    onClick={() => { setSelectionDraft(''); setAppliedSelection(''); }}
                  >
                    Show all
                  </button>
                </div>
              )}
              {selectionStatus && <span className="text-xs text-stone-400">{selectionStatus}</span>}
            </div>
            <p className="mt-2 text-xs text-stone-500">
              Drill into the list by position — the <span className="text-stone-300">#</span> column
              numbers the list <em>as you currently see it</em>. The three buttons here act on what&rsquo;s
              typed in the box; <span className="text-stone-300">Add</span> below does not.
              {' '}<span className="text-stone-300">Enter</span> narrows the view to those rows (it only
              filters, it doesn&rsquo;t schedule). To schedule the shown rows, use
              {' '}<span className="text-stone-300">Add</span> in the scheduling area below.
              {' '}<span className="text-stone-300">Remove from list</span> takes them out of the list
              you&rsquo;re working so they won&rsquo;t be called — they stay in the campaign, with their
              history, and <span className="text-stone-300">Restore all</span> puts them back.
              {' '}<span className="text-stone-300">Remove selected from schedule</span> just clears their
              times. <span className="text-stone-300">Delete selected</span> removes them from the
              campaign entirely and cannot be undone.
            </p>
          </div>
        )}

        {/* ── Scheduling ─────────────────────────────────────────────────────────
            Relocated ABOVE the contact list (was stacked below it). Collapsed by
            default (mirrors the Knowledge collapse pattern); expanding reveals two
            TABS — Auto-schedule (specific times) and Autonomous (rate-based). Only
            the active tab's controls render. All underlying state/handlers intact. */}
        <div className="mb-4 rounded-lg border border-stone-800">
          <button
            type="button"
            className="flex w-full items-center justify-between text-left px-4 py-3"
            onClick={() => setSchedulingOpen((o) => !o)}
            aria-expanded={schedulingOpen}
          >
            <span className="inline-flex items-center gap-2">
              {schedulingOpen ? <ChevronDown size={18} className="text-stone-400" /> : <ChevronRight size={18} className="text-stone-400" />}
              <CalendarClock size={16} className="text-alfred-gold" />
              <span className="text-base text-stone-100">Scheduling</span>
            </span>
            <span className="text-xs text-stone-500">{schedulingOpen ? 'Hide' : 'Show'}</span>
          </button>

          {schedulingOpen && (
          <div className="border-t border-stone-800 p-4 space-y-4">
            {/* Tab bar — pick a scheduling mode. Active = alfred-gold pill. */}
            <div className="inline-flex items-center gap-1 rounded-lg border border-stone-800 bg-stone-900/40 p-1">
              {[
                { key: 'auto', label: 'Auto-schedule', Icon: CalendarClock },
                { key: 'autonomous', label: 'Autonomous', Icon: Play }
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setScheduleTab(t.key)}
                  aria-pressed={scheduleTab === t.key}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs ${
                    scheduleTab === t.key
                      ? 'bg-alfred-gold/15 text-alfred-gold border border-alfred-gold/50'
                      : 'text-stone-400 hover:text-stone-200 border border-transparent'
                  }`}
                >
                  <t.Icon size={13} /> {t.label}
                </button>
              ))}
            </div>

            {/* Tab 1 — Auto-schedule: put contacts on specific times. */}
            {scheduleTab === 'auto' && (
            <div className="rounded-lg border border-stone-800 p-4">
              <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                <h4 className="text-base text-stone-100 inline-flex items-center gap-2">
                  <CalendarClock size={16} className="text-alfred-gold" />
                  Auto-schedule — schedule contacts at specific times
                </h4>
              </div>
              <p className="text-sm text-stone-400 mb-3">
                Lay out each contact's call times — spread the call column across a
                start time + interval, or queue a numbered selection at a chosen time.
              </p>

              <div className="mb-4 rounded-lg border border-alfred-gold/30 p-4">
                <p className="text-sm text-stone-300 mb-3">
                  Pre-fill the call column, spaced by the interval from the start time.
                  {' '}<span className="text-alfred-gold">Add schedules the whole list as loaded</span>;
                  apply a filter or a <span className="text-stone-100">Call entries</span> selection first
                  to narrow it to just those rows. Already-called contacts are put back in the queue
                  (do-not-call is never re-armed), and <span className="text-stone-100">Clear</span> undoes it.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    {/* Name the zone: the time entered here is the tenant's wall
                        clock, which is not necessarily the clock on the screen
                        the person is typing at. */}
                    <label className="field-label">
                      Start time {zoneKnown ? `(${zoneAbbreviation()})` : ''}
                    </label>
                    <input
                      type="datetime-local"
                      className="field-input"
                      value={scheduleDraft.start}
                      disabled={!canSchedule}
                      onChange={(e) => setScheduleDraft({ ...scheduleDraft, start: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="field-label">Minutes between calls</label>
                    <input
                      type="number" min="0" max="1440" className="field-input"
                      value={scheduleDraft.interval}
                      onChange={(e) => setScheduleDraft({ ...scheduleDraft, interval: e.target.value })}
                    />
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button type="button" className="btn-primary" disabled={!canSchedule} onClick={() => runAutoSchedule(1)}>Add</button>
                  {/* Clear stays enabled: unscheduling doesn't need a zone. */}
                  <button type="button" className="btn-ghost text-red-300 hover:text-red-200" onClick={() => clearColumn(1)}>Clear</button>
                  {scheduleStatus && <span className="text-xs text-stone-400">{scheduleStatus}</span>}
                </div>
                <p className="mt-2 text-xs text-stone-500">
                  “Add” overwrites any manual times already in the call column, for the rows it
                  targets (your selection/filter, else all queued). “Clear” always wipes the column
                  across all queued contacts — to clear just some, use “Remove selected from schedule”.
                </p>
              </div>

              {/* The "Call entries — drill down" box was moved OUT of here to sit
                  directly under the filter chips (it's a filter, not a scheduling
                  control). See the render above the Scheduling section. */}
            </div>
            )}

            {/* Tab 2 — Autonomous: start it and let it pace itself (rate fields).
                Same save + clear handlers. */}
            {scheduleTab === 'autonomous' && (
            <div className="rounded-lg border border-stone-800 p-4">
              <h4 className="text-base text-stone-100 inline-flex items-center gap-2 mb-1">
                <Play size={16} className="text-alfred-gold" />
                Autonomous — start it and let it pace itself (calls/minute, retries)
              </h4>
              <p className="text-sm text-stone-400 mb-3">
                No fixed times — Alfred dials continuously at this pace until the list is done.
              </p>
              {/* The old standalone "Use schedule" toggle lives here as the mode
                  switch: leave it on to dial by per-contact times (Auto-schedule
                  tab); turn it OFF to enable this rate-based autonomous pacing. The
                  useSchedule state + setField binding are preserved. */}
              <label className="flex items-start gap-2 cursor-pointer select-none mb-3">
                <input
                  type="checkbox"
                  className="mt-1 accent-alfred-gold"
                  checked={!useSchedule}
                  onChange={(e) => setField('useSchedule', !e.target.checked)}
                />
                <span>
                  <span className="inline-flex items-center gap-2 text-stone-100">
                    <Play size={14} className="text-alfred-gold" /> Run autonomously (rate-based)
                  </span>
                  <span className="block text-sm text-stone-400 mt-0.5">
                    Uncheck to instead dial strictly by each contact's 1st-call / 2nd-call
                    times (set via the Auto-schedule tab). When on, the rate fields below pace it.
                  </span>
                </span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className={useSchedule ? 'opacity-50' : ''}>
                  <label className="field-label">Calls / minute</label>
                  <input
                    type="number" min="1" max="60" className="field-input"
                    disabled={useSchedule}
                    value={campaign.callsPerMinute ?? ''}
                    onChange={(e) => setField('callsPerMinute', e.target.value === '' ? '' : Number(e.target.value))}
                  />
                </div>
                <div>
                  <label className="field-label">Max attempts</label>
                  <input
                    type="number" min="1" max="10" className="field-input"
                    value={campaign.maxAttempts ?? ''}
                    onChange={(e) => setField('maxAttempts', e.target.value === '' ? '' : Number(e.target.value))}
                  />
                </div>
                <div className={useSchedule ? 'opacity-50' : ''}>
                  <label className="field-label">Retry after (min)</label>
                  <input
                    type="number" min="5" max="1440" step="5" className="field-input"
                    disabled={useSchedule}
                    value={campaign.retryAfterMin ?? ''}
                    onChange={(e) => setField('retryAfterMin', e.target.value === '' ? '' : Number(e.target.value))}
                  />
                </div>
              </div>

              {/* Calling hours — the window autonomous pacing is allowed to dial in.
                  Only applies in autonomous mode (manual/per-contact times ignore
                  it), so it's dimmed when useSchedule is on. */}
              <div className={`mt-4 rounded-lg border border-stone-800 p-4 ${useSchedule ? 'opacity-50' : ''}`}>
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <CalendarClock size={16} className="text-alfred-gold" />
                    <span className="text-stone-100 text-sm font-medium">Calling hours</span>
                  </div>
                  <select
                    className="field-input max-w-[240px] py-1 text-sm"
                    value={callingHours.timezone}
                    disabled={useSchedule}
                    onChange={(e) => setCallingTz(e.target.value)}
                    title="The zone these calling hours are interpreted in"
                  >
                    {COMMON_TIMEZONES.some((o) => o.value === callingHours.timezone)
                      ? null
                      : <option value={callingHours.timezone}>{callingHours.timezone}</option>}
                    {COMMON_TIMEZONES.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-stone-500 mb-3">
                  Autonomous dialing only runs inside these windows. If no day is enabled,
                  it dials any time (still bounded by TCPA). Per-contact scheduled times
                  (manual mode) ignore this.
                </p>
                <div className="space-y-1.5">
                  {CALLING_DAYS.map(([key, label]) => {
                    const d = callingHours.days[key];
                    return (
                      <div key={key} className="grid grid-cols-12 items-center gap-2">
                        <label className="col-span-3 sm:col-span-2 inline-flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={!!d.enabled}
                            disabled={useSchedule}
                            onChange={(e) => setCallingDay(key, { enabled: e.target.checked })}
                            className="h-4 w-4 rounded border-stone-600 bg-stone-900 text-alfred-gold"
                          />
                          <span className="text-stone-300 w-9">{label}</span>
                        </label>
                        <input
                          type="time"
                          className="field-input col-span-4 sm:col-span-3 py-1 text-sm"
                          value={d.start}
                          disabled={useSchedule || !d.enabled}
                          onChange={(e) => setCallingDay(key, { start: e.target.value })}
                        />
                        <span className="col-span-1 text-center text-stone-500 text-sm">to</span>
                        <input
                          type="time"
                          className="field-input col-span-4 sm:col-span-3 py-1 text-sm"
                          value={d.end}
                          disabled={useSchedule || !d.enabled}
                          onChange={(e) => setCallingDay(key, { end: e.target.value })}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <button type="button" className="btn-primary" onClick={saveCampaign} disabled={saving}>
                  <Save size={14} /> {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={clearRateFields}
                  title="Clear Calls / minute, Max attempts, and Retry after — they persist as empty on Save"
                >
                  <Eraser size={14} /> Clear rate fields
                </button>
                {savedAt && <span className="text-xs text-stone-500">Saved {savedAt.toLocaleTimeString()}</span>}
              </div>
            </div>
            )}

            {/* Campaign actions — relocated out of the page header into the
                bottom of the Scheduling section. Outside the per-tab conditional
                so they show regardless of which tab is active. Handlers/props
                are preserved exactly as they were in the header. */}
            <div className="mt-4 flex gap-2 border-t border-stone-800 pt-4">
              <button type="button" className="btn-ghost" onClick={load} disabled={loading}>
                <RefreshCcw size={14} /> Refresh
              </button>
              <button type="button" className="btn-ghost" onClick={resetCampaign}
                title="Requeue every completed/failed contact so the campaign can be re-run">
                <RotateCcw size={14} /> Reset
              </button>
              {/* Deliberately LEFT of Start: the question "what will they hear" belongs
                  before the button that starts dialling, not after it. */}
              <button type="button" className="btn-ghost" onClick={() => setTestOpen((v) => !v)}
                title="Dial my number with this campaign's real opener, voice and briefing">
                <PhoneOutgoing size={14} /> Hear it first
              </button>
              {campaign.status !== 'active' ? (
                <button type="button" className="btn-primary" onClick={() => setStatus('active')}>
                  <Play size={14} /> Start
                </button>
              ) : (
                <button type="button" className="btn-ghost" onClick={() => setStatus('paused')}>
                  <Pause size={14} /> Pause
                </button>
              )}
            </div>
          </div>
          )}

          {testOpen && (
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-alfred-gold/30 bg-alfred-gold/5 px-3 py-2">
              <span className="text-sm text-stone-200">Call me with this campaign's pitch</span>
              <input
                className="field-input py-1 px-2 text-xs w-44"
                placeholder="+17372728567"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                aria-label="Number to place the test call to, E.164"
              />
              <button type="button" className="btn-primary" onClick={runCampaignTest} disabled={testing}>
                {testing ? 'Placing…' : 'Call me'}
              </button>
              <span className="text-xs text-stone-500">
                {toE164(testTo)
                  ? <>dials {formatPhone(toE164(testTo))} — your own phone, never a prospect</>
                  : 'your own phone — a rehearsal never dials a prospect'}
              </span>
              {testMsg && (
                <p className={`w-full text-xs ${testMsg.ok ? 'text-emerald-300' : 'text-amber-300'}`}>{testMsg.text}</p>
              )}
            </div>
          )}
        </div>

        {callsError && (
          <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{callsError}</p>
        )}

        {addOpen && (
          <div className="mb-4 rounded-lg border border-alfred-gold/30 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-stone-300">Add a single contact (great for testing).</p>
              <button type="button" className="text-stone-500 hover:text-stone-300" onClick={() => setAddOpen(false)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <input
                  className="field-input"
                  placeholder="+13462580006 or 3462580006"
                  value={addDraft.phone}
                  onChange={(e) => setAddDraft({ ...addDraft, phone: e.target.value })}
                  autoFocus
                />
                {addDraft.phone && (
                  (() => {
                    const e164 = normalizePhoneInput(addDraft.phone);
                    return e164 ? (
                      <p className="mt-1 text-xs text-emerald-300">Will dial: {e164}</p>
                    ) : (
                      <p className="mt-1 text-xs text-red-300">Not a valid number — need 10 US digits or full +E.164</p>
                    );
                  })()
                )}
              </div>
              <input
                className="field-input"
                placeholder="Name (optional)"
                value={addDraft.name}
                onChange={(e) => setAddDraft({ ...addDraft, name: e.target.value })}
              />
              <input
                className="field-input"
                placeholder="Company (optional)"
                value={addDraft.company}
                onChange={(e) => setAddDraft({ ...addDraft, company: e.target.value })}
              />
            </div>
            {allLists.length > 0 && (
              <div className="mt-2">
                <label className="text-xs text-stone-500 mr-2">Also save to list:</label>
                <select
                  className="field-input inline-block max-w-xs"
                  value={addDraft.alsoSaveToList}
                  onChange={(e) => setAddDraft({ ...addDraft, alsoSaveToList: e.target.value })}
                >
                  <option value="">— none —</option>
                  {allLists.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="mt-3 flex items-center gap-2">
              <button type="button" className="btn-primary" onClick={addOneContact}>Add to campaign</button>
              {addStatus && <span className="text-xs text-stone-400">{addStatus}</span>}
            </div>
          </div>
        )}

        {importOpen && (
          <div className="mb-4 rounded-lg border border-alfred-gold/30 p-4">
            <p className="text-sm text-stone-300 mb-2">
              Pull contacts from a saved list. Numbers already in this campaign
              will be skipped automatically.
            </p>
            {allLists.length === 0 ? (
              <p className="text-sm text-stone-500">
                No lists yet. <Link to="/lists" className="text-alfred-gold hover:underline">Create one →</Link>
              </p>
            ) : (
              <div className="flex items-center gap-2">
                <select
                  className="field-input flex-1"
                  value={selectedListId}
                  onChange={(e) => setSelectedListId(e.target.value)}
                >
                  <option value="">— pick a list —</option>
                  {allLists.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name} ({l.memberCount.toLocaleString()})
                    </option>
                  ))}
                </select>
                <button type="button" className="btn-primary" onClick={importFromList} disabled={!selectedListId}>
                  Import
                </button>
              </div>
            )}
            {importStatus && <p className="mt-2 text-xs text-stone-400">{importStatus}</p>}
          </div>
        )}

        {uploadOpen && (
          <div className="mb-4 rounded-lg border border-alfred-gold/30 p-4">
            <p className="text-sm text-stone-300 mb-2">
              CSV must have a header row. Recognized columns: <code>phone</code>{' '}
              (or <code>phoneE164</code>) — required, E.164 format like
              <code> +13462580006</code>; <code>name</code>, <code>company</code>,{' '}
              <code>email</code> — optional. Other columns are saved as metadata.
            </p>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])}
              className="block text-sm text-stone-300"
            />
            {uploadStatus && <p className="mt-2 text-xs text-stone-400">{uploadStatus}</p>}
          </div>
        )}

        {contacts.length === 0 ? (
          <p className="text-sm text-stone-500">
            No contacts yet. Upload a CSV to add some.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-stone-500 border-b border-stone-800">
                  {/* No select-all. The box in this column REMOVES a row from the
                      list, so a header version would mean "remove everything I'm
                      looking at" — a click that empties the screen, one keystroke
                      from a filter that took real work to build. Removal is
                      deliberately one row at a time. */}
                  <th className="px-3 py-2 w-6" aria-label="Remove from list"></th>
                  <th className="px-3 py-2 w-6"></th>
                  <th className="px-3 py-2 w-6"></th>
                  <th className="px-3 py-2 w-10 text-right">#</th>
                  {/* One BUSINESS column instead of Phone + Name + Company. Twelve
                      columns forced a horizontal scroll to read a single row, and the
                      three of them are one thing anyway — you read the company, then
                      who and what number underneath it. */}
                  <th className="px-3 py-2">Business</th>
                  {/* These boxes are typed in the tenant's zone, not the browser's —
                      so name it, but only once it's a real choice rather than a fallback. */}
                  <th className="px-3 py-2">Call{zoneKnown ? ` (${zoneAbbreviation()})` : ''}</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">
                    <button
                      type="button"
                      className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-stone-300 ${contactSort === 'priority' ? 'text-alfred-gold' : ''}`}
                      onClick={() => setContactSort('priority')}
                      title="Sort by Call response — conversations first, then voicemail, then couldn’t-reach"
                    >
                      Response <ArrowUpDown size={12} />
                    </button>
                  </th>
                  <th className="px-3 py-2"></th>
                  <th className="px-3 py-2">Last called</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((row) => {
                  if (row.type === 'group') {
                    // The pinned "🔥 Hot leads" header gets the amber/gold tier so
                    // it reads as the follow-up-priority section, distinct from the
                    // plain stone outcome/stage group headers.
                    const hotHeader = row.id === 'grp-hot-leads';
                    return (
                      <tr key={row.id} className={`border-b ${hotHeader ? 'border-amber-500/30 bg-amber-500/10' : 'border-stone-800 bg-stone-900/60'}`}>
                        <td colSpan={10} className="px-3 py-2">
                          <span className={`inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${hotHeader ? 'text-amber-300' : 'text-stone-300'}`}>
                            {row.label}
                            <span className={`badge text-[10px] ${hotHeader ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30' : 'bg-stone-800 text-stone-400 border border-stone-700'}`}>{row.count}</span>
                            {/* A count you can act on. The header used to be a dead
                                label: it could tell you five businesses were waiting on
                                an email and give you no way to reach them. */}
                            <Link
                              to="/leads"
                              className={`ml-1 normal-case tracking-normal font-normal underline-offset-2 hover:underline ${hotHeader ? 'text-amber-200/80 hover:text-amber-100' : 'text-stone-500 hover:text-alfred-gold'}`}
                              title="See every business in a follow-up stage, across all campaigns"
                            >
                              open in Leads →
                            </Link>
                          </span>
                        </td>
                      </tr>
                    );
                  }
                  const c = row.contact;
                  const open = expandedContactId === c.id;
                  const contactCalls = callsForContact(c);
                  // Three visual tiers on the contact row itself:
                  //  1. Hot lead   → amber/gold left-ring highlight + ⭐ marker.
                  //  2. Call back  → sky/blue left-ring (voicemail / callback req).
                  //  3. Everything else → default styling.
                  const hot = isHotLead(c);
                  const callback = !hot && isCallback(c);
                  // Best-effort answered SUB-TYPE for the sub-badge in the Response
                  // cell — only shown for answered contacts with a classifiable
                  // call (heuristic today; QA field #57 later). 'unclassified' and
                  // no-call rows show no sub-badge.
                  const subcat = resolveOutcome(c.outcome) === 'answered' ? contactSubcategory(c) : null;
                  const rowTier = hot
                    ? 'bg-amber-500/[0.07] shadow-[inset_3px_0_0_0] shadow-amber-400/70 hover:bg-amber-500/10'
                    : callback
                      ? 'bg-sky-500/[0.06] shadow-[inset_3px_0_0_0] shadow-sky-400/60 hover:bg-sky-500/10'
                      : 'hover:bg-stone-900/40';
                  return (
                  <Fragment key={c.id}>
                  <tr
                    className={`border-b border-stone-900 cursor-pointer ${rowTier}`}
                    onClick={() => setExpandedContactId(open ? null : c.id)}
                  >
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      {/* Ticking REMOVES this row from the list being worked (and
                          stops it being called). It never renders checked, because
                          a removed row isn't in the table any more — see removeFromList. */}
                      <input
                        type="checkbox"
                        checked={false}
                        onChange={() => removeFromList(c)}
                        title="Remove from this list — keeps the contact and its history in the campaign, but stops it being called"
                        aria-label={`Remove ${c.company || c.name || c.phoneE164} from this list`}
                      />
                    </td>
                    <td className="px-3 py-2 text-stone-500">
                      {contactCalls.length > 0
                        ? (open ? <ChevronDown size={16} /> : <ChevronRight size={16} />)
                        : <span className="inline-block w-4" />}
                    </td>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => toggleHotLead(c)}
                        className={hot ? 'text-amber-400 hover:text-amber-300' : 'text-stone-600 hover:text-stone-400'}
                        title={hot ? 'Hot lead — pinned to top. Click to unpin.' : 'Mark as hot lead — pins to top follow-up priority.'}
                        aria-label={hot ? 'Unmark hot lead' : 'Mark hot lead'}
                        aria-pressed={hot}
                      >
                        <Star size={16} fill={hot ? 'currentColor' : 'none'} />
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right text-xs tabular-nums text-stone-500">
                      {entryIndexById.get(c.id) ?? ''}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        {hot && <span title="Hot lead — top follow-up priority" aria-label="Hot lead">⭐</span>}
                        <span className="text-stone-200">{c.company || c.name || formatPhone(c.phoneE164)}</span>
                        {contactCalls.length > 0 && (
                          <span className="badge bg-stone-800 text-stone-400 border border-stone-700 text-[10px]">
                            {contactCalls.length} call{contactCalls.length === 1 ? '' : 's'}
                          </span>
                        )}
                        {hazmatOf(c) && (
                          <span
                            className="badge bg-amber-600/20 text-amber-200 border border-amber-600/40 text-[10px]"
                            title="Hazmat-authorized carrier (FMCSA)"
                          >
                            ☣️ Hazmat
                          </span>
                        )}
                        {/* Driver count — the qualifier for this vertical, so it belongs
                            on the row and not only in the expanded dossier. CDL rides in
                            the tooltip because the two diverge (128 drivers / 32 CDL is
                            real) and showing one number for both would be a quiet lie. */}
                        {driversOf(c) !== null && (
                          <span
                            className="badge bg-sky-500/15 text-sky-200 border border-sky-500/30 text-[10px]"
                            title={`${driversOf(c)} drivers${carrierProfileOf(c).totalCdl ? ` · ${carrierProfileOf(c).totalCdl} CDL` : ''} (FMCSA)`}
                          >
                            {driversOf(c)} drivers
                          </span>
                        )}
                      </div>
                      {/* Who and what number, subordinate to the business name.
                          THE EMAIL BELONGS HERE TOO. Brian, 2026-08-20: "I should be able to
                          see the email along with the business contact information once it is
                          given." Capturing an address is the objective of the whole campaign,
                          and until now the only place it surfaced was the Leads page — so the
                          row you are looking at when you review a call could not tell you
                          whether that call had actually achieved anything. The API already
                          returns it (listContacts does a bare findMany, no select), so this
                          was purely a rendering gap. Gold, because it is the win. */}
                      <div className="text-xs text-stone-500">
                        {[c.company ? c.name : null, formatPhone(c.phoneE164)].filter(Boolean).join(' · ')}
                        {c.email && (
                          <>
                            {' · '}
                            <span className="text-alfred-gold" title={c.email}>{c.email}</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="datetime-local"
                        className="field-input py-1 px-2 text-xs w-[170px]"
                        value={slotValue(c, 1)}
                        disabled={!canSchedule}
                        onChange={(e) => onSlotInput(c, 1, e.target.value)}
                        onBlur={() => commitSlot(c, 1)}
                        title={
                          !canSchedule
                            ? 'Set your timezone in Settings before scheduling calls'
                            : c.slot1Fired ? 'Call already dialed' : 'Schedule the call'
                        }
                      />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className="badge bg-stone-800 text-stone-300 border border-stone-700">{c.status}</span>
                      {(c.status === 'suppressed' || c.status === 'do_not_call' || c.outcome === 'do_not_call') && (
                        <span
                          className="ml-1 badge bg-red-600/20 text-red-200 border border-red-600/40 text-[10px] align-middle"
                          title="On the tenant Do-Not-Call list — suppressed before dialing."
                        >
                          DNC
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`badge ${OUTCOME_BADGE[resolveOutcome(c.outcome)] || OUTCOME_BADGE.other}`}>
                        {outcomeLabel(c.outcome)}
                      </span>
                      {subcat && subcat !== 'unclassified' && (
                        <span
                          className="ml-1 badge bg-stone-800 text-stone-300 border border-stone-600 text-[10px] align-middle"
                          title="Answered sub-type (best-effort from transcript until QA sub-classification lands)"
                        >
                          {ANSWERED_SUBCAT_LABEL[subcat]}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        {/* Stage MOVED to the expanded area (Brian, 2026-07-30): squeezed
                            into a table cell the dropdown was too narrow to read the
                            selected value. Only the re-dial action stays inline. */}
                        {/* Re-dial tomorrow at a different time of day. Needs a tenant
                            timezone (same gate as all scheduling). */}
                        {(() => {
                          const suppressed = ['do_not_call', 'suppressed'].includes(c.status);
                          return (
                            <button
                              type="button"
                              className="shrink-0 text-stone-500 hover:text-alfred-gold disabled:opacity-40 disabled:hover:text-stone-500"
                              onClick={() => scheduleCallback(c)}
                              disabled={!canSchedule || suppressed}
                              title={
                                suppressed
                                  ? 'On the do-not-call list — can’t schedule a callback'
                                  : canSchedule
                                    ? 'Schedule a callback — tomorrow, a different time of day than the last call'
                                    : 'Set your timezone in Settings before scheduling callbacks'
                              }
                              aria-label="Schedule a callback"
                            >
                              <CalendarClock size={14} />
                            </button>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-stone-500 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-between gap-2">
                        {(() => {
                          // Evidence-derived, so a callback that never wrote back to the
                          // row still shows up here. See callHistory().
                          const h = callHistory(c);
                          return (
                            <span>
                              {h.lastAt ? formatDateTime(h.lastAt) : ''}
                              {/* Attempts had its own column for a single digit. */}
                              {h.count > 0 && (
                                <span className="ml-1.5 text-xs text-stone-600 tabular-nums">
                                  ·&nbsp;{h.count} {h.count === 1 ? 'try' : 'tries'}
                                </span>
                              )}
                              {h.rowIsStale && (
                                <span
                                  className="ml-1.5 text-xs text-amber-300"
                                  title="A call happened after the date this contact record was last updated — the callback placed the call but never wrote back to the row. The date and count shown here come from the calls themselves."
                                >
                                  ·&nbsp;from calls
                                </span>
                              )}
                            </span>
                          );
                        })()}
                        <button
                          type="button"
                          className="text-stone-600 hover:text-red-300 shrink-0"
                          onClick={() => deleteOneContact(c)}
                          title="Delete this contact from the campaign"
                          aria-label="Delete contact"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {open && (
                    <tr className="border-b border-stone-900 bg-stone-950/40">
                      <td></td>
                      <td colSpan={9} className="px-3 py-4 space-y-3">
                        {/* TABS on the expanded row. Previously Notes, Follow-ups and
                            Briefing were three full panels stacked ABOVE the call
                            history — so on a contact with twenty calls the things you
                            act on were pushed off screen by the thing you read, and
                            Brian couldn't find them at all (2026-07-30). Labelled tabs
                            with counts make each one visible without any of them
                            occupying the page. */}
                        {(() => {
                          const tab = contactTab[c.id] || 'calls';
                          const pick = (t) => setContactTab((s2) => ({ ...s2, [c.id]: t }));
                          const noteCount = (c.outcomeNotes || '').trim() ? '●' : '';
                          const TABS = [
                            { key: 'calls', label: 'Calls', badge: contactCalls.length || '' },
                            { key: 'followups', label: 'Follow-ups', badge: '' },
                            { key: 'notes', label: 'Notes', badge: noteCount },
                            { key: 'briefing', label: 'What the AI is told', badge: '' }
                          ];
                          return (
                            <>
                              <div className="flex flex-wrap items-center gap-1 border-b border-stone-800 pb-2">
                                {TABS.map((t) => (
                                  <button
                                    key={t.key}
                                    type="button"
                                    onClick={() => pick(t.key)}
                                    className={`px-3 py-1.5 text-xs rounded-full transition ${
                                      tab === t.key
                                        ? 'bg-alfred-gold/20 text-alfred-gold'
                                        : 'text-stone-400 hover:text-stone-200'
                                    }`}
                                  >
                                    {t.label}
                                    {t.badge !== '' && t.badge != null && (
                                      <span className="ml-1.5 text-[10px] opacity-70">{t.badge}</span>
                                    )}
                                  </button>
                                ))}
                              </div>

                              {/* Stage + response correction. Moved out of the table row:
                                  the stage dropdown was too narrow there to read its own
                                  value, and there was NO way at all to fix a response the
                                  classifier got wrong. Full width, always on this tab. */}
                              <div className="flex flex-wrap items-end gap-4 rounded-lg border border-stone-800 bg-stone-900/30 p-3">
                                <label className="block">
                                  <span className="field-label text-xs">Sales stage</span>
                                  <span className="flex items-center gap-2">
                                    <select
                                      className="field-input py-1.5 px-2 text-sm min-w-[15rem]"
                                      value={c.salesStage || ''}
                                      onChange={(e) => setSalesStage(c, e.target.value)}
                                    >
                                      {SALES_STAGE_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                      ))}
                                    </select>
                                    {stageSavedId === c.id && (
                                      <span className="text-xs text-emerald-400 whitespace-nowrap">Saved</span>
                                    )}
                                  </span>
                                </label>
                                <label className="block">
                                  <span className="field-label text-xs">Response</span>
                                  <select
                                    className="field-input py-1.5 px-2 text-sm min-w-[15rem]"
                                    value={c.outcome || ''}
                                    onChange={(e) => setContactOutcome(c, e.target.value)}
                                    disabled={!outcomeEditable}
                                    title={outcomeEditable ? '' : 'Correcting a response needs a core update — see the note.'}
                                  >
                                    <option value="">Not set</option>
                                    {OUTCOME_GROUP_ORDER
                                      .filter((o) => o.key !== 'not_called')
                                      .map((o) => (
                                        <option key={o.key} value={o.key}>{o.label}</option>
                                      ))}
                                  </select>
                                  <span className="mt-1 block text-xs text-stone-500">
                                    {outcomeEditable
                                      ? 'The AI classifies this from the transcript. Change it when it got it wrong.'
                                      : 'Read-only for now — the API doesn’t accept a corrected response yet.'}
                                  </span>
                                </label>
                              </div>

                              {tab === 'notes' && (
                                <ContactNotes
                                  contact={c}
                                  canEdit={user?.role === 'admin'}
                                  onSave={async (patch) => {
                                    const r = await campaignsAPI.updateContact(id, c.id, patch);
                                    const updated = r?.data || r;
                                    setContacts((cs) => cs.map((x) => (x.id === c.id ? { ...x, ...updated } : x)));
                                  }}
                                />
                              )}
                              {tab === 'followups' && (
                                <ContactCallbacks
                                  campaignId={id}
                                  contact={c}
                                  canEdit={user?.role === 'admin'}
                                />
                              )}
                              {tab === 'briefing' && (
                                <ContactDossierEditor
                                  contact={c}
                                  canEdit={user?.role === 'admin'}
                                  onSave={async (patch) => {
                                    const r = await campaignsAPI.updateContact(id, c.id, patch);
                                    const updated = r?.data || r;
                                    // Replace just this row — `contacts` is a server list,
                                    // not an edit buffer, so a targeted swap is safe.
                                    setContacts((cs) => cs.map((x) => (x.id === c.id ? { ...x, ...updated } : x)));
                                  }}
                                />
                              )}
                            </>
                          );
                        })()}

                        {(contactTab[c.id] || 'calls') === 'calls' && (
                          <>
                        {contactCalls.length === 0 && (
                          <p className="text-sm text-stone-500">No calls for this contact yet.</p>
                        )}
                        {contactCalls.map((call) => (
                          <div key={call.id} className="rounded-lg border border-stone-800 bg-stone-900/40 p-3">
                            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                              <span className="text-xs text-stone-400">
                                {call.startedAt ? formatDateTime(call.startedAt) : '—'} · {formatDuration(call.durationSeconds)}
                              </span>
                              {call.outcome && (
                                <span className={`badge ${OUTCOME_BADGE[call.outcome] || OUTCOME_BADGE.other}`}>
                                  {outcomeLabel(call.outcome)}
                                </span>
                              )}
                            </div>
                            {call.recordingUrl && (
                              <div className="mb-3">
                                <p className="text-xs uppercase tracking-wider text-stone-500 mb-1">Recording</p>
                                <RecordingPlayer recordingUrl={call.recordingUrl} />
                              </div>
                            )}
                            {call.outcomeNotes && (
                              <div className="mb-3">
                                <p className="text-xs uppercase tracking-wider text-stone-500 mb-1">Follow-up notes</p>
                                <p className="text-sm text-stone-300 whitespace-pre-wrap">{call.outcomeNotes}</p>
                              </div>
                            )}
                            <p className="text-xs uppercase tracking-wider text-stone-500 mb-1">
                              Transcript{call.transcriptVerbatim ? ' — from the recording' : ''}
                            </p>
                            {call.transcriptVerbatim ? (
                              // Re-derived FROM THE AUDIO after the call (what was said).
                              // Beneath it, the AI lines the audio does NOT contain.
                              <div className="max-h-[28rem] overflow-y-auto rounded-lg border border-stone-800 bg-stone-900/50 p-3">
                                <pre className="text-sm text-stone-200 whitespace-pre-wrap font-sans leading-relaxed">{call.transcriptVerbatim}</pre>
                                {Array.isArray(call.transcriptVerbatimMeta?.aiTurns?.missing) && call.transcriptVerbatimMeta.aiTurns.missing.length > 0 && (
                                  <div className="mt-3 pt-2 border-t border-stone-800 text-xs text-stone-400">
                                    <p className="uppercase tracking-wider text-stone-500 mb-1">Generated by the AI but not in the recording</p>
                                    {call.transcriptVerbatimMeta.aiTurns.missing.map((m) => (
                                      <p key={m.index} className="line-through decoration-stone-600">{m.text}</p>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : Array.isArray(call.displayTurns) && call.displayTurns.length > 0 ? (
                              // The READABLE projection of the turn rows (tokens stripped,
                              // [Name] → speaker, injected directives dropped, STT fragments
                              // joined). A line the box marked NOT SPOKEN stays, struck through.
                              <div className="max-h-[28rem] overflow-y-auto rounded-lg border border-stone-300 bg-stone-50 p-3 space-y-2">
                                {call.displayTurns.map((t, i) => {
                                  const isCaller = t.role === 'user';
                                  return (
                                    <div key={i} className={isCaller ? 'flex justify-start' : 'flex justify-end'}>
                                      <div className={`max-w-[85%] rounded-lg px-3 py-2 ${isCaller ? 'bg-white text-stone-900 border border-stone-300' : 'bg-amber-100 text-stone-900 border border-amber-300'} ${t.delivered === false ? 'opacity-60' : ''}`}>
                                        <span className={`block text-[10px] uppercase tracking-wider mb-0.5 ${isCaller ? 'text-stone-500' : 'text-amber-700'}`}>
                                          {t.speaker || (isCaller ? 'Caller' : 'AI')}
                                          {t.delivered === false ? ` · not spoken (${t.undeliveredReason || 'unknown'})` : ''}
                                          {t.cutOff ? ` · cut off (${t.cutOff})` : ''}
                                        </span>
                                        <span className={`text-sm whitespace-pre-wrap leading-relaxed ${t.delivered === false ? 'line-through' : ''}`}>{t.text}</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : Array.isArray(call.turns) && call.turns.length > 0 ? (
                              // Role-labeled bubbles built from the turn-level
                              // `conversation` rows (the source of truth). Caller
                              // (the business owner we dialed) vs AI (Jay/panel).
                              <div className="max-h-[28rem] overflow-y-auto rounded-lg border border-stone-800 bg-stone-900/50 p-3 space-y-2">
                                {call.turns.map((t, i) => {
                                  const isCaller = t.role === 'user';
                                  return (
                                    <div key={i} className={isCaller ? 'flex justify-start' : 'flex justify-end'}>
                                      {/* Caller bubble: a neutral slate/stone with a
                                          light border + bright text so it's clearly
                                          legible (the old bg-stone-800/80 read as a
                                          muddy brown against the gold palette and
                                          made caller text hard to read). AI side
                                          keeps the gold-tinted bubble. */}
                                      <div className={`max-w-[85%] rounded-lg px-3 py-2 ${isCaller ? 'bg-stone-700/50 text-stone-50 border border-stone-600/50' : 'bg-alfred-gold/10 text-stone-100 border border-alfred-gold/20'}`}>
                                        <span className={`block text-[10px] uppercase tracking-wider mb-0.5 ${isCaller ? 'text-stone-300' : 'text-alfred-gold/80'}`}>
                                          {isCaller ? 'Caller' : 'AI'}
                                        </span>
                                        <span className="text-sm whitespace-pre-wrap leading-relaxed">{t.content}</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : call.transcript ? (
                              <pre className="text-sm text-stone-200 whitespace-pre-wrap font-sans leading-relaxed max-h-[28rem] overflow-y-auto rounded-lg border border-stone-800 bg-stone-900/50 p-3">
                                {call.transcript}
                              </pre>
                            ) : (
                              (() => {
                                const r = missingTranscriptReason(call);
                                return (
                                  <p className={r.tone === 'alarm' ? 'text-sm text-amber-300' : 'text-sm text-stone-500'}>
                                    {r.text}
                                  </p>
                                );
                              })()
                            )}
                            {/* Owner feedback / learning-loop: coach this call →
                                compiled into the COACHING PLAYBOOK injected into
                                the outbound agent's prompt on the next calls. */}
                            <CallFeedbackPanel callId={call.id} />
                          </div>
                        ))}
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
          {totalContactsShown > 0 && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-stone-400">
              <span>
                {paginated
                  ? `Showing ${pageStart + 1}–${Math.min(pageStart + pageSize, totalContactsShown)} of ${totalContactsShown}`
                  : `${totalContactsShown} ${totalContactsShown === 1 ? 'entry' : 'entries'}`}
              </span>
              <div className="flex items-center gap-2">
                <label className="text-xs text-stone-500">Per page</label>
                <select
                  className="field-input py-1 px-2 text-xs"
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={0}>All</option>
                </select>
                {paginated && (
                  <>
                    <button type="button" className="btn-ghost py-1" disabled={curPage === 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}>Prev</button>
                    <span className="text-xs tabular-nums">Page {curPage + 1} / {totalPages}</span>
                    <button type="button" className="btn-ghost py-1" disabled={curPage >= totalPages - 1}
                      onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}>Next</button>
                  </>
                )}
              </div>
            </div>
          )}
          </div>
        )}
        </>)}
      </section>
      )}
        </div>
      </div>
      {/* One dialog for the WHOLE PAGE. ⛔ Must live inside CampaignDetail —
          v1.62.0 put it in ContactDossier, where `dialogProps` is not in scope,
          so the page that CALLS confirm() rendered no dialog and every promise
          hung: Clear, Reset and Delete silently did nothing. */}
      <ConfirmDialog {...dialogProps} />
    </div>
  );
}

// ─── Campaign call History (read-only) ───────────────────────────────────────
// A log of calls actually PLACED, newest first — the counterpart to the
// scheduling-oriented Contacts view. Reuses the same recording/transcript
// rendering. Monitor-review (call.qa) shows when that lands in the calls payload
// (rides the core reconcile); until then the block is simply omitted.
const HISTORY_ANSWERED = {
  owner_reached:   { emoji: '🎯', label: 'Owner reached' },
  gatekeeper_only: { emoji: '🚪', label: 'Gatekeeper' },
  reached_ai:      { emoji: '🤖', label: 'Their AI' },
  voicemail:       { emoji: '📨', label: 'Voicemail' },
  ivr:             { emoji: '🔢', label: 'IVR / menu' },
  hung_up:         { emoji: '📴', label: 'Hung up' }
};

// A contact's city, read from its metadata (uploaded CSV columns land there).
// Tolerant of header variants; '' when absent. Used by the campaign city filter.
function cityOf(contact) {
  const m = contact?.metadata && typeof contact.metadata === 'object' ? contact.metadata : {};
  for (const k of Object.keys(m)) {
    if (/^(city|town|municipality)$/i.test(k.trim())) {
      const v = m[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
  }
  return '';
}

// TX metro bucketing for the contact filter. AREA-CODE-PRIMARY (Brian 2026-07-24):
// the metadata `city` string is dirty — e.g. 97 "Westlake" contacts have no
// state/zip and their PHONE area codes scatter across all 4 metros (they aren't one
// place; 2 are even Westlake OH). The contact's phone area code is the reliable
// geographic signal for these business landlines (validated in prod: houston→
// 713/281/832, plano→972/469/214, katy→281/832/346, SA→210/726 — all agree). So:
//   1. area code in a metro-exclusive TX code → that metro (authoritative);
//   2. area code is a valid geographic code but NOT a TX metro → 'Other';
//   3. area code STRADDLES a metro boundary (940 Denton|Wichita Falls, 830 Boerne|
//      Kerrville, 936 Conroe|Huntsville, 409 Galveston|Beaumont) OR is toll-free OR
//      absent → defer to the city map to disambiguate.
// The city map (below) is now the FALLBACK/disambiguator, not the primary key.
const METRO_ORDER = ['Houston', 'Dallas', 'Austin', 'San Antonio', 'Other'];

// Metro-exclusive area codes → resolve straight to the metro (case 1).
const AREACODE_METRO = {
  '713': 'Houston', '281': 'Houston', '832': 'Houston', '346': 'Houston',
  '214': 'Dallas', '469': 'Dallas', '972': 'Dallas', '945': 'Dallas', '817': 'Dallas', '682': 'Dallas',
  '512': 'Austin', '737': 'Austin',
  '210': 'San Antonio', '726': 'San Antonio',
};
// Area codes that cover BOTH a metro's suburbs and far-out towns → don't trust the
// code; let the city map decide (case 3). Toll-free carries no geography → same.
const AMBIGUOUS_AREACODES = new Set(['940', '830', '936', '409']);
const TOLLFREE_AREACODES = new Set(['800', '888', '877', '866', '855', '844', '833', '822']);

// City → metro map, the fallback/disambiguator (core + suburbs within ~50mi).
const TX_METROS = {
  Houston: ['houston', 'katy', 'sugar land', 'the woodlands', 'pearland', 'pasadena', 'spring', 'cypress', 'humble', 'conroe', 'league city', 'friendswood', 'missouri city', 'baytown', 'tomball', 'richmond', 'rosenberg', 'stafford', 'bellaire', 'deer park', 'la porte', 'channelview', 'kingwood', 'atascocita', 'webster', 'seabrook', 'dickinson', 'texas city', 'galveston'],
  Dallas: ['dallas', 'plano', 'frisco', 'mckinney', 'allen', 'richardson', 'garland', 'irving', 'arlington', 'fort worth', 'ft worth', 'grand prairie', 'mesquite', 'carrollton', 'denton', 'lewisville', 'flower mound', 'mansfield', 'rowlett', 'euless', 'bedford', 'grapevine', 'coppell', 'wylie', 'desoto', 'cedar hill', 'rockwall', 'prosper', 'little elm', 'the colony', 'southlake', 'keller', 'north richland hills', 'hurst', 'addison', 'farmers branch', 'duncanville', 'waxahachie', 'burleson'],
  Austin: ['austin', 'round rock', 'cedar park', 'georgetown', 'pflugerville', 'leander', 'kyle', 'buda', 'san marcos', 'hutto', 'lakeway', 'bee cave', 'west lake hills', 'westlake', 'dripping springs', 'manor', 'elgin'],
  'San Antonio': ['san antonio', 'new braunfels', 'schertz', 'boerne', 'stone oak', 'converse', 'cibolo', 'universal city', 'live oak', 'seguin', 'helotes', 'leon valley', 'windcrest', 'alamo heights'],
};
// Reverse index (normalized city name → metro), built once at module load.
const CITY_TO_METRO = Object.entries(TX_METROS).reduce((idx, [metro, cities]) => {
  for (const c of cities) idx[c] = metro;
  return idx;
}, {});

// The contact's 3-digit area code from phoneE164 ('' if unparseable/missing).
function areaCodeOf(contact) {
  const digits = String(contact?.phoneE164 || '').replace(/\D/g, '');
  const ten = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
  return ten.length === 10 ? ten.slice(0, 3) : '';
}

// A contact's state from metadata ('' if absent), uppercased. ~60% present in prod.
function stateOf(contact) {
  const m = contact?.metadata && typeof contact.metadata === 'object' ? contact.metadata : {};
  for (const k of Object.keys(m)) {
    if (/^state$/i.test(k.trim())) {
      const v = m[k];
      if (typeof v === 'string' && v.trim()) return v.trim().toUpperCase();
    }
  }
  return '';
}

// City-map bucket: metro when the city is a known metro/suburb, 'Other' when a city
// is present but unmapped, '' when there's no city at all. The disambiguator.
function cityMetroOf(contact) {
  // An explicit NON-TX state kills any TX-metro match — guards city-name collisions
  // like "Westlake, OH" that would otherwise map to Austin. state is ~60% present;
  // when absent we can't guard, but the area-code stage already handles most
  // out-of-state contacts (a 419/440 OH number → 'Other' before reaching here).
  const st = stateOf(contact);
  if (st && st !== 'TX' && st !== 'TEXAS') return 'Other';
  const city = cityOf(contact);
  if (!city) return '';
  const norm = city.toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ').trim();
  return CITY_TO_METRO[norm] || 'Other';
}

// A contact's TX metro, AREA-CODE-PRIMARY (see the header comment above).
function metroOf(contact) {
  const ac = areaCodeOf(contact);
  if (ac && AREACODE_METRO[ac]) return AREACODE_METRO[ac];        // case 1
  if (ac && !AMBIGUOUS_AREACODES.has(ac) && !TOLLFREE_AREACODES.has(ac)) return 'Other'; // case 2
  return cityMetroOf(contact);                                    // case 3 (defer to city)
}

// ── FMCSA carrier profile (freight campaigns) ────────────────────────────────
// The re-import (core/scripts/reimport-carriers-houston.mjs) writes these keys onto
// each contact's metadata: hazmat ('Y'|''), cargo_types, cargo_other, total_cdl,
// trucks, drivers, years, safety_rating, etc. "Endorsements" for a carrier list maps
// to hazmat authority (per-driver CDL endorsements aren't published per company).

// Does the contact's metadata carry a given key at all (even if blank)? Used to
// feature-detect whether a campaign has been FMCSA-enriched yet.
function metaHasKey(contact, key) {
  const m = contact?.metadata && typeof contact.metadata === 'object' ? contact.metadata : null;
  return !!m && Object.prototype.hasOwnProperty.call(m, key);
}

// Hazmat-authorized? metadata.hazmat === 'Y' (case-insensitive; tolerates 'yes').
function hazmatOf(contact) {
  const m = contact?.metadata && typeof contact.metadata === 'object' ? contact.metadata : {};
  const v = String(m.hazmat ?? '').trim().toLowerCase();
  return v === 'y' || v === 'yes' || v === 'true';
}

// Compact carrier profile for row/dossier display. Strings kept as-is; '' when absent.
function carrierProfileOf(contact) {
  const m = contact?.metadata && typeof contact.metadata === 'object' ? contact.metadata : {};
  const s = (k) => (typeof m[k] === 'string' ? m[k].trim() : (m[k] != null ? String(m[k]) : ''));
  return {
    hazmat: hazmatOf(contact),
    cargoTypes: s('cargo_types') || s('cargo_other'),
    drivers: s('drivers'),
    totalCdl: s('total_cdl'),
    trucks: s('trucks'),
    safetyRating: s('safety_rating')
  };
}

/**
 * Driver count — the qualifier for the carrier vertical.
 *
 * ⛔ `drivers` AND `total_cdl` ARE NOT THE SAME NUMBER and must not be conflated.
 * Century Transportation is 128 drivers / 32 CDL in prod. Sorting on the wrong one
 * quietly reorders the whole call list, and on this vertical the order IS the
 * targeting — Alpha nearly retired the five largest driver employers on a
 * name heuristic (Key Energy 206, Siddons Martin 191, Sterling Crane 126).
 *
 * Returns null (not 0) when absent, so "no data" sorts distinctly from "zero
 * drivers" instead of both sinking to the bottom as if they meant the same thing.
 */
function driversOf(contact) {
  const m = contact?.metadata && typeof contact.metadata === 'object' ? contact.metadata : {};
  const raw = String(m.drivers ?? '').trim();
  if (!/^\d+$/.test(raw)) return null;
  return Number(raw);
}

// FMCSA cargo-class code → friendly label (mirrors CARGO_LABEL in
// core/scripts/reimport-carriers-houston.mjs — keep in sync). The 24 crgo_* classes
// are the carrier-level analogue to CDL endorsements (what they're authorized to haul).
const CARGO_LABEL = {
  genfreight: 'general freight', metalsheet: 'metal/sheet', motoveh: 'motor vehicles',
  logpole: 'logs/poles', bldgmat: 'building materials', machlrg: 'heavy machinery',
  liqgas: 'liquids/gas', intermodal: 'intermodal containers', oilfield: 'oilfield equipment',
  grainfeed: 'grain/feed', garbage: 'refuse', chem: 'chemicals', drybulk: 'dry bulk',
  coldfood: 'refrigerated', beverages: 'beverages', paperprod: 'paper products',
  construct: 'construction', household: 'household goods', produce: 'produce',
  utility: 'utility', mobilehome: 'mobile homes', cargoothr: 'other'
};
const cargoLabel = (code) => CARGO_LABEL[code] || code;

// A carrier's cargo-class codes from metadata.cargo_types (comma-separated, as the
// reimport writes it) → array of trimmed codes ([] when absent).
function cargoClassesOf(contact) {
  const m = contact?.metadata && typeof contact.metadata === 'object' ? contact.metadata : {};
  return String(m.cargo_types ?? '').split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
}

// Read the dossier fields out of a contact's metadata JSON, tolerant of key
// variants until Charlie's #3 pins the canonical names. REFERENCE data the AI uses
// to ANSWER questions (e.g. a gatekeeper asks who we're calling for) — never to
// lead with or proactively ask for the owner.
function readDossier(metadata) {
  const m = metadata && typeof metadata === 'object' ? metadata : {};
  const pick = (...keys) => {
    for (const k of keys) {
      const v = m[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
    return '';
  };
  return {
    owner: pick('owner', 'ownerName', 'owner_name', 'decisionMaker'),
    industry: pick('industry', 'vertical', 'category'),
    notes: pick('notes', 'context', 'businessContext', 'business_context', 'research', 'dossier')
  };
}

// Per-contact "Reference (dossier)" card. READ-ONLY for now — editing is wired once
// Charlie's task #3 confirms the canonical metadata keys + the contact-PUT write
// path (shipping an editor whose save silently drops would be exactly the ghosting
// bug we audited against).
function ContactDossier({ contact }) {
  const d = readDossier(contact?.metadata);
  const has = d.owner || d.industry || d.notes;
  // FMCSA carrier profile — only rendered when the contact carries the enriched
  // fields (freight campaigns post-reimport). Read-only, like the dossier.
  const carrier = metaHasKey(contact, 'hazmat') ? carrierProfileOf(contact) : null;
  const hasCarrier = carrier && (carrier.hazmat || carrier.cargoTypes || carrier.totalCdl || carrier.trucks || carrier.safetyRating);
  return (
    <div className="rounded-lg border border-stone-800 bg-stone-900/30 p-3">
      <div className="flex items-center gap-2 mb-1">
        <MessageSquareText size={14} className="text-stone-400" />
        <h4 className="text-sm font-medium text-stone-200">Reference (dossier)</h4>
      </div>
      <p className="text-xs text-stone-500 mb-3">
        Reference only — the AI uses this to <em>answer</em> questions (e.g. a gatekeeper asks who
        we're calling for). It never leads with it or proactively asks for the owner.
      </p>
      {has ? (
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div><dt className="text-xs uppercase tracking-wider text-stone-500">Owner</dt><dd className="text-stone-200">{d.owner || '—'}</dd></div>
          <div><dt className="text-xs uppercase tracking-wider text-stone-500">Industry</dt><dd className="text-stone-200">{d.industry || '—'}</dd></div>
          <div className="sm:col-span-3"><dt className="text-xs uppercase tracking-wider text-stone-500">Notes / context</dt><dd className="text-stone-300 whitespace-pre-wrap">{d.notes || '—'}</dd></div>
        </dl>
      ) : !hasCarrier ? (
        <p className="text-sm text-stone-500">No reference info for this contact yet.</p>
      ) : null}
      {hasCarrier && (
        <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm border-t border-stone-800 pt-3">
          <div>
            <dt className="text-xs uppercase tracking-wider text-stone-500">Endorsements</dt>
            <dd className={carrier.hazmat ? 'text-amber-200' : 'text-stone-300'}>{carrier.hazmat ? '☣️ Hazmat authorized' : 'None (non-hazmat)'}</dd>
          </div>
          <div><dt className="text-xs uppercase tracking-wider text-stone-500">Cargo</dt><dd className="text-stone-300">{cargoClassesOf(contact).map(cargoLabel).join(', ') || carrier.cargoTypes || '—'}</dd></div>
          {/* Drivers and CDL drivers are DIFFERENT counts (128 / 32 is real in prod).
              Both are shown so nobody has to guess which one the sort used. */}
          <div><dt className="text-xs uppercase tracking-wider text-stone-500">Drivers</dt><dd className="text-stone-300">{carrier.drivers || '—'}</dd></div>
          <div><dt className="text-xs uppercase tracking-wider text-stone-500">CDL drivers</dt><dd className="text-stone-300">{carrier.totalCdl || '—'}</dd></div>
          <div><dt className="text-xs uppercase tracking-wider text-stone-500">Trucks</dt><dd className="text-stone-300">{carrier.trucks || '—'}</dd></div>
        </dl>
      )}
    </div>
  );
}

// The AI's actual opening line on a call = its first ASSISTANT turn (prefer the
// spoken 'assistant' role over any 'system'/tool turn); fall back to the first
// non-caller turn, then to the intended opener stamped at originate.
function firstAiLine(call) {
  const turns = Array.isArray(call.turns) ? call.turns : [];
  const t = turns.find((x) => x.role === 'assistant') || turns.find((x) => x.role && x.role !== 'user');
  return (t?.content || call.openingLine || '').trim();
}

function CampaignHistory({ calls, contacts }) {
  const [open, setOpen] = useState(() => new Set());
  const [full, setFull] = useState(false);
  // Esc closes the fullscreen overlay (in addition to the Close/Collapse buttons).
  useEffect(() => {
    if (!full) return;
    const onKey = (e) => { if (e.key === 'Escape') setFull(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [full]);
  // Time-frame filter (tenant-zone calendar dates, inclusive). Empty = unbounded.
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const toggle = (cid) => setOpen((s) => {
    const n = new Set(s);
    if (n.has(cid)) n.delete(cid); else n.add(cid);
    return n;
  });

  // phone (trailing 10 digits) → contact, for name/company on each call row.
  const byPhone = new Map();
  for (const c of contacts || []) {
    const key = String(c.phoneE164 || '').replace(/\D/g, '').slice(-10);
    if (key && !byPhone.has(key)) byPhone.set(key, c);
  }
  const contactFor = (call) => {
    const key = String(call.toNumber || call.fromNumber || '').replace(/\D/g, '').slice(-10);
    return key ? byPhone.get(key) : null;
  };

  // Calls actually placed, newest first.
  const made = (calls || [])
    .filter((c) => c.startedAt || c.outcome || c.recordingUrl || c.transcript)
    .slice()
    .sort((a, b) => new Date(b.startedAt || 0) - new Date(a.startedAt || 0));

  // The call's calendar date in the TENANT zone ("YYYY-MM-DD"), for the range filter.
  const callDate = (call) => (call.startedAt ? isoToZonedInput(call.startedAt).slice(0, 10) : '');
  const rows = made.filter((call) => {
    if (!from && !to) return true;
    const d = callDate(call);
    // A call with no startedAt has no date to place in the range, so it drops out
    // once a range is set (it shows again when the filter is cleared).
    if (!d) return false;
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  });

  const COLS = 7;
  const zone = zoneAbbreviation();

  const body = (
    <div className="space-y-3">
      {/* Controls: time-frame filter + expand toggle. */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-stone-400">
          {rows.length} of {made.length} call{made.length === 1 ? '' : 's'}
        </span>
        <div className="flex items-center gap-2 text-xs text-stone-400">
          <span className="uppercase tracking-wider text-stone-500">From</span>
          <input type="date" className="field-input py-1 px-2 text-sm" value={from} onChange={(e) => setFrom(e.target.value)} />
          <span className="uppercase tracking-wider text-stone-500">to</span>
          <input type="date" className="field-input py-1 px-2 text-sm" value={to} onChange={(e) => setTo(e.target.value)} />
          {(from || to) && (
            <button type="button" className="underline hover:text-alfred-gold" onClick={() => { setFrom(''); setTo(''); }}>
              clear
            </button>
          )}
          <span className="text-stone-600">· times in {zone}</span>
        </div>
        <button
          type="button"
          className="btn-ghost py-1 ml-auto"
          onClick={() => setFull((v) => !v)}
          title={full ? 'Collapse the table' : 'Expand the table to fill the window'}
        >
          {full ? <><Minimize2 size={14} /> Collapse</> : <><Maximize2 size={14} /> Expand</>}
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-stone-500">
          {made.length === 0
            ? 'No calls placed yet — they appear here once the campaign dials.'
            : 'No calls in that date range.'}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-stone-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-stone-500 border-b border-stone-800">
                <th className="px-3 py-2 whitespace-nowrap">Time ({zone})</th>
                <th className="px-3 py-2">Business</th>
                <th className="px-3 py-2 whitespace-nowrap">AI voice</th>
                <th className="px-3 py-2 min-w-[16rem]">Pitch (opening)</th>
                <th className="px-3 py-2 whitespace-nowrap">What happened</th>
                <th className="px-3 py-2 whitespace-nowrap">Outcome</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">Duration</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((call) => {
                const c = contactFor(call);
                const sub = callSubcategory(call);
                const pitch = firstAiLine(call);
                const isOpen = open.has(call.id);
                return (
                  <Fragment key={call.id}>
                    <tr
                      className="border-b border-stone-900 hover:bg-stone-800/40 cursor-pointer align-top"
                      onClick={() => toggle(call.id)}
                    >
                      <td className="px-3 py-2 whitespace-nowrap text-stone-400">
                        <span className="inline-flex items-center gap-1">
                          {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                          {call.startedAt ? formatDateTime(call.startedAt) : '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-stone-200">
                        {c?.name || c?.company || formatPhone(call.toNumber || call.fromNumber || '') || '—'}
                        {c?.company && c?.name ? <span className="block text-xs text-stone-500">{c.company}</span> : null}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-stone-300">{call.voiceName || call.voiceId || '—'}</td>
                      <td className="px-3 py-2 text-stone-300 max-w-[28rem]">
                        <span className="block truncate" title={pitch || undefined}>{pitch || <span className="text-stone-600">—</span>}</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="badge bg-stone-800 text-stone-300 border border-stone-700">
                          {ANSWERED_SUBCAT_LABEL[sub] || 'Unclassified'}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {call.outcome
                          ? <span className={`badge ${OUTCOME_BADGE[call.outcome] || OUTCOME_BADGE.other}`}>{outcomeLabel(call.outcome)}</span>
                          : <span className="text-stone-600">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap text-stone-400 tabular-nums">{formatDuration(call.durationSeconds)}</td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b border-stone-900 bg-stone-950/40">
                        <td colSpan={COLS} className="px-3 py-3">
                          <div className="space-y-3">
                            {call.recordingUrl && (
                              <div>
                                <p className="text-xs uppercase tracking-wider text-stone-500 mb-1">Recording</p>
                                <RecordingPlayer recordingUrl={call.recordingUrl} />
                              </div>
                            )}
                            {call.qa && (
                              <div className="rounded-lg border border-stone-800 bg-stone-900/40 p-3">
                                <p className="text-xs uppercase tracking-wider text-stone-500 mb-1">
                                  Monitor review{call.qa.overallScore != null ? ` · ${call.qa.overallScore}/5` : ''}
                                </p>
                                {call.qa.summary
                                  ? <p className="text-sm text-stone-300 whitespace-pre-wrap">{call.qa.summary}</p>
                                  : <p className="text-sm text-stone-500">No summary saved.</p>}
                              </div>
                            )}
                            <div>
                              <p className="text-xs uppercase tracking-wider text-stone-500 mb-1">Transcript</p>
                              {Array.isArray(call.turns) && call.turns.length > 0 ? (
                                <div className="max-h-[24rem] overflow-y-auto rounded-lg border border-stone-800 bg-stone-900/50 p-3 space-y-2">
                                  {call.turns.map((t, i) => {
                                    const isCaller = t.role === 'user';
                                    return (
                                      <div key={i} className={isCaller ? 'flex justify-start' : 'flex justify-end'}>
                                        <div className={`max-w-[85%] rounded-lg px-3 py-2 ${isCaller ? 'bg-stone-700/50 text-stone-50 border border-stone-600/50' : 'bg-alfred-gold/10 text-stone-100 border border-alfred-gold/20'}`}>
                                          <span className={`block text-[10px] uppercase tracking-wider mb-0.5 ${isCaller ? 'text-stone-300' : 'text-alfred-gold/80'}`}>{isCaller ? 'Caller' : 'AI'}</span>
                                          <span className="text-sm whitespace-pre-wrap leading-relaxed">{t.content}</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : call.transcript ? (
                                <pre className="text-sm text-stone-200 whitespace-pre-wrap font-sans leading-relaxed max-h-[24rem] overflow-y-auto rounded-lg border border-stone-800 bg-stone-900/50 p-3">{call.transcript}</pre>
                              ) : (
                                (() => {
                                const r = missingTranscriptReason(call);
                                return (
                                  <p className={r.tone === 'alarm' ? 'text-sm text-amber-300' : 'text-sm text-stone-500'}>
                                    {r.text}
                                  </p>
                                );
                              })()
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Expanded = a full-window overlay so the wide table has room to breathe.
  if (full) {
    return (
      <div className="fixed inset-0 z-50 bg-stone-950 overflow-auto p-4 sm:p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-stone-100">Call history</h2>
          <button type="button" className="btn-ghost" onClick={() => setFull(false)}>
            <Minimize2 size={14} /> Close
          </button>
        </div>
        {body}
      </div>
    );
  }
  return body;
}

// Human-readable call duration from a seconds count (or null). "—" when unknown.
export function formatDuration(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  const s = Math.max(0, Math.round(Number(seconds)));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
}

// Strict phone normalizer for the Add Contact form. Assumes US when
// only digits are typed. Returns null for anything that isn't a valid
// E.164. Crucial: rejects 9-digit / 11-non-1-prefix / other malformed
// US inputs that a permissive regex would let through.
export function normalizePhoneInput(input) {
  const trimmed = (input || '').trim();
  if (!trimmed) return null;
  // Full E.164: keep as-is if it parses cleanly (allows non-US numbers).
  if (trimmed.startsWith('+')) {
    const e164 = trimmed.replace(/[^\d+]/g, '');
    return /^\+[1-9]\d{7,14}$/.test(e164) ? e164 : null;
  }
  // Bare digits — strip formatting, normalize to US.
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) return '+1' + digits;
  if (digits.length === 11 && digits.startsWith('1')) return '+' + digits;
  return null;
}

// Convert an ISO timestamp (or null) to the value an <input type="datetime-local">
// expects: a zone-less wall-clock string "YYYY-MM-DDTHH:mm".
//
// The clock is the TENANT's, not the browser's — a campaign dials on the
// account's zone, so "9:10am" has to mean 9:10am there no matter where the
// person scheduling it is sitting. This and the parse on save (zonedInputToIso)
// are an inverse pair: change one and every scheduled call silently shifts by the
// browser/tenant offset, with nothing raised.
export function isoToTenantInput(iso) {
  return isoToZonedInput(iso);
}

// NOTE: the curated ElevenLabs VOICE_OPTIONS list that used to live here has been
// folded into the shared, vendor-aware voice catalog (core/src/lib/voiceCatalog.js,
// served at GET /voices/catalog) and is now rendered by <VoicePicker>. That single
// source backs BOTH this campaign Voice field and the Personas roster editor.

// Minimal CSV parser — handles quoted/unquoted, comma sep, CRLF/LF.
function parseCsv(text) {
  const rows = [];
  let cur = [], field = '', inQ = false;
  const pushRow = () => { if (cur.length > 1 || (cur[0] && cur[0].length > 0)) rows.push(cur); cur = []; };
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else { inQ = false; }
      } else field += ch;
    } else {
      if (ch === '"') inQ = true;
      else if (ch === ',') { cur.push(field); field = ''; }
      else if (ch === '\r') {} // ignore
      else if (ch === '\n') { cur.push(field); field = ''; pushRow(); }
      else field += ch;
    }
  }
  if (field.length > 0 || cur.length > 0) { cur.push(field); pushRow(); }
  if (rows.length < 2) return [];

  const headers = rows[0].map((h) => h.trim().toLowerCase());
  const KNOWN = {
    phone: 'phoneE164', phonee164: 'phoneE164', phone_e164: 'phoneE164',
    name: 'name', firstname: 'name', first_name: 'name', fullname: 'name',
    company: 'company', business: 'company', clinic: 'company', practice: 'company',
    email: 'email'
  };
  const out = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    if (row.every((c) => !c.trim())) continue;
    const obj = {};
    const meta = {};
    for (let i = 0; i < headers.length; i++) {
      const h = headers[i], v = (row[i] || '').trim();
      if (!v) continue;
      const key = KNOWN[h];
      if (key) obj[key] = v;
      else meta[headers[i]] = v;
    }
    if (Object.keys(meta).length > 0) obj.metadata = meta;
    if (obj.phoneE164) out.push(obj);
  }
  return out;
}
